import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import "./pp2-theme.css";
import { Pp2Confetti } from "@/components/Pp2Confetti";
import { Camera, ArrowRight, MessageCircle, Copy, FileDown, CalendarDays, Orbit, Home, X, Shield, Lock } from "lucide-react";
import { BrowserRouter, Routes, Route, Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Drawer, DrawerTrigger, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { QRCodeSVG } from "qrcode.react";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import {
  useContracts,
  ContractsTab,
  ReportePagos,
} from "./modules/Contracts";
import {
  useQuotes,
  QuotesTab,
  generateQuotePDF,
} from "./modules/Quotes";
import {
  useServicesSection,
  ServicesSectionTab,
  Pp2ServicesSection,
} from "./modules/ServicesSection";
import { AVISO_URL } from "./lib/avisoPrivacidad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Mismo enlace usado en el QR de aviso de privacidad de los PDFs (cotización,
// contrato, contrato proveedor), reutilizado en los footers públicos (home y /cotizador).
// Única fuente: src/lib/avisoPrivacidad.js (AVISO_URL).
const PRIVACY_URL = AVISO_URL;
axios.defaults.withCredentials = true;
// Si el servidor responde 401 (sesión no válida/expirada) durante el uso del panel,
// se notifica globalmente para forzar el regreso a la pantalla de login.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      window.dispatchEvent(new CustomEvent("picparty:session-expired"));
    }
    return Promise.reject(error);
  }
);

const SITE_DOMAIN = "picparty.net";
const PICPARTY_LOGO = "https://customer-assets.emergentagent.com/job_a3564e90-ad20-475c-89a1-7a897b2adf08/artifacts/l4swb3kc_logo%20pic%20party.png";

const PICPARTY_LOGO_BASE64 = "data:image/png;base64,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";


// ============ DATEPICKER VISUAL ============
const DatePicker = ({ value, onChange, placeholder = "Seleccionar fecha", className = "" }) => {
  const [open, setOpen] = useState(false);
  
  // Convertir string YYYY-MM-DD a Date object - usar 2026 como año base
  const selectedDate = value ? new Date(value + 'T12:00:00') : undefined;
  
  // Fecha por defecto para el calendario: mes actual de 2026
  const defaultMonth = new Date(2026, new Date().getMonth(), 1);
  
  // Formatear fecha para mostrar
  const formatDate = (dateStr) => {
    if (!dateStr) return placeholder;
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className={`w-full justify-start text-left font-normal input-premium ${!value ? 'text-pearl-muted' : 'text-pearl'} ${className}`}
        >
          <span className="mr-2">📅</span>
          {formatDate(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-night border-gold/30" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          defaultMonth={defaultMonth}
          onSelect={(date) => {
            if (date) {
              const yyyy = date.getFullYear();
              const mm = String(date.getMonth() + 1).padStart(2, '0');
              const dd = String(date.getDate()).padStart(2, '0');
              onChange(`${yyyy}-${mm}-${dd}`);
            }
            setOpen(false);
          }}
          initialFocus
          className="rounded-md border border-gold/20 bg-night text-pearl"
        />
      </PopoverContent>
    </Popover>
  );
};


// ============ COMPONENTE DE TARJETA DE EVENTO ============
// Paleta fija: los 8 colores del obturador del logo de PicParty, en orden.
// Se asignan de forma secuencial según el orden en pantalla, así dos
// tarjetas seguidas nunca comparten color (al rotar 8 colores, la posición
// i y la i+1 nunca coinciden).
const PP2_PALETTE = ["#E53935", "#F57C00", "#FBC02D", "#43A047", "#00ACC1", "#1E5BD8", "#7B2FBE", "#D81B60"];
const PP2_YELLOW = "#FBC02D";
const PP2_DARK_TEXT = "#3E2723";

const assignEventColors = (eventsInDisplayOrder) => {
  const colorMap = {};
  eventsInDisplayOrder.forEach((e, idx) => { colorMap[e.id] = PP2_PALETTE[idx % PP2_PALETTE.length]; });
  return colorMap;
};

// Tamaño de letra del nombre según su longitud, para que nombres largos
// quepan sin tener que cortar ninguna palabra a la mitad.
const getEventNameFontSize = (name = "") => {
  const len = name.length;
  if (len <= 14) return 24;
  if (len <= 24) return 21;
  if (len <= 36) return 18;
  if (len <= 50) return 16;
  return 14;
};

const PP2_SERVICES = {
  cabina:       { label: "Cabina de fotos" },
  video360:     { label: "Video 360°" },
  keymoments:   { label: "Key Moments" },
  picpartylive: { label: "PicPartyLive" },
};

const EventCard = ({ event, color }) => {
  const cardColor = color || PP2_PALETTE[0];
  const isYellowCard = cardColor.toUpperCase() === PP2_YELLOW;
  const cardTextColor = isYellowCard ? PP2_DARK_TEXT : "#ffffff";
  // El texto de los botones usa el color de la tarjeta, salvo en la amarilla:
  // amarillo sobre blanco se lee mal, así que ahí también usa café oscuro.
  const btnTextColor = isYellowCard ? PP2_DARK_TEXT : cardColor;
  const services = event.services || [];

  // "En vivo": SOLO el día exacto del evento.
  const isLive = (() => {
    try {
      const evDate = new Date(event.date + 'T12:00:00');
      const today = new Date(); today.setHours(12, 0, 0, 0);
      return Math.round((today - evDate) / 86400000) === 0;
    } catch { return false; }
  })();

  // Formato "sáb 10 oct 2026"
  const dateLabel = (() => {
    try {
      const d = new Date(event.date + 'T12:00:00');
      const weekday = d.toLocaleDateString('es-MX', { weekday: 'short' }).replace('.', '');
      const month = d.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '');
      return `${weekday} ${d.getDate()} ${month} ${d.getFullYear()}`;
    } catch { return event.date; }
  })();

  return (
    <div className="pp2-card" style={{ "--pp2-c": cardColor, "--pp2-card-text": cardTextColor, "--pp2-btn-text": btnTextColor }} data-testid={`pp2-event-card-${event.id}`}>
      <div className="pp2-cover">
        <div className="pp2-cover-logo"><img src={PICPARTY_LOGO} alt="" /></div>
        {isLive && <span className="pp2-live" data-testid="pp2-live-badge">En vivo</span>}
        <div className="pp2-cover-info">
          <span className="pp2-cover-name" style={{ fontSize: getEventNameFontSize(event.name) }} data-testid="pp2-event-name">{event.name}</span>
          <span className="pp2-cover-date">{dateLabel}</span>
          {services.length > 0 && (
            <div className="pp2-svc-tags" data-testid="pp2-event-services">
              {services.map(s => PP2_SERVICES[s] && <span key={s} className="pp2-svc-tag">{PP2_SERVICES[s].label}</span>)}
            </div>
          )}
        </div>
      </div>
      <div className="pp2-meta">
        <div className="pp2-card-actions">
          {services.includes("cabina") && event.fotoshare_url && (
            <a href={event.fotoshare_url} target="_blank" rel="noopener noreferrer" className="pp2-card-cta pp2-card-cta-photo" data-testid={`pp2-btn-fotos-${event.id}`}>
              <Camera size={18} /> Ver y descargar fotos
            </a>
          )}
          {services.includes("video360") && event.video360_url && (
            <a href={event.video360_url} target="_blank" rel="noopener noreferrer" className="pp2-card-cta pp2-card-cta-video" data-testid={`pp2-btn-video360-${event.id}`}>
              <Orbit size={18} /> Ver y descargar video 360°
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const PP2_TIME_GROUPS = ["Mañana", "Tarde", "Noche", "Sin horario"];

const groupEventsByTimeOfDay = (events) => {
  const groups = { "Mañana": [], "Tarde": [], "Noche": [], "Sin horario": [] };
  events.forEach(e => {
    if (!e.time) { groups["Sin horario"].push(e); return; }
    const hour = parseInt(e.time.split(':')[0], 10);
    if (isNaN(hour)) groups["Sin horario"].push(e);
    else if (hour < 14) groups["Mañana"].push(e);
    else if (hour < 19) groups["Tarde"].push(e);
    else groups["Noche"].push(e);
  });
  Object.values(groups).forEach(list => list.sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99")));
  return PP2_TIME_GROUPS.map(label => ({ label, events: groups[label] })).filter(g => g.events.length > 0);
};

const pluralEventos = (n) => `${n} ${n === 1 ? "evento" : "eventos"}`;
const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ============ BANNER DE PROMOCIÓN (solo portada pública /) ============
const Pp2PromoBanner = () => {
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/promotions/active`)
      .then(res => setPromo(res.data))
      .catch(() => setPromo(null));
  }, []);

  if (!promo || dismissed) return null;
  if (sessionStorage.getItem(`pp2_promo_dismissed_${promo.id}`)) return null;

  // Separa el precio (ej. "$3,599") del resto del texto para poder acomodarlo en su
  // propia línea en celular (texto arriba, precio + botón abajo); en escritorio ambas
  // líneas caben una junto a la otra, en una sola fila.
  const priceMatch = promo.texto.match(/\$[\d,]+(?:\.\d+)?/);
  const price = priceMatch ? priceMatch[0] : null;
  const mainText = priceMatch
    ? promo.texto.slice(0, priceMatch.index).replace(/\s*(por solo|por|desde)\s*$/i, "").trim()
    : promo.texto;

  return (
    <div className="pp2-promo-banner" style={{ "--pp2-promo-bg": promo.color_fondo }} data-testid="pp2-promo-banner">
      <div className="pp2-promo-line1">
        {promo.icono && <span className="pp2-promo-icon">{promo.icono}</span>}
        <span className="pp2-promo-text" data-testid="pp2-promo-text">{mainText}</span>
      </div>
      <div className="pp2-promo-line2">
        {price && <span className="pp2-promo-price" data-testid="pp2-promo-price">{price}</span>}
        <Link to={promo.paquete ? `/cotizador?paquete=${promo.paquete}` : "/cotizador"} className="pp2-promo-btn" data-testid="pp2-promo-cta">{promo.texto_boton}</Link>
      </div>
      <button
        className="pp2-promo-close"
        aria-label="Cerrar promoción"
        data-testid="pp2-promo-close"
        onClick={() => {
          sessionStorage.setItem(`pp2_promo_dismissed_${promo.id}`, "1");
          setDismissed(true);
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

// ============ GALERÍA PRINCIPAL (/) ============
const EventGallery = () => {
  const [allEvents, setAllEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [nameQuery, setNameQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const monthInitialized = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?published_only=true`);
      setAllEvents(response.data);
      // Al abrir: mostrar el mes del evento más reciente (no el mes actual si no tiene eventos)
      if (!monthInitialized.current && response.data.length > 0) {
        const mostRecent = response.data.reduce((max, e) => (!max || e.date > max.date) ? e : max, null);
        if (mostRecent) {
          setCalendarMonth(new Date(mostRecent.date + 'T12:00:00'));
          monthInitialized.current = true;
        }
      }
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const eventDates = allEvents.map(e => new Date(e.date + 'T12:00:00'));

  // Mapa fecha -> cantidad de eventos, para pintar punto (1) o número (2+) en el calendario
  const eventCountByDate = {};
  allEvents.forEach(e => { eventCountByDate[e.date] = (eventCountByDate[e.date] || 0) + 1; });

  const Pp2DayContent = ({ date }) => {
    const count = eventCountByDate[toLocalDateStr(date)] || 0;
    return (
      <>
        <span>{date.getDate()}</span>
        {count === 1 && <span className="pp2-day-dot" />}
        {count >= 2 && <span className="pp2-day-count">{count}</span>}
      </>
    );
  };

  // Búsqueda global por nombre: ignora la fecha seleccionada, busca en TODOS los eventos publicados
  const searchResults = nameQuery.trim()
    ? allEvents.filter(e => (e.name || "").toLowerCase().includes(nameQuery.trim().toLowerCase()))
    : [];

  // Eventos de la fecha elegida en el calendario
  const dateResults = selectedDate
    ? allEvents.filter(e => e.date === selectedDate.toISOString().split('T')[0])
    : [];

  // Últimas 8 galerías publicadas (el backend ya ordena por created_at DESC)
  const recentEvents = allEvents.slice(0, 8);

  const mode = nameQuery.trim() ? 'search' : (selectedDate ? 'date' : 'recent');
  const resultsToShow = mode === 'search' ? searchResults : mode === 'date' ? dateResults : recentEvents;
  const resultsHeader = mode === 'search'
    ? `Resultados para "${nameQuery.trim()}"`
    : mode === 'date'
      ? `Eventos del ${selectedDate.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}`
      : "Eventos recientes";

  // Cuando hay varios eventos el mismo día, se agrupan por horario (Mañana/Tarde/Noche)
  const dateGroups = (mode === 'date' && resultsToShow.length > 1) ? groupEventsByTimeOfDay(resultsToShow) : null;

  // Color estable por tarjeta: se asigna según la posición en pantalla
  // (orden real mostrado), rotando los 8 colores del logo, así dos tarjetas
  // seguidas nunca comparten color.
  const eventColors = assignEventColors(resultsToShow);

  const CalendarBody = (
    <Calendar
      mode="single"
      selected={selectedDate}
      onSelect={(date) => { setSelectedDate(date); setCalendarOpen(false); }}
      month={calendarMonth}
      onMonthChange={setCalendarMonth}
      modifiers={{ hasEvent: eventDates }}
      modifiersClassNames={{ hasEvent: "pp2-day-has-event" }}
      components={{ DayContent: Pp2DayContent }}
      data-testid="pp2-date-calendar"
    />
  );

  if (loading) return (
    <div className="pp2-theme" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div data-testid="pp2-loading">Cargando...</div>
    </div>
  );

  return (
    <div className="pp2-theme" data-testid="pp2-event-gallery-page">
      <Pp2PromoBanner />
      <section className="pp2-hero">
        <Pp2Confetti />
        <div className="pp2-wrap">
          <div className="pp2-topbar">
            <div className="pp2-brand">
              <img src={PICPARTY_LOGO} alt="PicParty" />
              <small>Cabina Fotográfica · Video 360° · Desde 2014</small>
            </div>
            <Link to="/cotizador" className="pp2-header-cta" data-testid="pp2-cta-cotizador">
              <span className="pp2-header-cta-full">Cotiza tu evento</span>
              <span className="pp2-header-cta-short">Cotiza</span>
              <ArrowRight size={16} />
            </Link>
          </div>
          <h1 className="pp2-h1">Encuentra <em>tu galería</em><br />en segundos</h1>
          <p className="pp2-lead">Busca por nombre o elige la fecha de tu evento y accede a las fotos y video 360° que capturamos para ti.</p>

          <div className="pp2-searchbar-row">
            <span className="pp2-label">Busca tu evento</span>
            <div className="pp2-searchbar">
              <input
                type="text"
                className="pp2-name-input"
                placeholder="Ej. Boda de Ana y Luis"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                data-testid="pp2-name-filter-input"
              />
              {isMobile ? (
                <Drawer open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <DrawerTrigger asChild>
                    <button className="pp2-cal-btn" data-testid="pp2-calendar-trigger" aria-label="Elegir fecha"><CalendarDays size={20} /><span className="pp2-cal-btn-text">Fecha</span></button>
                  </DrawerTrigger>
                  <DrawerContent className="pp2-theme pp2-cal-drawer">
                    <DrawerTitle className="sr-only">Elegir fecha</DrawerTitle>
                    <div className="pp2-wrap" style={{ paddingBottom: 24 }}>
                      {CalendarBody}
                    </div>
                  </DrawerContent>
                </Drawer>
              ) : (
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button className="pp2-cal-btn" data-testid="pp2-calendar-trigger" aria-label="Elegir fecha"><CalendarDays size={20} /><span className="pp2-cal-btn-text">Fecha</span></button>
                  </PopoverTrigger>
                  <PopoverContent className="pp2-theme pp2-cal-popover" align="end">
                    {CalendarBody}
                  </PopoverContent>
                </Popover>
              )}
            </div>
            {selectedDate && (
              <button className="pp2-reset" onClick={() => setSelectedDate(null)} data-testid="pp2-reset-search-btn">Quitar fecha</button>
            )}
          </div>

          <div className="pp2-results-block">
            <div className="pp2-bar">
              <h2>{resultsHeader}</h2>
              <span className="pp2-count" data-testid="pp2-results-count">{pluralEventos(resultsToShow.length)}</span>
            </div>
            {resultsToShow.length === 0 ? (
              <div className="pp2-empty" data-testid="pp2-empty-results">
                {mode === 'search' ? "No encontramos eventos con ese nombre." : mode === 'date' ? "No hay eventos para esa fecha." : "Aún no hay galerías publicadas."}
              </div>
            ) : dateGroups ? (
              dateGroups.map(group => (
                <div key={group.label} className="pp2-time-group" data-testid={`pp2-time-group-${group.label}`}>
                  <h3 className="pp2-time-group-label">{group.label}</h3>
                  <div className="pp2-grid">
                    {group.events.map(event => <EventCard key={event.id} event={event} color={eventColors[event.id]} />)}
                  </div>
                </div>
              ))
            ) : (
              <div className="pp2-grid">
                {resultsToShow.map(event => <EventCard key={event.id} event={event} color={eventColors[event.id]} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      <Pp2ServicesSection />

      <footer className="pp2-wrap pp2-footer">
        <span className="pp2-flogo"><img src={PICPARTY_LOGO} alt="" />PicParty © {new Date().getFullYear()}</span>
        <Link to="/picpartylive" style={{ color: "var(--pp2-gold)", textDecoration: "none", fontWeight: 600 }} data-testid="pp2-live-link">PicPartyLive</Link>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="pp2-privacy-btn" data-testid="pp2-privacy-link"><Shield size={14} /> Aviso de privacidad</a>
        <Link to="/admin" className="pp2-adm" data-testid="pp2-admin-link">Acceso staff</Link>
      </footer>
    </div>
  );
};

// ============ GALERÍA INDIVIDUAL CON PESTAÑAS (Fotos / Video 360°) ============
const EventGalleryViewer = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("fotos");
  const [pin, setPin] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}`)
      .then(res => setEvent(res.data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  // Los eventos privados no deben indexarse en buscadores.
  useEffect(() => {
    if (event?.is_private) {
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex";
      document.head.appendChild(meta);
      return () => document.head.removeChild(meta);
    }
  }, [event?.is_private]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    setUnlockError("");
    try {
      const res = await axios.post(`${API}/events/${eventId}/unlock`, { pin });
      setEvent({ ...res.data, unlocked: true });
    } catch (err) {
      setUnlockError(err.response?.data?.detail || "No se pudo verificar el PIN");
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) return (
    <div className="pp2-theme" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div data-testid="pp2-loading">Cargando...</div>
    </div>
  );

  if (!event) return (
    <div className="pp2-theme" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }} data-testid="pp2-gallery-viewer-notfound">
      <p>No encontramos esta galería.</p>
      <Link to="/" className="pp2-home-btn" data-testid="pp2-back-home"><Home size={24} /> Inicio</Link>
    </div>
  );

  // Evento privado y aún no desbloqueado: solo pedir el PIN.
  if (event.is_private && !event.unlocked) {
    return (
      <div className="pp2-theme" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 20 }} data-testid="pp2-gallery-pin-gate">
        <Lock size={32} className="text-gold" />
        <h1 className="pp2-h1" style={{ fontSize: "clamp(22px,5vw,30px)", textAlign: "center" }}>{event.name}</h1>
        <p style={{ color: "#a99fbd" }}>Esta galería es privada. Ingresa el PIN para verla.</p>
        <form onSubmit={handleUnlock} style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <Input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="PIN de 4 dígitos"
            inputMode="numeric"
            maxLength={4}
            className="input-premium"
            style={{ textAlign: "center", fontSize: 22, letterSpacing: 6, width: 160 }}
            data-testid="pp2-pin-input"
          />
          {unlockError && <p style={{ color: "#ff6b6b", fontSize: 14 }} data-testid="pp2-pin-error">{unlockError}</p>}
          <Button type="submit" className="btn-gold" disabled={unlocking || pin.length !== 4} data-testid="pp2-pin-submit">
            {unlocking ? "Verificando..." : "Entrar"}
          </Button>
        </form>
        <Link to="/" className="pp2-home-btn" data-testid="pp2-back-home"><Home size={20} /> Inicio</Link>
      </div>
    );
  }

  const activeUrl = tab === "fotos" ? event.fotoshare_url : event.video360_url;

  return (
    <div className="pp2-theme" data-testid="pp2-gallery-viewer-page">
      <div className="pp2-wrap" style={{ paddingTop: 20, paddingBottom: 20 }}>
        <Link to="/" className="pp2-home-btn" data-testid="pp2-back-home"><Home size={24} /> Inicio</Link>
        <h1 className="pp2-h1" style={{ fontSize: "clamp(24px,5vw,34px)", margin: "10px 0" }}>{event.name}</h1>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="pp2-tabs-list" data-testid="pp2-gallery-tabs">
            <TabsTrigger value="fotos" className="pp2-tabs-trigger" data-testid="pp2-tab-fotos">Fotos</TabsTrigger>
            <TabsTrigger value="video360" className="pp2-tabs-trigger" data-testid="pp2-tab-video360">Video 360°</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="pp2-iframe-wrap">
          {activeUrl ? (
            <iframe src={activeUrl} title={event.name} className="pp2-iframe" data-testid="pp2-gallery-iframe" />
          ) : (
            <div className="pp2-empty">Esta sección aún no está disponible.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ COTIZADOR COMPLETO - PRECIOS REALES NETO ============
// Precios por defecto del cotizador (respaldo mientras carga /api/cotizador/precios
// o si la llamada falla). La fuente de verdad vive en Mongo (app_config) para que
// puedan editarse más adelante desde el admin sin tocar código.
const DEFAULT_COTIZADOR_PRECIOS = {
  cabina: [
    { horas: 2, precio: 2699, label: null, descripcion: "" },
    { horas: 3, precio: 3299, label: null, descripcion: "" },
    { horas: 4, precio: 3799, label: null, descripcion: "" },
    { horas: 5, precio: 4699, label: null, descripcion: "" },
  ],
  cabina_llaveros: {
    horas: 2,
    precio: 3599,
    llaveros: 50,
    precio_llavero_extra: 35,
    label: "Cabina 2 h + 50 llaveros con tu foto",
    descripcion: "Cabina de fotos 2 horas + 50 llaveros con la foto favorita de tus invitados. Llavero extra $35.",
    activo: true,
  },
  video360: {
    horas: 2,
    precio: 3000,
    label: "Video 360° 2 horas",
    descripcion: "",
    activo: true,
  },
  video360_nieve: {
    horas: 2,
    precio: 3800,
    label: "Video 360° con nieve",
    descripcion: "",
    activo: false,
  },
  nota_precios: "Precios netos, IVA incluido. Aplican restricciones.",
};

const Cotizador = () => {
  const [searchParams] = useSearchParams();
  const autoSelectedPaqueteRef = useRef(false);

  // Datos del cliente
  const [clientData, setClientData] = useState({ 
    nombre: "", 
    telefono: "", 
    salon: "", 
    fecha: "", 
    horario: "", 
    zona: "local", // "local" (Chihuahua capital) | "foraneo"
    facturaRequerida: false
  });
  
  const [quote, setQuote] = useState(null);
  const [folio, setFolio] = useState(null);
  const [pdfFormat, setPdfFormat] = useState("letter"); // "letter" o "half-letter"

  // Precios vigentes, cargados desde el backend (con respaldo local mientras llegan).
  // null hasta que la llamada responde (éxito o error) para nunca intentar leer un
  // paquete antes de tener datos reales — así el cotizador jamás truena.
  const [pricing, setPricing] = useState(null);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cotizador/precios`)
      .then(res => setPricing(res.data))
      .catch(() => setPricing(DEFAULT_COTIZADOR_PRECIOS))
      .finally(() => setPricingLoaded(true));
  }, []);

  // Textos editables del PDF (flete, galería/privacidad, notas, link de privacidad, pie de página)
  const [pdfTextos, setPdfTextos] = useState(null);
  useEffect(() => {
    axios.get(`${API}/cotizador/pdf-textos`)
      .then(res => setPdfTextos(res.data))
      .catch(() => setPdfTextos(null));
  }, []);
  
  // SERVICIOS MÚLTIPLES - Cada uno con su propia selección
  // keychain: true cuando se eligió el paquete "Cabina 2h + 50 llaveros" en vez de por horas
  const [services, setServices] = useState({
    cabina: { selected: false, hours: 0, keychain: false },
    video360: { selected: false, hours: 0, nieve: false },
    keyMoments: { selected: false, pieces: 0 },
    picpartyLive: { selected: false, isPromo: false } // isPromo = Promo Expo Boda
  });
  
  // Key Moments y PicPartyLive están en pausa: se ocultan del cotizador público
  // pero su lógica de precio se conserva íntegra por si se reactivan.
  const keyMomentsPrecios = [
    { piezas: 80, precio: 2999 },
    { piezas: 100, precio: 3250 },
    { piezas: 140, precio: 3499 },
    { piezas: 200, precio: 4499 },
  ];

  // Folio atómico: lo genera el backend al crear la cotización (POST /api/quotes).
  // Se mantiene este wrapper solo como respaldo offline si el backend falla.
  const generateFolioFallback = useCallback(() => {
    const stored = localStorage.getItem('lastQuoteFolio');
    const lastFolio = stored ? parseInt(stored, 10) : 1399;
    const newFolioNum = lastFolio + 1;
    localStorage.setItem('lastQuoteFolio', newFolioNum.toString());
    return `COT-${newFolioNum}`;
  }, []);

  // Obtener precio de cabina (horas normales o paquete de llaveros)
  const getCabinaPrice = () => {
    if (!services.cabina.selected) return 0;
    if (services.cabina.keychain) return pricing?.cabina_llaveros?.precio || 0;
    if (!services.cabina.hours) return 0;
    const found = pricing?.cabina?.find(p => p.horas === services.cabina.hours);
    return found ? found.precio : 0;
  };

  // Obtener precio de video 360 (promoción normal o la variante "con nieve")
  const getVideo360Price = () => {
    if (!services.video360.selected) return 0;
    if (services.video360.nieve) return pricing?.video360_nieve?.precio || 0;
    return pricing?.video360?.precio || 0;
  };

  // Etiqueta de video 360 según la variante elegida
  const getVideo360Label = () => services.video360.nieve ? (pricing?.video360_nieve?.label || "Video 360°") : (pricing?.video360?.label || "Video 360°");

  // Etiqueta de un paquete de Cabina por hora (usa el nombre configurado en admin, o "{horas}h" por defecto)
  const getCabinaTierLabel = (hours) => pricing?.cabina?.find(p => p.horas === hours)?.label || `${hours}h`;

  // Línea de resumen de Cabina ("Cabina de Fotos (2h)" o, si el nombre del paquete ya
  // incluye la palabra "Cabina" (ej. "Cabina 2 h + 50 llaveros con tu foto"), se muestra
  // solo ese nombre sin repetir "Cabina de Fotos (...)".
  const getCabinaSummaryLabel = (hours, keychain) => {
    const label = keychain ? (pricing?.cabina_llaveros?.label || "paquete") : getCabinaTierLabel(hours);
    return label.toLowerCase().includes("cabina") ? label : `Cabina de Fotos (${label})`;
  };

  // Obtener precio de Key Moments
  const getKeyMomentsPrice = () => {
    if (!services.keyMoments.selected || !services.keyMoments.pieces) return 0;
    const found = keyMomentsPrecios.find(p => p.piezas === services.keyMoments.pieces);
    return found ? found.precio : 0;
  };

  // LÓGICA DE PRECIO PICPARTYLIVE AUTOMÁTICA - 3 NIVELES
  // $1,500 NETO = Precio Normal (solo PicPartyLive sin promo)
  // $1,000 NETO = Promo Expo Boda 
  // $700 NETO = Combo con otro servicio (Cabina, Video360 o Key Moments)
  const getLivePrice = () => {
    if (!services.picpartyLive.selected) return 0;
    const hasOtherService = services.cabina.selected || services.video360.selected || services.keyMoments.selected;
    
    if (hasOtherService) {
      return 700; // Combo: mejor precio
    } else if (services.picpartyLive.isPromo) {
      return 1000; // Promo Expo Boda
    } else {
      return 1500; // Precio normal
    }
  };

  // Obtener tipo de precio para mostrar en cotización
  const getLivePriceType = () => {
    if (!services.picpartyLive.selected) return null;
    const hasOtherService = services.cabina.selected || services.video360.selected || services.keyMoments.selected;
    
    if (hasOtherService) {
      return "combo";
    } else if (services.picpartyLive.isPromo) {
      return "promo";
    } else {
      return "normal";
    }
  };

  // Calcular ahorro cuando hay combo o promo
  const getAhorro = () => {
    if (!services.picpartyLive.selected) return 0;
    const hasOtherService = services.cabina.selected || services.video360.selected || services.keyMoments.selected;
    
    if (hasOtherService) {
      return 1500 - 700; // $800 de ahorro con combo
    } else if (services.picpartyLive.isPromo) {
      return 1500 - 1000; // $500 de ahorro con promo
    }
    return 0;
  };
  
  // Verificar si hay algún servicio seleccionado
  const hasAnyService = () => {
    return services.cabina.selected || services.video360.selected || 
           services.keyMoments.selected || services.picpartyLive.selected;
  };

  // Seleccionar/deseleccionar servicio de horas (Cabina o Video 360)
  const toggleHourService = (type, hours) => {
    setServices(prev => {
      const isSameSelection = prev[type].selected && prev[type].hours === hours && !prev[type].keychain && !prev[type].nieve;
      return {
        ...prev,
        [type]: {
          ...prev[type],
          selected: !isSameSelection,
          hours: isSameSelection ? 0 : hours,
          ...(type === "cabina" ? { keychain: false } : {}),
          ...(type === "video360" ? { nieve: false } : {})
        }
      };
    });
  };

  // Seleccionar/deseleccionar el paquete "Cabina 2h + 50 llaveros"
  const toggleCabinaKeychain = () => {
    setServices(prev => {
      const isSameSelection = prev.cabina.selected && prev.cabina.keychain;
      return {
        ...prev,
        cabina: isSameSelection
          ? { selected: false, hours: 0, keychain: false }
          : { selected: true, hours: pricing?.cabina_llaveros?.horas || 0, keychain: true }
      };
    });
  };

  // Seleccionar/deseleccionar el paquete "Video 360° con Nieve"
  const toggleVideo360Nieve = () => {
    setServices(prev => {
      const isSameSelection = prev.video360.selected && prev.video360.nieve;
      return {
        ...prev,
        video360: isSameSelection
          ? { selected: false, hours: 0, nieve: false }
          : { selected: true, hours: pricing?.video360_nieve?.horas || 0, nieve: true }
      };
    });
  };

  // Preselecciona el paquete indicado por ?paquete= en la URL (ej. desde el botón "Cotizar"
  // del banner de promoción). Se ejecuta una sola vez, cuando los precios ya cargaron.
  useEffect(() => {
    if (!pricingLoaded || !pricing || autoSelectedPaqueteRef.current) return;
    const paquete = searchParams.get("paquete");
    if (!paquete) return;
    autoSelectedPaqueteRef.current = true;
    if (paquete === "cabina_llaveros") toggleCabinaKeychain();
    else if (paquete === "video360_nieve") toggleVideo360Nieve();
    else if (paquete === "video360" && pricing.video360?.horas) toggleHourService("video360", pricing.video360.horas);
    else if (paquete.startsWith("cabina_")) {
      const hrs = parseInt(paquete.replace("cabina_", ""), 10);
      if (hrs) toggleHourService("cabina", hrs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingLoaded, pricing, searchParams]);

  // Seleccionar/deseleccionar Key Moments
  const toggleKeyMoments = (pieces) => {
    setServices(prev => ({
      ...prev,
      keyMoments: {
        selected: !(prev.keyMoments.selected && prev.keyMoments.pieces === pieces),
        pieces: prev.keyMoments.selected && prev.keyMoments.pieces === pieces ? 0 : pieces
      }
    }));
  };

  // Toggle PicPartyLive
  const togglePicPartyLive = () => {
    setServices(prev => ({
      ...prev,
      picpartyLive: { 
        selected: !prev.picpartyLive.selected,
        isPromo: prev.picpartyLive.isPromo 
      }
    }));
  };

  // Toggle Promo Expo Boda
  const togglePromoExpoBoda = () => {
    setServices(prev => ({
      ...prev,
      picpartyLive: {
        ...prev.picpartyLive,
        isPromo: !prev.picpartyLive.isPromo
      }
    }));
  };

  // Calcular cotización
  const calculateQuote = async () => {
    // Validar nombre
    if (!clientData.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    // Validar teléfono: OPCIONAL. Si se escribe, debe tener 10 dígitos y no ser
    // un número obviamente falso (los 10 dígitos iguales, ej. "0000000000").
    if (clientData.telefono) {
      if (!/^\d{10}$/.test(clientData.telefono)) {
        toast.error("Si capturas el teléfono, debe tener 10 dígitos");
        return;
      }
      if (/^(\d)\1{9}$/.test(clientData.telefono)) {
        toast.error("Ingresa un teléfono válido");
        return;
      }
    }

    // Validar fecha del evento
    if (!clientData.fecha) {
      toast.error("La fecha del evento es obligatoria");
      return;
    }
    
    // Validar que haya seleccionado al menos un servicio
    if (!hasAnyService()) {
      toast.error("Selecciona al menos un servicio");
      return;
    }
    
    // Calcular precios. Sin descuento por combinar servicios (se eliminó).
    // Los precios ya incluyen IVA: pedir factura NO cambia el total.
    const cabinaPrice = getCabinaPrice();
    const video360Price = getVideo360Price();
    const keyMomentsPrice = getKeyMomentsPrice();
    const livePrice = getLivePrice();
    const subtotal = cabinaPrice + video360Price + keyMomentsPrice + livePrice;
    const total = subtotal;

    // Reservar folio atómicamente en el backend al guardar la cotización
    const newFolio = await saveQuoteToBackend(cabinaPrice, video360Price, subtotal, total);

    setFolio(newFolio);
    setQuote({ 
      cabinaPrice,
      video360Price,
      keyMomentsPrice,
      livePrice,
      subtotal, 
      total, 
      zona: clientData.zona,
      factura: clientData.facturaRequerida,
      salon: clientData.salon,
      services: { ...services }
    });
    
    toast.success(`Cotización generada - Folio: ${newFolio}`);
  };

  // Guarda la cotización en MongoDB (colección `quotes`) para que aparezca en
  // Admin → Cotizaciones. El backend asigna el folio COT-XXXX atómicamente.
  const saveQuoteToBackend = async (cabinaPrice, video360Price, subtotal, total) => {
    try {
      const res = await axios.post(`${API}/quotes`, {
        nombre: clientData.nombre,
        telefono: clientData.telefono || null,
        fecha_evento: clientData.fecha || null,
        salon: clientData.salon || null,
        zona: clientData.zona || null,
        factura: clientData.facturaRequerida,
        servicios: {
          cabina: services.cabina.selected ? {
            selected: true,
            hours: services.cabina.hours,
            keychain: services.cabina.keychain,
            label: getCabinaSummaryLabel(services.cabina.hours, services.cabina.keychain),
            precio: cabinaPrice,
          } : { selected: false },
          video360: services.video360.selected ? {
            selected: true,
            nieve: services.video360.nieve,
            label: getVideo360Label(),
            precio: video360Price,
          } : { selected: false },
        },
        subtotal,
        total,
      });
      return res.data.folio;
    } catch (e) {
      console.error("Error guardando cotización:", e);
      toast.error("No se pudo reservar el folio en el servidor, usando fallback local.");
      return generateFolioFallback();
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(amount);

  // Texto plano de la cotización, reutilizado por WhatsApp y "Copiar texto"
  const buildQuoteText = () => {
    if (!quote || !folio) return "";
    const lines = [`*Cotización PicParty* - Folio ${folio}`, ""];
    if (quote.services?.picpartyLive?.selected) lines.push(`PicPartyLive: ${formatCurrency(quote.livePrice)}`);
    if (quote.services?.cabina?.selected) {
      const label = getCabinaSummaryLabel(quote.services.cabina.hours, quote.services.cabina.keychain);
      lines.push(`${label}: ${formatCurrency(quote.cabinaPrice)}`);
    }
    if (quote.services?.video360?.selected) lines.push(`${getVideo360Label()}: ${formatCurrency(quote.video360Price)}`);
    if (quote.services?.keyMoments?.selected) lines.push(`Key Moments (${quote.services.keyMoments.pieces} pzas): ${formatCurrency(quote.keyMomentsPrice)}`);
    lines.push("", `*TOTAL NETO: ${formatCurrency(quote.total)}*`);
    lines.push(quote.factura ? "Con factura (el total no cambia, el precio ya incluye IVA)" : "Sin factura");
    if (quote.salon) lines.push(`Lugar/Salón: ${quote.salon}`);
    lines.push(quote.zona === 'foraneo' ? "Zona: fuera de Chihuahua capital (flete a confirmar)" : "Zona: Chihuahua capital");
    lines.push("", pricing?.nota_precios || "");
    lines.push([pdfTextos?.nota_vigencia, pdfTextos?.nota_no_contrato].filter(Boolean).join(" ") || "Cotización válida 15 días naturales. No es un contrato.");
    lines.push(`Aviso de privacidad: ${pdfTextos?.privacy_url || PRIVACY_URL}`);
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    const text = buildQuoteText();
    if (!text) return;
    window.open(`https://wa.me/526142725008?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyQuoteText = async () => {
    const text = buildQuoteText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Cotización copiada al portapapeles");
    } catch {
      toast.error("No se pudo copiar el texto");
    }
  };

  // Construye el documento de cotización (mismo formato que se persiste en el backend)
  // y genera el PDF usando el módulo compartido modules/Quotes/pdf/quotePDF.js —
  // así el PDF público y el "Ver PDF"/"Regenerar PDF" del Admin usan el mismo generador.
  const downloadPDF = async () => {
    if (!quote || !folio) {
      toast.error("Primero genera la cotización");
      return;
    }
    toast.info("Generando PDF optimizado para impresión...");
    const quoteDoc = {
      folio,
      nombre: clientData.nombre,
      telefono: clientData.telefono || null,
      fecha_evento: clientData.fecha || null,
      salon: clientData.salon || null,
      zona: clientData.zona || null,
      factura: clientData.facturaRequerida,
      servicios: {
        cabina: quote.services?.cabina?.selected ? {
          selected: true,
          hours: quote.services.cabina.hours,
          keychain: quote.services.cabina.keychain,
          label: getCabinaSummaryLabel(quote.services.cabina.hours, quote.services.cabina.keychain),
          precio: quote.cabinaPrice,
        } : { selected: false },
        video360: quote.services?.video360?.selected ? {
          selected: true,
          nieve: quote.services.video360.nieve,
          label: getVideo360Label(),
          precio: quote.video360Price,
        } : { selected: false },
      },
      subtotal: quote.subtotal,
      total: quote.total,
      created_at: new Date().toISOString(),
    };
    const textos = { ...(pdfTextos || {}), nota_precios: pricing?.nota_precios, privacy_url: pdfTextos?.privacy_url || PRIVACY_URL };
    await generateQuotePDF(quoteDoc, textos, pdfFormat);
  };

  if (!pricingLoaded) {
    return (
      <div className="pp2-theme" data-testid="pp2-cotizador-page">
        <div className="pp2-wrap" style={{ paddingTop: 24 }}>
          <Link to="/" className="pp2-home-btn" data-testid="pp2-back-home"><Home size={24} /> Inicio</Link>
          <p className="pp2-fieldlabel" style={{ marginTop: 60, textAlign: "center", fontSize: 16 }} data-testid="pp2-cotizador-loading">Cargando…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pp2-theme" data-testid="pp2-cotizador-page">
      <div className="pp2-wrap" style={{ paddingTop: 24 }}>
        <Link to="/" className="pp2-home-btn" data-testid="pp2-back-home"><Home size={24} /> Inicio</Link>
        <div className="pp2-quote-head">
          <h1>Cotiza tu evento</h1>
          <p>Elige tus servicios y arma tu cotización al instante.</p>
        </div>

        <div className="pp2-qgrid">
          {/* ===== Columna izquierda: selección de servicios ===== */}
          <div className="pp2-svcpick">

            {/* Datos del cliente */}
            <div className="pp2-sp" style={{ "--pp2-c": "var(--pp2-lilac)" }}>
              <h3>Tus datos</h3>
              <div className="pp2-fieldgrid" style={{ marginTop: 10 }}>
                <div>
                  <span className="pp2-fieldlabel">Nombre *</span>
                  <input className="pp2-field" placeholder="Tu nombre completo" value={clientData.nombre}
                    onChange={(e) => setClientData({ ...clientData, nombre: e.target.value })} data-testid="cotizador-nombre" />
                </div>
                <div>
                  <span className="pp2-fieldlabel">Teléfono</span>
                  <input className="pp2-field" placeholder="10 dígitos (opcional)" type="tel" maxLength={10} value={clientData.telefono}
                    onChange={(e) => setClientData({ ...clientData, telefono: e.target.value.replace(/\D/g, '') })} data-testid="cotizador-telefono" />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <span className="pp2-fieldlabel">Fecha del evento *</span>
                <DatePicker value={clientData.fecha} onChange={(fecha) => setClientData({ ...clientData, fecha })} placeholder="Seleccionar fecha" />
              </div>
              <div style={{ marginTop: 10 }}>
                <span className="pp2-fieldlabel">Lugar o salón del evento (opcional)</span>
                <input className="pp2-field" placeholder="Nombre del salón o lugar" value={clientData.salon}
                  onChange={(e) => setClientData({ ...clientData, salon: e.target.value })} data-testid="cotizador-salon" />
              </div>
              <label className="pp2-promo" style={{ marginTop: 10 }} data-testid="factura-label">
                <input type="checkbox" checked={clientData.facturaRequerida}
                  onChange={(e) => setClientData({ ...clientData, facturaRequerida: e.target.checked })} data-testid="factura-checkbox" />
                ¿Requieres factura? (el total no cambia, el precio ya incluye IVA)
              </label>
            </div>

            {/* Cabina de Fotos */}
            <div className={`pp2-sp ${services.cabina.selected ? 'on' : ''}`} style={{ "--pp2-c": "var(--pp2-pink)" }}>
              <h3><Camera size={17} style={{ display: "inline", marginRight: 6, verticalAlign: -3 }} />Cabina de Fotos</h3>
              <div className="pp2-opts">
                {(pricing.cabina || []).filter(t => t.activo !== false).map(({ horas, precio, label }) => (
                  <div key={`cabina-${horas}`} className={`pp2-opt ${services.cabina.selected && !services.cabina.keychain && services.cabina.hours === horas ? 'on' : ''}`}
                    onClick={() => toggleHourService("cabina", horas)} data-testid={`cabina-${horas}h`}>
                    <b>{label || `${horas}h`}</b><small>{formatCurrency(precio)}</small>
                  </div>
                ))}
                {pricing.cabina_llaveros && pricing.cabina_llaveros.activo !== false && (
                  <div className={`pp2-opt ${services.cabina.selected && services.cabina.keychain ? 'on' : ''}`}
                    onClick={toggleCabinaKeychain} data-testid="cabina-llaveros-pack">
                    <b>{pricing.cabina_llaveros.label}</b><small>{formatCurrency(pricing.cabina_llaveros.precio)}</small>
                  </div>
                )}
              </div>
              {services.cabina.selected && (() => {
                const desc = services.cabina.keychain
                  ? pricing.cabina_llaveros?.descripcion
                  : (pricing.cabina || []).find(t => t.horas === services.cabina.hours)?.descripcion;
                return desc ? <p className="pp2-opt-desc" style={{ marginTop: 8 }} data-testid="cabina-selected-descripcion">{desc}</p> : null;
              })()}
            </div>

            {/* Video 360 */}
            {((pricing.video360 && pricing.video360.activo !== false) || (pricing.video360_nieve && pricing.video360_nieve.activo === true)) && (
            <div className={`pp2-sp ${services.video360.selected ? 'on' : ''}`} style={{ "--pp2-c": "var(--pp2-mint)" }}>
              <h3>Video 360°</h3>
              <div className="pp2-opts">
                {pricing.video360 && pricing.video360.activo !== false && (
                  <div className={`pp2-opt ${services.video360.selected && !services.video360.nieve ? 'on' : ''}`}
                    onClick={() => toggleHourService("video360", pricing.video360.horas)} data-testid="video360-promo">
                    <b>{pricing.video360.label}</b><small>{formatCurrency(pricing.video360.precio)}</small>
                  </div>
                )}
                {pricing.video360_nieve && pricing.video360_nieve.activo === true && (
                  <div className={`pp2-opt ${services.video360.selected && services.video360.nieve ? 'on' : ''}`}
                    onClick={toggleVideo360Nieve} data-testid="video360-nieve-pack">
                    <b>{pricing.video360_nieve.label}</b><small>{formatCurrency(pricing.video360_nieve.precio)}</small>
                  </div>
                )}
              </div>
              {services.video360.selected && (() => {
                const desc = services.video360.nieve ? pricing.video360_nieve?.descripcion : pricing.video360?.descripcion;
                return desc ? <p className="pp2-opt-desc" style={{ marginTop: 8 }} data-testid="video360-selected-descripcion">{desc}</p> : null;
              })()}
            </div>
            )}

            {pricing.nota_precios && (
              <p className="pp2-phone" style={{ textAlign: "left" }} data-testid="pp2-nota-precios">{pricing.nota_precios}</p>
            )}
          </div>

          {/* ===== Columna derecha: resumen sticky ===== */}
          <div className="pp2-summary">
            <h3>Resumen</h3>
            <ul className="pp2-lines">
              {services.cabina.selected && (
                <li><span>{getCabinaSummaryLabel(services.cabina.hours, services.cabina.keychain)}</span><span>{formatCurrency(getCabinaPrice())}</span></li>
              )}
              {services.video360.selected && (
                <li><span>{getVideo360Label()}</span><span>{formatCurrency(getVideo360Price())}</span></li>
              )}
              {!hasAnyService() && <li className="pp2-empty-line"><span>Selecciona al menos un servicio</span></li>}
            </ul>
            <div className="pp2-total">
              <span>Total neto</span>
              <b data-testid="pp2-total-amount">{formatCurrency(
                getCabinaPrice() + getVideo360Price() + getKeyMomentsPrice() + getLivePrice()
              )}</b>
            </div>

            <div className="pp2-zona">
              <b>Zona del evento</b>
              <div className="pp2-seg">
                <button className={clientData.zona === 'local' ? 'on' : ''} onClick={() => setClientData({ ...clientData, zona: 'local' })} data-testid="zona-local-btn">Chihuahua ciudad</button>
                <button className={clientData.zona === 'foraneo' ? 'on' : ''} onClick={() => setClientData({ ...clientData, zona: 'foraneo' })} data-testid="zona-foraneo-btn">Fuera de la ciudad</button>
              </div>
              <div className={`pp2-flete ${clientData.zona === 'foraneo' ? 'hot' : ''}`} data-testid="pp2-flete-notice">
                {clientData.zona === 'foraneo'
                  ? <><strong>Aplica costo de flete</strong>Fuera de Chihuahua ciudad aplica un costo de flete según la distancia. Te lo confirmamos antes de apartar la fecha.</>
                  : <><strong>Flete según ubicación</strong>Dentro de Chihuahua ciudad normalmente no hay costo de flete. Si el lugar está retirado (quintas o salones a las afueras, sobre carretera o en colonias alejadas), puede aplicar un costo de flete que te confirmamos antes de apartar la fecha.</>}
                <small className="pp2-flete-extra">Si después de contratar cambias el lugar del evento y queda más retirado, puede aplicar un flete adicional.</small>
              </div>
            </div>

            {quote && (
              <div className="pp2-zona">
                <b>Formato del PDF</b>
                <div className="pp2-seg">
                  <button className={pdfFormat === 'letter' ? 'on' : ''} onClick={() => setPdfFormat('letter')} data-testid="pdf-format-letter-btn">Carta completa</button>
                  <button className={pdfFormat === 'half-letter' ? 'on' : ''} onClick={() => setPdfFormat('half-letter')} data-testid="pdf-format-half-btn">Media carta</button>
                </div>
              </div>
            )}

            <button className="pp2-pdfbtn" onClick={calculateQuote} disabled={!hasAnyService()} data-testid="generar-cotizacion-btn">
              Generar cotización
            </button>
            <button className="pp2-pdfbtn" onClick={downloadPDF} disabled={!quote} data-testid="descargar-pdf-btn">
              <FileDown size={16} /> Descargar PDF
            </button>
            <div className="pp2-share2">
              <button className="pp2-wa" onClick={shareWhatsApp} disabled={!quote} data-testid="pp2-whatsapp-btn"><MessageCircle size={15} /> WhatsApp</button>
              <button className="pp2-copybtn" onClick={copyQuoteText} disabled={!quote} data-testid="pp2-copy-text-btn"><Copy size={15} /> Copiar texto</button>
            </div>

            {folio && <p className="pp2-phone" data-testid="pp2-folio-display">Folio: <strong>{folio}</strong> · Vigencia 15 días naturales</p>}
            <p className="pp2-phone">Esta cotización no es un contrato.</p>
          </div>
        </div>
      </div>
      <footer className="pp2-wrap pp2-footer pp2-footer-cotizador">
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="pp2-privacy-btn" data-testid="pp2-privacy-link"><Shield size={14} /> Aviso de privacidad</a>
      </footer>
    </div>
  );
};

// ============ PICPARTY LIVE (MICROSITIO) ============
// ============ PICPARTY LIVE - INTERFAZ COMPLETA CON CLOUDINARY ============
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dpvliv2wl';
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'picparty_unsigned';

// Emojis temáticos PicParty
const PICPARTY_EMOJIS = ['👸', '✨', '👑', '💃', '📸'];

// Utilidad para LocalStorage con expiración de 24 horas
const SESSION_STORAGE_KEY = 'picparty_active_session';
const SESSION_EXPIRY_HOURS = 24;

const saveSessionToStorage = (sessionData) => {
  const data = {
    session: sessionData,
    expiry: Date.now() + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000)
  };
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
};

const getSessionFromStorage = () => {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    if (Date.now() > data.expiry) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return data.session;
  } catch {
    return null;
  }
};

const clearSessionStorage = () => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
};

// ============ LANDING PAGE DE VENTAS PICPARTYLIVE ============
const PicPartyLiveLanding = () => {
  const navigate = useNavigate();
  const [creatingDemo, setCreatingDemo] = useState(false);
  
  // Crear demo temporal individual (24 horas)
  const createTempDemo = async () => {
    setCreatingDemo(true);
    try {
      // Generar código único para demo
      const demoCode = 'DEMO-' + Math.random().toString(36).substring(2, 8).toUpperCase();
      
      // Crear sesión demo en backend
      const params = new URLSearchParams({
        code: demoCode,
        event_name: `Demo Temporal ${new Date().toLocaleDateString('es-MX')}`,
        event_type: 'demo',
        event_date: new Date().toISOString().split('T')[0],
        client_phone: '0000000000',
        is_demo: 'true'
      });
      
      await axios.post(`${API}/live/sessions/create?${params.toString()}`);
      
      toast.success(`¡Tu demo privada está lista! Código: ${demoCode}`);
      navigate(`/live?code=${demoCode}`);
    } catch (e) {
      toast.error("Error al crear demo. Intenta de nuevo.");
    }
    setCreatingDemo(false);
  };
  
  return (
    <div className="min-h-screen bg-premium-radial">
      {/* Header Premium */}
      <header className="header-premium sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src={PICPARTY_LOGO} alt="PicParty" className="h-10 w-10 object-contain" />
            <span className="text-xl font-bold text-gold">PicParty</span>
          </div>
          <Link to="/cotizador">
            <Button className="btn-gold">
              Cotizar Ahora
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge animado */}
          <div className="mb-6">
            <Badge className="badge-gold px-4 py-2 text-lg animate-pulse">
              PICPARTYLIVE
            </Badge>
          </div>
          
          {/* Título principal */}
          <h1 className="text-4xl md:text-6xl font-black text-pearl mb-4 leading-tight">
            <span className="text-gold">
              Cero Aplicaciones.
            </span>{" "}
            <br className="hidden md:block" />
            Fotos al Instante.
          </h1>
          
          <p className="text-xl text-pearl-muted mb-8 max-w-2xl mx-auto">
            Tus invitados suben fotos desde el navegador y aparecen al instante en la pantalla del evento. Sin descargas, sin complicaciones.
          </p>
          
          {/* CTA Principal */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={() => navigate('/cotizador')}
              className="h-14 px-8 text-lg font-bold btn-gold"
              data-testid="landing-cotizar-btn"
            >
              Quiero PICPARTYLIVE en mi fiesta
            </Button>
            <Button 
              variant="outline"
              onClick={createTempDemo}
              disabled={creatingDemo}
              className="h-14 px-8 text-lg btn-gold-outline"
              data-testid="landing-demo-btn"
            >
              {creatingDemo ? 'Creando...' : 'Probar GRATIS (24h)'}
            </Button>
          </div>
          
          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="card-premium p-6 text-center">
              <div className="text-4xl mb-3">♾️</div>
              <h3 className="text-gold font-bold text-lg mb-2">Almacenamiento SIN LÍMITE</h3>
              <p className="text-pearl-muted text-sm">Sube todas las fotos que quieras. Sin restricciones de espacio ni cantidad.</p>
            </Card>
            
            <Card className="card-premium p-6 text-center">
              <div className="text-4xl mb-3">📅</div>
              <h3 className="text-gold font-bold text-lg mb-2">Vigencia 6 MESES</h3>
              <p className="text-pearl-muted text-sm">Tienes hasta 6 meses para descargar todas tus fotos después del evento.</p>
            </Card>
            
            <Card className="card-premium p-6 text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="text-gold font-bold text-lg mb-2">Fotos al Instante</h3>
              <p className="text-pearl-muted text-sm">Las fotos aparecen en la pantalla en tiempo real desde el navegador. Cero apps.</p>
            </Card>
          </div>
          
          {/* Privacidad */}
          <div className="card-premium border-gold/30 rounded-2xl p-6 mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="text-3xl">🔒</span>
              <h3 className="text-xl font-bold text-gold">Galerías 100% Exclusivas</h3>
            </div>
            <p className="text-pearl-muted">
              Cada evento tiene su propia galería privada. Las fotos <strong className="text-gold">NUNCA se mezclan</strong> con otros clientes. 
              Tu código de acceso es único y solo tus invitados pueden ver y subir fotos.
            </p>
          </div>
          
          {/* Pricing Section */}
          <div className="card-premium rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-gold mb-6">Precios NETO</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-6">
                <div className="text-3xl font-black text-gold mb-2">$700</div>
                <div className="text-pearl font-semibold">Súper Precio</div>
                <p className="text-pearl-muted text-sm mt-2">Al contratar Cabina de Fotos o Video 360°</p>
              </div>
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-6">
                <div className="text-3xl font-black text-gold mb-2">$1,000</div>
                <div className="text-pearl font-semibold">Promo Expo</div>
                <p className="text-pearl-muted text-sm mt-2">Precio temporal por tiempo limitado</p>
              </div>
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-6">
                <div className="text-3xl font-black text-gold mb-2">$1,500</div>
                <div className="text-pearl font-semibold">Precio Normal</div>
                <p className="text-pearl-muted text-sm mt-2">PICPARTYLIVE solo (sin servicio adicional)</p>
              </div>
            </div>
          </div>
          
          {/* CTA Final */}
          <div className="text-center">
            <p className="text-pearl-muted mb-4">¿Ya tienes un código de evento?</p>
            <Button 
              variant="outline"
              onClick={() => {
                const code = prompt("Ingresa el código de tu evento:");
                if (code) navigate(`/live?code=${code}`);
              }}
              className="btn-gold-outline"
            >
              Ingresar Código
            </Button>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-gold/20 py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-pearl-muted">PICPARTYLIVE • picparty.net</p>
          <p className="text-pearl-muted/60 text-sm mt-2">© 2025 PicParty - Cabina Fotográfica</p>
        </div>
      </footer>
    </div>
  );
};

const PicPartyLive = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Soportar tanto ?event= como ?code= para acceso directo
  const eventCode = searchParams.get('event') || searchParams.get('code');
  const [session, setSession] = useState(null);
  const [code, setCode] = useState(eventCode || "");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [error, setError] = useState("");
  const [showPWABanner, setShowPWABanner] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estados para capítulos en galería pública
  const [publicChapters, setPublicChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState("all"); // "all" o chapter_id
  
  // === INSTAGRAM-STYLE STATES ===
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [likeAnimation, setLikeAnimation] = useState(null);
  const [likedPhotos, setLikedPhotos] = useState(() => {
    const saved = localStorage.getItem('picparty_liked_photos');
    return saved ? JSON.parse(saved) : [];
  });
  const lastTapRef = useRef(0);
  
  // NUEVOS ESTADOS para Interfaz Maestra
  const [viewMode, setViewMode] = useState("menu"); // menu, gallery, projection, download
  const [projectionEffect, setProjectionEffect] = useState("slideshow"); // slideshow, mosaic, popup
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [prevSlideIndex, setPrevSlideIndex] = useState(null); // Para crossfade
  const [kenBurnsEffect, setKenBurnsEffect] = useState(0); // 0-3 para rotar efectos
  const [newPhotoPopup, setNewPhotoPopup] = useState(null);
  const [downloadPassword, setDownloadPassword] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Detección de dispositivo para Smart View
  const [isMobile, setIsMobile] = useState(false);
  
  // Detectar si es móvil al cargar
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768; // < 768px = móvil
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // ID único para este invitado (anónimo)
  const [visitorId] = useState(() => {
    let id = localStorage.getItem('picparty_visitor_id');
    if (!id) {
      id = 'guest_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('picparty_visitor_id', id);
    }
    return id;
  });

  // Cargar fotos del muro colaborativo y capítulos
  const fetchGalleryPhotos = async (eventCode) => {
    try {
      const response = await axios.get(`${API}/live/photos/${eventCode}`);
      setGalleryPhotos(response.data.photos || []);
    } catch (e) {
      console.error("Error cargando galería:", e);
    }
  };

  // Cargar capítulos de la sesión (para galería pública)
  const fetchPublicChapters = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/live/sessions/${sessionId}/chapters`);
      setPublicChapters(response.data.chapters || []);
    } catch (e) {
      console.error("Error cargando capítulos:", e);
      setPublicChapters([]);
    }
  };

  // Fotos filtradas por capítulo seleccionado
  const filteredGalleryPhotos = selectedChapter === "all" 
    ? galleryPhotos 
    : galleryPhotos.filter(photo => photo.chapter_id === selectedChapter);

  // === INSTAGRAM-STYLE FUNCTIONS ===
  
  // Handle double tap for like (Instagram style)
  const handlePhotoTap = (photo, idx) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Double tap - dar like
      handleDoubleTapLike(photo);
    } else {
      // Single tap - abrir lightbox después de delay
      setTimeout(() => {
        if (Date.now() - lastTapRef.current >= DOUBLE_TAP_DELAY) {
          openLightbox(photo, idx);
        }
      }, DOUBLE_TAP_DELAY);
    }
    lastTapRef.current = now;
  };
  
  // Handle double tap like with animation
  const handleDoubleTapLike = (photo) => {
    const photoId = photo.id || photo._id;
    
    // Show heart animation
    setLikeAnimation(photoId);
    setTimeout(() => setLikeAnimation(null), 1000);
    
    // Toggle like
    if (!likedPhotos.includes(photoId)) {
      const newLiked = [...likedPhotos, photoId];
      setLikedPhotos(newLiked);
      localStorage.setItem('picparty_liked_photos', JSON.stringify(newLiked));
      // Send like to server
      addReaction(photoId, '❤️');
    }
  };
  
  // Open lightbox
  const openLightbox = (photo, idx) => {
    setLightboxPhoto(photo);
    setLightboxIndex(idx);
  };
  
  // Close lightbox
  const closeLightbox = () => {
    setLightboxPhoto(null);
  };
  
  // Navigate lightbox
  const navigateLightbox = (direction) => {
    const newIndex = direction === 'next' 
      ? (lightboxIndex + 1) % galleryPhotos.length
      : (lightboxIndex - 1 + galleryPhotos.length) % galleryPhotos.length;
    setLightboxIndex(newIndex);
    setLightboxPhoto(galleryPhotos[newIndex]);
  };

  // Agregar reacción a una foto
  const addReaction = async (photoId, emoji) => {
    try {
      await axios.post(`${API}/live/photos/${photoId}/react`, { emoji });
      // Actualizar galería
      if (session) fetchGalleryPhotos(session.code);
    } catch (e) {
      console.error("Error al reaccionar");
    }
  };

  // ============ FUNCIONES PARA PROYECCIÓN ============
  
  // Toggle pantalla completa
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => console.log(err));
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Estado para pre-carga de imágenes
  const [preloadedImages, setPreloadedImages] = useState([]);
  const [nextSlideIndex, setNextSlideIndex] = useState(1);
  const [fadeState, setFadeState] = useState('visible'); // 'visible', 'fading'

  // Pre-cargar imágenes para slideshow fluido
  useEffect(() => {
    if (galleryPhotos.length > 0) {
      const preload = galleryPhotos.slice(0, 50).map(photo => {
        const img = new Image();
        img.src = photo.cloudinary_url;
        return img;
      });
      setPreloadedImages(preload);
    }
  }, [galleryPhotos]);

  // Slideshow automático con crossfade suave (1.5s) y Ken Burns (5s)
  useEffect(() => {
    if (viewMode === "projection" && projectionEffect === "slideshow" && galleryPhotos.length > 0) {
      const interval = setInterval(() => {
        // Guardar el índice anterior para crossfade
        setCurrentSlideIndex(prev => {
          setPrevSlideIndex(prev);
          const next = (prev + 1) % galleryPhotos.length;
          setNextSlideIndex((next + 1) % galleryPhotos.length);
          // Rotar el efecto Ken Burns (4 variantes)
          setKenBurnsEffect(e => (e + 1) % 4);
          return next;
        });
        // Limpiar imagen anterior después del crossfade (1.5s)
        setTimeout(() => setPrevSlideIndex(null), 1600);
      }, 5000); // 5 segundos = duración del Ken Burns
      return () => clearInterval(interval);
    }
  }, [viewMode, projectionEffect, galleryPhotos.length]);

  // Detectar nuevas fotos para efecto Pop-up
  const prevGalleryLength = useRef(galleryPhotos.length);
  useEffect(() => {
    if (viewMode === "projection" && projectionEffect === "popup") {
      if (galleryPhotos.length > prevGalleryLength.current && galleryPhotos.length > 0) {
        // Nueva foto detectada
        const newPhoto = galleryPhotos[0]; // La más reciente está primero
        setNewPhotoPopup(newPhoto);
        // Ocultar después de 5 segundos
        setTimeout(() => setNewPhotoPopup(null), 5000);
      }
      prevGalleryLength.current = galleryPhotos.length;
    }
  }, [galleryPhotos, viewMode, projectionEffect]);

  // Polling más rápido en modo proyección (cada 3 segundos)
  useEffect(() => {
    if (viewMode === "projection" && session) {
      const interval = setInterval(() => {
        fetchGalleryPhotos(session.code);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [viewMode, session]);

  // Función para intentar descargar ZIP
  const handleDownload = async () => {
    // Validar contraseña: últimos 4 dígitos del teléfono del cliente
    const clientPhone = session?.client_phone || "";
    const last4Digits = clientPhone.slice(-4);
    
    // Si no hay teléfono configurado, mostrar mensaje
    if (!clientPhone || clientPhone.length < 4) {
      setDownloadError("Este evento no tiene contraseña configurada. Contacta al administrador.");
      return;
    }
    
    if (downloadPassword !== last4Digits) {
      setDownloadError("Contraseña incorrecta. Usa los últimos 4 dígitos del teléfono del cliente.");
      return;
    }
    
    setIsDownloading(true);
    setDownloadError("");
    
    try {
      // Crear un zip con las URLs de las fotos
      const photoUrls = galleryPhotos.map(p => p.cloudinary_url);
      if (photoUrls.length === 0) {
        toast.error("No hay fotos para descargar");
        setIsDownloading(false);
        return;
      }
      
      // Por ahora, abrir galería de Cloudinary en nueva pestaña
      const folderPath = session?.cloudinary_folder || `ADOCA/${session?.event_name}`;
      window.open(`https://cloudinary.com/console/media_library/folders/${encodeURIComponent(folderPath)}`, '_blank');
      toast.success("Abriendo galería en Cloudinary...");
      setViewMode("menu");
    } catch (err) {
      toast.error("Error al descargar");
    }
    setIsDownloading(false);
  };

  // Verificar sesión guardada al iniciar
  useEffect(() => {
    const initSession = async () => {
      // Primero verificar si hay código en URL (prioridad)
      if (eventCode) {
        await handleJoin(eventCode);
        setLoading(false);
        return;
      }
      
      // Si no hay código en URL, buscar sesión guardada
      const savedSession = getSessionFromStorage();
      if (savedSession) {
        // Verificar que la sesión sigue activa en el servidor
        try {
          const response = await axios.get(`${API}/live/scan/${savedSession.code}`);
          setSession(response.data);
          saveSessionToStorage(response.data);
          fetchGalleryPhotos(savedSession.code);
          // Cargar capítulos si existen
          if (response.data.id) {
            fetchPublicChapters(response.data.id);
          }
          toast.success(`¡Bienvenido de nuevo a ${response.data.event_name}!`);
          
          // SMART VIEW: Si es móvil, ir directo a galería
          if (window.innerWidth < 768) {
            setViewMode("gallery");
          } else {
            setViewMode("menu");
          }
        } catch {
          clearSessionStorage();
          // No redirigir, la Landing Page se mostrará automáticamente
        }
      }
      setLoading(false);
      
      // Mostrar banner PWA después de 3 segundos
      setTimeout(() => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
        if (!isStandalone && !localStorage.getItem('pwa_banner_dismissed')) {
          setShowPWABanner(true);
        }
      }, 3000);
    };
    
    initSession();
  }, []);

  // Polling para actualizar galería en tiempo real (cada 10 segundos)
  useEffect(() => {
    if (!session) return;
    
    const interval = setInterval(() => {
      fetchGalleryPhotos(session.code);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [session]);

  // Unirse al evento
  const handleJoin = async (codeToUse) => {
    const targetCode = codeToUse || code;
    if (!targetCode.trim()) {
      toast.error("Ingresa un código");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/live/scan/${targetCode}`);
      setSession(response.data);
      saveSessionToStorage(response.data);
      fetchGalleryPhotos(targetCode);
      // Cargar capítulos si existen
      if (response.data.id) {
        fetchPublicChapters(response.data.id);
      }
      toast.success(`¡Bienvenido a ${response.data.event_name}!`);
      
      // SMART VIEW: Si es móvil, ir directo a galería
      if (window.innerWidth < 768) {
        setViewMode("gallery");
      } else {
        setViewMode("menu"); // PC/Tablet: mostrar menú maestro
      }
    } catch (e) {
      setError("Código inválido o evento no activo");
      toast.error("Código inválido o evento no activo");
    } finally {
      setLoading(false);
    }
  };

  // Cerrar sesión y redirigir a landing
  const handleLogout = () => {
    clearSessionStorage();
    setSession(null);
    setCode("");
    setUploadedPhotos([]);
    setGalleryPhotos([]);
    navigate('/'); // SEGURIDAD: Redirigir a landing
  };

  // Generar estructura de carpeta Cloudinary: ADOCA/MES/FECHA/TIPO_NOMBRE/
  const generateCloudinaryFolder = () => {
    const meses = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 
                   'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
    
    // Usar fecha del evento o fecha actual
    const eventDate = session.event_date ? new Date(session.event_date + 'T12:00:00') : new Date();
    const mes = meses[eventDate.getMonth()];
    const dia = String(eventDate.getDate()).padStart(2, '0');
    const mesNum = String(eventDate.getMonth() + 1).padStart(2, '0');
    const anio = String(eventDate.getFullYear()).slice(-2);
    const fechaFormato = `${dia}-${mesNum}-${anio}`; // Ej: 21-03-26
    
    // Tipo de evento en mayúsculas
    const tipoEvento = (session.event_type || 'EVENTO').toUpperCase().replace('QUINCEANIOS', 'XV');
    
    // Nombre del evento limpio
    const nombreEvento = session.event_name
      .toUpperCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Quitar acentos
      .replace(/[^A-Z0-9\s]/g, '') // Solo letras y números
      .replace(/\s+/g, '_'); // Espacios a guiones bajos
    
    // Estructura final: ADOCA/MES/FECHA/TIPO_NOMBRE/
    return `ADOCA/${mes}/${fechaFormato}/${tipoEvento}_${nombreEvento}`;
  };

  // Subir foto a Cloudinary y registrar en backend
  const uploadToCloudinary = async (file) => {
    const folderPath = generateCloudinaryFolder();
    
    // Validar tamaño del archivo (máximo 15MB)
    const maxSize = 15 * 1024 * 1024; // 15MB
    if (file.size > maxSize) {
      throw new Error('ARCHIVO_GRANDE');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', folderPath);
    
    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          },
          timeout: 60000 // 60 segundos timeout
        }
      );
      
      // Registrar foto en el backend para el muro colaborativo
      await axios.post(`${API}/live/photos`, {
        event_code: session.code,
        cloudinary_url: response.data.secure_url,
        thumbnail_url: response.data.secure_url.replace('/upload/', '/upload/w_300,h_300,c_fill/'),
        uploader_id: visitorId,
        cloudinary_folder: folderPath
      });
      
      return response.data;
    } catch (error) {
      console.error('Error subiendo a Cloudinary:', error);
      // Propagar el error con más contexto
      if (error.message === 'ARCHIVO_GRANDE') {
        throw error;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('TIMEOUT');
      } else if (!navigator.onLine) {
        throw new Error('SIN_CONEXION');
      }
      throw error;
    }
  };

  // Manejar selección de archivos (máximo 10 fotos)
  const handleFileSelect = async (e) => {
    let files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limitar a 10 fotos máximo
    if (files.length > 10) {
      toast.warning("Máximo 10 fotos a la vez. Se subirán las primeras 10.");
      files = files.slice(0, 10);
    }
    
    setUploading(true);
    setUploadProgress(0);
    setTotalFiles(files.length);
    setCurrentFileIndex(0);
    
    const uploaded = [];
    const errors = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setCurrentFileIndex(i + 1);
      try {
        const result = await uploadToCloudinary(file);
        uploaded.push({
          id: result.public_id,
          url: result.secure_url,
          thumbnail: result.secure_url.replace('/upload/', '/upload/w_200,h_200,c_fill/'),
          name: file.name,
          timestamp: new Date().toLocaleTimeString('es-MX'),
          emoji: PICPARTY_EMOJIS[Math.floor(Math.random() * PICPARTY_EMOJIS.length)]
        });
      } catch (err) {
        // Mensajes de error específicos
        let errorMsg = `❌ Error subiendo ${file.name}`;
        if (err.message === 'ARCHIVO_GRANDE') {
          errorMsg = `📦 ${file.name}: Archivo muy pesado (máx 15MB)`;
        } else if (err.message === 'TIMEOUT') {
          errorMsg = `⏱️ ${file.name}: Tiempo de espera agotado`;
        } else if (err.message === 'SIN_CONEXION') {
          errorMsg = `📡 Error de conexión: Revisa tu internet`;
        }
        errors.push(errorMsg);
        toast.error(errorMsg, { duration: 5000 });
      }
    }
    
    if (uploaded.length > 0) {
      setUploadedPhotos(prev => [...uploaded, ...prev]);
      // Actualizar galería colaborativa
      if (session) fetchGalleryPhotos(session.code);
      
      // Mensaje de éxito claro y visible
      if (uploaded.length === 1) {
        toast.success("✅ ¡Foto subida con éxito! Mírala en la pantalla. 📺", { 
          duration: 5000,
          style: { background: '#10B981', color: 'white', fontWeight: 'bold' }
        });
      } else {
        toast.success(`✅ ¡${uploaded.length} fotos subidas con éxito! Míralas en la pantalla. 📺`, { 
          duration: 5000,
          style: { background: '#10B981', color: 'white', fontWeight: 'bold' }
        });
      }
    }
    
    // Si hubo errores pero también éxitos, mostrar resumen
    if (errors.length > 0 && uploaded.length > 0) {
      toast.warning(`${uploaded.length} subidas, ${errors.length} con error`, { duration: 4000 });
    }
    
    // Si todas fallaron
    if (errors.length > 0 && uploaded.length === 0) {
      toast.error("❌ No se pudo subir ninguna foto. Intenta de nuevo.", { duration: 5000 });
    }
    
    setUploading(false);
    setUploadProgress(0);
    setTotalFiles(0);
    setCurrentFileIndex(0);
    
    // Limpiar el input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Función para prompt de instalación PWA
  const handleAddToHome = () => {
    setShowPWABanner(false);
    localStorage.setItem('pwa_banner_dismissed', 'true');
    // Mostrar instrucciones según el dispositivo
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      toast.info("Toca el ícono de compartir ⬆️ y luego 'Añadir a pantalla de inicio'", { duration: 5000 });
    } else {
      toast.info("Toca el menú ⋮ y selecciona 'Añadir a pantalla de inicio'", { duration: 5000 });
    }
  };

  const dismissPWABanner = () => {
    setShowPWABanner(false);
    localStorage.setItem('pwa_banner_dismissed', 'true');
  };

  // Pantalla de carga inicial
  if (loading) {
    return (
      <div className="min-h-screen bg-premium-radial flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto bg-gold/20 border border-gold/30 rounded-full flex items-center justify-center mb-6 animate-pulse">
            <img src={PICPARTY_LOGO} alt="PicParty" className="w-16 h-16 object-contain" />
          </div>
          <p className="text-pearl text-xl">Cargando...</p>
        </div>
      </div>
    );
  }
  
  // Si no hay código en URL, ni sesión guardada, mostrar Landing Page de Ventas
  if (!eventCode && !session) {
    return <PicPartyLiveLanding />;
  }

  return (
    <div className="min-h-screen bg-premium-radial">
      {/* Banner PWA flotante */}
      {showPWABanner && (
        <div className="fixed top-14 left-2 right-2 z-50 animate-in slide-in-from-top">
          <div className="bg-gold/90 rounded-xl p-3 shadow-2xl">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📱</span>
                <p className="text-night text-sm font-medium">
                  ¿Subir fotos más rápido? ¡Agrega a inicio!
                </p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" onClick={handleAddToHome} className="bg-night text-gold hover:bg-night/90 text-xs px-2 h-7">
                  Agregar
                </Button>
                <Button size="sm" variant="ghost" onClick={dismissPWABanner} className="text-night hover:bg-night/20 text-xs px-2 h-7">
                  ✕
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Premium */}
      <header className="header-premium sticky top-0 z-40">
        <div className="container mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src={PICPARTY_LOGO} alt="PicParty" className="h-8 w-8 object-contain" />
            <span className="text-lg font-bold text-gold">PicParty</span>
            <Badge className="badge-gold animate-pulse text-xs">LIVE</Badge>
          </div>
          {session && (
            <Badge className="badge-gold text-xs">
              {session.code}
            </Badge>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 pb-20">
        {!session ? (
          /* ============ PANTALLA DE ENTRADA ============ */
          <div className="max-w-md mx-auto text-center pt-8">
            
            <div className="w-28 h-28 mx-auto bg-gold/20 border border-gold/30 rounded-full flex items-center justify-center mb-6 shadow-2xl shadow-gold/20">
              <img src={PICPARTY_LOGO} alt="PicParty" className="w-20 h-20 object-contain" />
            </div>
            <h1 className="text-3xl font-black text-pearl mb-2">Únete a la Fiesta</h1>
            <p className="text-pearl-muted mb-6">Ingresa el código del evento</p>
            
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}
            
            <Card className="card-premium">
              <CardContent className="pt-6 space-y-4">
                <Input 
                  placeholder="CÓDIGO"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="input-premium text-center text-2xl h-14 tracking-[0.2em] font-bold"
                  data-testid="event-code-input"
                />
                <Button 
                  onClick={() => handleJoin()}
                  disabled={loading}
                  className="w-full h-12 text-lg font-bold btn-gold"
                  data-testid="join-event-btn"
                >
                  ENTRAR AL EVENTO
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* ============ INTERFAZ MAESTRA PICPARTYLIVE ============ */
          <>
            {/* Banner DEMO TEMPORAL (24 horas) */}
            {(session.is_demo || session.code?.startsWith('DEMO-')) && (
              <div className="max-w-4xl mx-auto mb-4 p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/50 rounded-lg">
                <p className="text-amber-300 text-center font-semibold flex items-center justify-center gap-2">
                  <span className="text-2xl">⏰</span>
                  <span>
                    <strong>Muestra temporal:</strong> Esta galería se borrará en 24 horas.<br/>
                    <span className="text-amber-400">¡Contrata el plan completo por 6 meses!</span>
                  </span>
                </p>
                <div className="text-center mt-2">
                  <Button 
                    size="sm"
                    className="btn-gold"
                    onClick={() => window.open('/cotizador', '_blank')}
                  >
                    Ver Precios
                  </Button>
                </div>
              </div>
            )}
            
            {/* Banner legado para código 9022 */}
            {session.code === '9022' && !session.is_demo && (
              <div className="max-w-4xl mx-auto mb-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg animate-pulse">
                <p className="text-amber-300 text-sm text-center font-semibold">
                  ⚠️ Galería de Prueba Pública: El contenido se comparte entre todos los visitantes.
                </p>
              </div>
            )}

            {/* ============ MODO MENÚ PRINCIPAL ============ */}
            {viewMode === "menu" && (
              <div className="max-w-2xl mx-auto px-4">
                {/* Header del evento */}
                <div className="text-center mb-8">
                  <div className="w-20 h-20 mx-auto mb-4">
                    <img src={PICPARTY_LOGO} alt="PicParty" className="w-full h-full object-contain" />
                  </div>
                  <h1 className="text-3xl font-black text-pearl mb-2">
                    Bienvenido
                  </h1>
                  <p className="text-2xl font-bold text-gold">
                    {session.event_name}
                  </p>
                  {session.event_type && (
                    <Badge className="mt-3 badge-gold text-base px-4 py-1">
                      {session.event_type === 'boda' && 'Boda'}
                      {session.event_type === 'quinceanios' && 'Quinceaños'}
                      {session.event_type === 'cumpleanos' && 'Cumpleaños'}
                      {session.event_type === 'empresarial' && 'Empresarial'}
                      {session.event_type === 'fiesta' && 'Fiesta'}
                      {session.event_type === 'publico' && 'Evento Público'}
                      {session.event_type === 'otro' && `${session.event_type_custom || 'Evento'}`}
                    </Badge>
                  )}
                  <p className="text-pearl-muted mt-3">{galleryPhotos.length} fotos en la galería</p>
                </div>

                {/* 3 BOTONES PRINCIPALES */}
                <div className="grid gap-4 mb-8">
                  {/* Botón PROYECTAR */}
                  <Button
                    className="h-24 text-xl font-bold card-premium border-gold/30 hover:border-gold/50 text-pearl"
                    onClick={() => setViewMode("projection-select")}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">📺</span>
                      <div className="text-left">
                        <div>PROYECTAR EN TV</div>
                        <div className="text-sm font-normal opacity-80">Mostrar fotos en pantalla grande</div>
                      </div>
                    </div>
                  </Button>

                  {/* Botón VER GALERÍA - DESTACADO EN DORADO */}
                  <Button
                    className="h-24 text-xl font-bold btn-upload-gold"
                    onClick={() => setViewMode("gallery")}
                    data-testid="btn-ver-galeria"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">📸</span>
                      <div className="text-left">
                        <div>SUBIR / VER FOTOS</div>
                        <div className="text-sm font-normal opacity-80">Sube fotos y ve la galería</div>
                      </div>
                    </div>
                  </Button>

                  {/* Botón DESCARGAR */}
                  <Button
                    className="h-24 text-xl font-bold card-premium border-gold/30 hover:border-gold/50 text-pearl"
                    onClick={() => setViewMode("download")}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">📥</span>
                      <div className="text-left">
                        <div>DESCARGAR EVENTO</div>
                        <div className="text-sm font-normal opacity-80">Bajar todas las fotos (Admin)</div>
                      </div>
                    </div>
                  </Button>
                </div>

                {/* Botón salir pequeño */}
                <Button 
                  variant="ghost" 
                  className="w-full text-pearl-muted hover:text-gold text-sm"
                  onClick={handleLogout}
                >
                  ← Salir del evento
                </Button>
              </div>
            )}

            {/* ============ MODO SELECTOR DE EFECTOS ============ */}
            {viewMode === "projection-select" && (
              <div className="max-w-3xl mx-auto px-4">
                <Button 
                  variant="ghost" 
                  className="mb-4 text-pearl-muted hover:text-gold"
                  onClick={() => setViewMode("menu")}
                >
                  ← Volver al menú
                </Button>
                
                <h2 className="text-2xl font-bold text-pearl text-center mb-6">
                  Selecciona el Efecto de Proyección
                </h2>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  {/* Slideshow */}
                  <Card 
                    className={`cursor-pointer transition-all hover:scale-105 card-premium ${projectionEffect === 'slideshow' ? 'ring-2 ring-gold' : ''}`}
                    onClick={() => setProjectionEffect('slideshow')}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="text-5xl mb-3">🖼️</div>
                      <h3 className="text-pearl font-bold text-lg">Slideshow</h3>
                      <p className="text-pearl-muted text-sm mt-2">Una foto con transición suave (Fade)</p>
                    </CardContent>
                  </Card>

                  {/* Mosaico */}
                  <Card 
                    className={`cursor-pointer transition-all hover:scale-105 card-premium ${projectionEffect === 'mosaic' ? 'ring-2 ring-gold' : ''}`}
                    onClick={() => setProjectionEffect('mosaic')}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="text-5xl mb-3">🔲</div>
                      <h3 className="text-pearl font-bold text-lg">Mosaico</h3>
                      <p className="text-pearl-muted text-sm mt-2">Cuadrícula dinámica de fotos</p>
                    </CardContent>
                  </Card>

                  {/* Pop-up */}
                  <Card 
                    className={`cursor-pointer transition-all hover:scale-105 card-premium ${projectionEffect === 'popup' ? 'ring-2 ring-gold' : ''}`}
                    onClick={() => setProjectionEffect('popup')}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="text-5xl mb-3">✨</div>
                      <h3 className="text-pearl font-bold text-lg">Pop-up</h3>
                      <p className="text-pearl-muted text-sm mt-2">Foto nueva aparece grande 5 seg</p>
                    </CardContent>
                  </Card>
                </div>

                <Button
                  className="w-full h-14 text-lg font-bold btn-gold"
                  onClick={() => { setViewMode("projection"); fetchGalleryPhotos(session.code); }}
                >
                  Iniciar Proyección
                </Button>
              </div>
            )}

            {/* ============ MODO PROYECCIÓN ============ */}
            {viewMode === "projection" && (
              <div className="fixed inset-0 bg-black z-50">
                {/* Logo PICPARTYLIVE en esquina */}
                <div className="absolute top-4 left-4 z-10">
                  <img src={PICPARTY_LOGO} alt="PicParty" className="h-12 opacity-80" />
                  <span className="text-white/60 text-xs ml-2">LIVE</span>
                </div>

                {/* Botones de control */}
                <div className="absolute top-4 right-4 z-10 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="bg-black/50 border-white/30 text-white"
                    onClick={toggleFullscreen}
                  >
                    {isFullscreen ? '⏹️ Salir' : '⛶ Pantalla Completa'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="bg-black/50 border-white/30 text-white"
                    onClick={() => { setViewMode("projection-select"); if (document.fullscreenElement) document.exitFullscreen(); }}
                  >
                    ✕ Cerrar
                  </Button>
                </div>

                {/* Contador de fotos */}
                <div className="absolute bottom-4 left-4 z-10 bg-black/60 px-3 py-1 rounded-full">
                  <span className="text-white/80 text-sm">📸 {galleryPhotos.length} fotos</span>
                </div>

                {/* EFECTO SLIDESHOW - Pantalla completa estilo boda con Ken Burns */}
                {projectionEffect === "slideshow" && galleryPhotos.length > 0 && (
                  <div className="w-full h-full bg-black relative overflow-hidden">
                    {/* Viñeta elegante para efecto cinematográfico */}
                    <div className="absolute inset-0 pointer-events-none z-20"
                      style={{
                        background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.5) 100%)'
                      }}
                    />
                    
                    {/* Imagen anterior (para crossfade suave 1.5s) */}
                    {prevSlideIndex !== null && galleryPhotos[prevSlideIndex] && (
                      <img 
                        src={galleryPhotos[prevSlideIndex]?.cloudinary_url}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover slideshow-crossfade-image"
                        style={{ 
                          opacity: 0,
                          zIndex: 1,
                          filter: 'brightness(1.02) contrast(1.02)'
                        }}
                      />
                    )}
                    
                    {/* Imagen actual con Ken Burns - PANTALLA COMPLETA */}
                    <img 
                      key={currentSlideIndex}
                      src={galleryPhotos[currentSlideIndex]?.cloudinary_url}
                      alt="Foto del evento"
                      className={`absolute inset-0 w-full h-full object-cover slideshow-ken-burns slideshow-crossfade-image ${
                        kenBurnsEffect === 0 ? 'ken-burns-zoom-in' :
                        kenBurnsEffect === 1 ? 'ken-burns-zoom-out' :
                        kenBurnsEffect === 2 ? 'ken-burns-pan-left' :
                        'ken-burns-pan-right'
                      }`}
                      style={{ 
                        opacity: 1,
                        zIndex: 2,
                        filter: 'brightness(1.02) contrast(1.02)'
                      }}
                    />
                    
                    {/* Pre-carga de siguiente imagen (invisible) */}
                    {galleryPhotos[nextSlideIndex] && (
                      <img 
                        src={galleryPhotos[nextSlideIndex]?.cloudinary_url}
                        alt=""
                        className="hidden"
                        aria-hidden="true"
                      />
                    )}
                    
                    {/* Contador elegante estilo boda */}
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30">
                      <div className="bg-black/40 backdrop-blur-sm px-6 py-2 rounded-full border border-white/10">
                        <span className="text-white/90 text-sm font-light tracking-wider">
                          {currentSlideIndex + 1} de {galleryPhotos.length}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* EFECTO MOSAICO - Optimizado con grid fluido */}
                {projectionEffect === "mosaic" && (
                  <div className="w-full h-full p-4 overflow-hidden bg-black">
                    <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 h-full auto-rows-fr">
                      {galleryPhotos.slice(0, 30).map((photo, idx) => (
                        <div 
                          key={photo.id || idx} 
                          className="relative overflow-hidden rounded-lg"
                          style={{ 
                            opacity: 0,
                            animation: `fadeInMosaic 0.6s ease-out ${idx * 0.05}s forwards`
                          }}
                        >
                          <img 
                            src={photo.thumbnail_url || photo.cloudinary_url}
                            alt="Foto"
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* EFECTO POP-UP */}
                {projectionEffect === "popup" && (
                  <div className="w-full h-full">
                    {/* Mosaico de fondo */}
                    <div className="absolute inset-0 p-4 opacity-40">
                      <div className="grid grid-cols-4 md:grid-cols-5 gap-2 h-full auto-rows-fr">
                        {galleryPhotos.slice(0, 20).map((photo, idx) => (
                          <div key={photo._id || idx} className="rounded overflow-hidden">
                            <img 
                              src={photo.thumbnail_url || photo.cloudinary_url}
                              alt="Foto"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Pop-up de nueva foto */}
                    {newPhotoPopup && (
                      <div className="absolute inset-0 flex items-center justify-center bg-night/90 z-20">
                        <div className="relative" style={{ animation: 'scaleIn 0.5s ease-out' }}>
                          <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gold px-4 py-1 rounded-full text-night font-bold">
                            ¡NUEVA FOTO!
                          </div>
                          <img 
                            src={newPhotoPopup.cloudinary_url}
                            alt="Nueva foto"
                            className="max-w-[80vw] max-h-[80vh] object-contain rounded-xl shadow-2xl ring-4 ring-gold"
                          />
                        </div>
                      </div>
                    )}

                    {/* Mensaje de espera */}
                    {!newPhotoPopup && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center text-pearl/60">
                          <div className="text-6xl mb-4 animate-pulse">📸</div>
                          <p className="text-xl">Esperando nuevas fotos...</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sin fotos */}
                {galleryPhotos.length === 0 && (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-pearl">
                      <div className="text-8xl mb-4">📷</div>
                      <h3 className="text-2xl font-bold mb-2">Sin fotos aún</h3>
                      <p className="text-pearl-muted">Las fotos aparecerán aquí en tiempo real</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ============ MODO GALERÍA (con subida) ============ */}
            {viewMode === "gallery" && (
              <div className="max-w-4xl mx-auto px-4 pb-32">
                <Button 
                  variant="ghost" 
                  className="mb-4 text-pearl-muted hover:text-gold"
                  onClick={() => setViewMode("menu")}
                >
                  ← Volver al menú
                </Button>

                {/* Instagram-style header - Solo nombre y contador */}
                <div className="text-center mb-4">
                  <h2 className="text-xl font-bold text-pearl">{session.event_name}</h2>
                  <p className="text-pearl-muted text-sm">{galleryPhotos.length} fotos</p>
                </div>

                {/* TABS: Subir / Ver - Estilo minimalista */}
                <div className="flex border-b border-gold/20 mb-4">
                  <button 
                    className={`flex-1 py-3 text-center transition-all ${activeTab === "upload" ? "border-b-2 border-gold text-gold" : "text-pearl-muted"}`}
                    onClick={() => setActiveTab("upload")}
                  >
                    <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                    </svg>
                  </button>
                  <button 
                    className={`flex-1 py-3 text-center transition-all ${activeTab === "gallery" ? "border-b-2 border-gold text-gold" : "text-pearl-muted"}`}
                    onClick={() => { setActiveTab("gallery"); fetchGalleryPhotos(session.code); }}
                  >
                    <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                    </svg>
                  </button>
                </div>

                {activeTab === "upload" ? (
                  <Card className="card-premium">
                    <CardContent className="p-6">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        id="photo-upload-gallery"
                      />
                      <label 
                        htmlFor="photo-upload-gallery"
                        className="block cursor-pointer"
                      >
                        <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${uploading ? 'border-gold bg-gold/10' : 'border-gold/30 hover:border-gold hover:bg-gold/5'}`}>
                          {uploading ? (
                            <div className="space-y-3">
                              <div className="text-5xl animate-bounce">📤</div>
                              <p className="text-pearl font-bold">Subiendo foto {currentFileIndex} de {totalFiles}...</p>
                              <div className="w-full bg-night rounded-full h-3">
                                <div className="bg-gold h-3 rounded-full transition-all" style={{width: `${uploadProgress}%`}}></div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <svg className="w-16 h-16 mx-auto mb-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                              </svg>
                              <p className="text-gold font-semibold text-lg">Subir fotos</p>
                              <p className="text-pearl-muted text-sm">Toca para seleccionar</p>
                            </>
                          )}
                        </div>
                      </label>
                      
                      {/* Info de almacenamiento */}
                      <p className="text-pearl-muted text-xs text-center mt-4">
                        Almacenamiento ilimitado
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  /* === GALERÍA CON FILTRO POR CAPÍTULOS === */
                  <>
                    {/* Pestañas de capítulos (solo si hay capítulos) */}
                    {publicChapters.length > 0 && (
                      <div className="flex overflow-x-auto gap-2 mb-4 pb-2 scrollbar-hide">
                        <button
                          className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                            selectedChapter === "all" 
                              ? "bg-gold text-night" 
                              : "bg-night/50 text-pearl-muted hover:bg-gold/20 hover:text-gold border border-gold/30"
                          }`}
                          onClick={() => setSelectedChapter("all")}
                        >
                          Todas ({galleryPhotos.length})
                        </button>
                        {publicChapters.map(chapter => {
                          const count = galleryPhotos.filter(p => p.chapter_id === chapter.id).length;
                          return (
                            <button
                              key={chapter.id}
                              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                                selectedChapter === chapter.id 
                                  ? "bg-gold text-night" 
                                  : "bg-night/50 text-pearl-muted hover:bg-gold/20 hover:text-gold border border-gold/30"
                              }`}
                              onClick={() => setSelectedChapter(chapter.id)}
                            >
                              {chapter.name} ({count})
                            </button>
                          );
                        })}
                        {/* Mostrar fotos sin capítulo si hay alguna */}
                        {galleryPhotos.some(p => !p.chapter_id) && (
                          <button
                            className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                              selectedChapter === "uncategorized" 
                                ? "bg-gold text-night" 
                                : "bg-night/50 text-pearl-muted hover:bg-gold/20 hover:text-gold border border-gold/30"
                            }`}
                            onClick={() => setSelectedChapter("uncategorized")}
                          >
                            Otras ({galleryPhotos.filter(p => !p.chapter_id).length})
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* INSTAGRAM-STYLE GRID */}
                    <div className="grid grid-cols-3 gap-0.5 md:gap-1">
                      {(selectedChapter === "uncategorized" 
                        ? galleryPhotos.filter(p => !p.chapter_id)
                        : filteredGalleryPhotos
                      ).map((photo, idx) => {
                      const photoId = photo.id || photo._id;
                      const isLiked = likedPhotos.includes(photoId);
                      const showHeartAnim = likeAnimation === photoId;
                      
                      return (
                        <div 
                          key={photoId || idx} 
                          className="relative aspect-square cursor-pointer overflow-hidden bg-black/20"
                          onClick={() => handlePhotoTap(photo, idx)}
                        >
                          <img 
                            src={photo.thumbnail_url || photo.cloudinary_url} 
                            alt=""
                            className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
                            loading="lazy"
                            draggable={false}
                          />
                          
                          {/* Heart animation on double tap - CORAZÓN BLANCO GRANDE */}
                          {showHeartAnim && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <svg className="w-24 h-24 text-white drop-shadow-2xl like-heart-animation" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                              </svg>
                            </div>
                          )}
                          
                          {/* Like counter + heart icon debajo de cada foto */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                            <div className="flex items-center gap-1">
                              <svg 
                                className={`w-4 h-4 ${isLiked ? 'text-red-500' : 'text-white/80'}`} 
                                fill={isLiked ? 'currentColor' : 'none'} 
                                stroke="currentColor" 
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                              </svg>
                              <span className="text-white text-xs">{photo.likes || 0}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {(selectedChapter === "uncategorized" 
                      ? galleryPhotos.filter(p => !p.chapter_id)
                      : filteredGalleryPhotos
                    ).length === 0 && (
                      <div className="col-span-full text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-3">
                          <img src={PICPARTY_LOGO} alt="PicParty" className="w-full h-full object-contain opacity-50" />
                        </div>
                        <p className="text-pearl-muted">
                          {galleryPhotos.length === 0 
                            ? "No hay fotos aún. Sé el primero en subir." 
                            : "No hay fotos en esta sección."}
                        </p>
                      </div>
                    )}
                  </div>
                  </>
                )}
              </div>
            )}

            {/* ============ MODO DESCARGA ============ */}
            {viewMode === "download" && (
              <div className="max-w-md mx-auto px-4">
                <Button 
                  variant="ghost" 
                  className="mb-4 text-pearl-muted hover:text-gold"
                  onClick={() => setViewMode("menu")}
                >
                  ← Volver al menú
                </Button>

                <Card className="card-premium">
                  <CardHeader className="text-center">
                    <div className="text-5xl mb-2">📥</div>
                    <CardTitle className="text-pearl">Descargar Evento</CardTitle>
                    <CardDescription className="text-pearl-muted">
                      Descarga todas las fotos del evento. Usa los últimos 4 dígitos del teléfono del cliente.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-pearl">Contraseña de Descarga</Label>
                      <Input 
                        type="password"
                        placeholder="Últimos 4 dígitos del teléfono"
                        maxLength={4}
                        value={downloadPassword}
                        onChange={(e) => setDownloadPassword(e.target.value.replace(/\D/g, ''))}
                        className="input-premium mt-1 text-center text-xl tracking-widest"
                      />
                      <p className="text-pearl-muted text-xs mt-1">Ej: Si el teléfono es 5512345678, la clave es 5678</p>
                      {downloadError && (
                        <p className="text-red-400 text-sm mt-1">{downloadError}</p>
                      )}
                    </div>
                    
                    <div className="bg-night/50 p-3 rounded-lg border border-gold/20">
                      <p className="text-pearl-muted text-sm">
                        <strong className="text-gold">Resumen:</strong><br/>
                        • Evento: {session.event_name}<br/>
                        • Fotos disponibles: {galleryPhotos.length}<br/>
                        • Carpeta: {session.cloudinary_folder || 'ADOCA/...'}
                      </p>
                    </div>

                    <Button
                      className="w-full btn-gold"
                      onClick={handleDownload}
                      disabled={isDownloading || galleryPhotos.length === 0}
                    >
                      {isDownloading ? 'Preparando...' : 'Descargar Fotos'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </main>

      {/* === INSTAGRAM-STYLE LIGHTBOX === */}
      {lightboxPhoto && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white z-50 p-2"
            onClick={closeLightbox}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
          
          {/* Navigation arrows */}
          {galleryPhotos.length > 1 && (
            <>
              <button 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-2 z-50"
                onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
              >
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
              <button 
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-2 z-50"
                onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
              >
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </>
          )}
          
          {/* Main image */}
          <div 
            className="max-w-full max-h-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxPhoto.cloudinary_url} 
              alt=""
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onDoubleClick={() => handleDoubleTapLike(lightboxPhoto)}
            />
            
            {/* Like heart animation in lightbox - CORAZÓN BLANCO GRANDE */}
            {likeAnimation === (lightboxPhoto.id || lightboxPhoto._id) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg className="w-32 h-32 text-white drop-shadow-2xl like-heart-animation" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
            )}
          </div>
          
          {/* Photo counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {lightboxIndex + 1} / {galleryPhotos.length}
          </div>
          
          {/* Like button */}
          <button 
            className="absolute bottom-4 right-4 p-3"
            onClick={(e) => { e.stopPropagation(); handleDoubleTapLike(lightboxPhoto); }}
          >
            <svg 
              className={`w-8 h-8 transition-all ${likedPhotos.includes(lightboxPhoto.id || lightboxPhoto._id) ? 'text-red-500 scale-110' : 'text-white/70 hover:text-red-400'}`} 
              fill={likedPhotos.includes(lightboxPhoto.id || lightboxPhoto._id) ? 'currentColor' : 'none'} 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
          </button>
        </div>
      )}

      {/* Footer con botón de ventas y privacidad - Solo en modo menú y galería */}
      {viewMode !== "projection" && (
        <footer className="fixed bottom-0 left-0 right-0 bg-night/90 backdrop-blur-sm border-t border-gold/20 py-3 z-40">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center gap-2">
              {/* Botón de ventas */}
              <Link to="/cotizador">
                <Button 
                  size="sm" 
                  className="btn-gold font-bold shadow-lg"
                >
                  ¡Quiero PICPARTYLIVE en mi fiesta!
                </Button>
              </Link>
              {/* Footer info */}
              <div className="flex items-center gap-3 text-xs">
                <span className="text-pearl-muted">PICPARTYLIVE • picparty.net</span>
                <button 
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-gold hover:text-gold/80 underline"
                >
                  Seguridad y Privacidad
                </button>
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* Modal de Privacidad */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-night/90 flex items-center justify-center z-50 p-4">
          <Card className="card-premium max-w-md w-full max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-gold flex items-center gap-2">
                Seguridad y Privacidad
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-pearl-muted text-sm">
              <div>
                <h4 className="text-gold font-bold mb-1">Control del Anfitrión</h4>
                <p>Solo el anfitrión decide quién puede ver y subir fotos mediante códigos de acceso únicos.</p>
              </div>
              <div>
                <h4 className="text-gold font-bold mb-1">Fotos Privadas</h4>
                <p>Las fotos solo son visibles para invitados autorizados con el código del evento.</p>
              </div>
              <div>
                <h4 className="text-gold font-bold mb-1">Sin Apps que Descargar</h4>
                <p>Acceso directo desde cualquier navegador. Sin instalaciones ni registros.</p>
              </div>
              <div>
                <h4 className="text-gold font-bold mb-1">Protección de Datos</h4>
                <p>Tus fotos se almacenan de forma segura. El anfitrión tiene control total para eliminar contenido.</p>
              </div>
              <Button 
                className="w-full btn-gold mt-4"
                onClick={() => setShowPrivacyModal(false)}
              >
                Entendido
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============ ADMIN PANEL ============
// Sistema de Roles: ADMIN (full access), VENTAS (contratos + reportes), STAFF (solo galería)
const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null); // "admin", "ventas" o "staff"
  const [userName, setUserName] = useState("");
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [events, setEvents] = useState([]);
  const [eventPinDrafts, setEventPinDrafts] = useState({});
  const [liveSessions, setLiveSessions] = useState([]);
  const [selectedEventCode, setSelectedEventCode] = useState(null);
  const [eventPhotos, setEventPhotos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [preferences, setPreferences] = useState({ show_net_price: true });
  const [loading, setLoading] = useState(true);
  const [photosCounts, setPhotosCounts] = useState({}); // Contador de fotos por evento
  // Estados para capítulos
  const [chapterModal, setChapterModal] = useState({ open: false, sessionId: null, sessionCode: null });
  const [newChapterName, setNewChapterName] = useState("");
  const [sessionChapters, setSessionChapters] = useState([]);
  const [sessionPhotosForChapters, setSessionPhotosForChapters] = useState([]);
  const [selectedPhotosForChapter, setSelectedPhotosForChapter] = useState([]);
  const [newEvent, setNewEvent] = useState({ name: "", date: "", time: "", description: "", fotoshare_url: "", video360_url: "", location: "", has_photos: true, has_video360: false, color: "", category: "otro", services: [], is_published: true });

  const EVENT_CATEGORIES = [
    { value: "boda", label: "Boda" },
    { value: "xv", label: "XV años" },
    { value: "grad", label: "Graduación" },
    { value: "corporativo", label: "Corporativo" },
    { value: "cumpleanos", label: "Cumpleaños" },
    { value: "otro", label: "Otro" },
  ];
  const EVENT_SERVICES = [
    { value: "cabina", label: "Cabina de fotos" },
    { value: "video360", label: "Video 360°" },
  ];
  const toggleNewEventService = (value) => {
    setNewEvent(prev => ({
      ...prev,
      services: prev.services.includes(value) ? prev.services.filter(s => s !== value) : [...prev.services, value]
    }));
  };
  const toggleEditEventService = (value) => {
    setEditEventForm(prev => ({
      ...prev,
      services: (prev.services || []).includes(value) ? prev.services.filter(s => s !== value) : [...(prev.services || []), value]
    }));
  };
  // Links de Fotoshare para Staff
  const [fotoshareLinks, setFotoshareLinks] = useState({ fotos: "", videos: "" });
  // Proveedores
  const [showProveedorForm, setShowProveedorForm] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [proveedorForm, setProveedorForm] = useState({
    nombre_empresa: "", contacto: "", telefono: "", servicios: [],
    costo_cabina: "", costo_video360: "", costo_key_moments: "", notas: ""
  });
  // Seguridad: personal (staff) y contraseñas
  const [staffMembers, setStaffMembers] = useState([]);
  const [newStaffForm, setNewStaffForm] = useState({ staff_code: "", name: "", pin: "", assigned_contract_ids: [] });
  const [pinResetValues, setPinResetValues] = useState({});
  const [passwordForm, setPasswordForm] = useState({ target_username: "OCTAVIO", new_password: "" });
  const [backups, setBackups] = useState([]);
  // Precios del cotizador público (editable por Admin)
  const [preciosForm, setPreciosForm] = useState(null);
  const [savingPrecios, setSavingPrecios] = useState(false);
  const fetchStaffMembers = async () => {
    try {
      const res = await axios.get(`${API}/staff/members`);
      setStaffMembers(res.data);
    } catch (e) { /* no-op */ }
  };
  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API}/admin/backups`);
      setBackups(res.data);
    } catch (e) { /* no-op */ }
  };
  const fetchPreciosCotizador = async () => {
    try {
      const res = await axios.get(`${API}/cotizador/precios`);
      setPreciosForm(res.data);
    } catch (e) { /* no-op */ }
  };
  const savePreciosCotizador = async () => {
    if (!preciosForm) return;
    for (const tier of preciosForm.cabina) {
      if (!(tier.precio > 0)) return toast.error(`El precio de ${tier.horas}h debe ser mayor a 0`);
    }
    if (!(preciosForm.cabina_llaveros.precio > 0)) return toast.error("El precio del paquete de llaveros debe ser mayor a 0");
    if (!(preciosForm.video360.precio > 0)) return toast.error("El precio de Video 360° debe ser mayor a 0");
    if (!(preciosForm.video360_nieve.precio > 0)) return toast.error("El precio de Video 360° con Nieve debe ser mayor a 0");
    setSavingPrecios(true);
    try {
      await axios.put(`${API}/cotizador/precios`, preciosForm);
      toast.success("Precios del cotizador actualizados");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Error al guardar los precios");
    } finally {
      setSavingPrecios(false);
    }
  };
  useEffect(() => {
    if (isAuthenticated && userRole === "admin") { fetchStaffMembers(); fetchBackups(); fetchPreciosCotizador(); fetchPromotions(); }
  }, [isAuthenticated, userRole]);
  // Promociones (banner editable de la portada pública)
  const emptyPromoForm = { texto: "", texto_boton: "Cotizar", color_fondo: "#e8336f", icono: "", precio: "", paquete: "", fecha_inicio: "", fecha_fin: "", activa: false };
  const [promotionsList, setPromotionsList] = useState([]);
  const [promoForm, setPromoForm] = useState(emptyPromoForm);
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [savingPromo, setSavingPromo] = useState(false);
  const fetchPromotions = async () => {
    try {
      const res = await axios.get(`${API}/promotions`);
      setPromotionsList(res.data);
    } catch (e) { /* no-op */ }
  };
  const savePromotion = async () => {
    if (!promoForm.texto.trim()) return toast.error("Falta el texto de la promoción");
    if (!promoForm.texto_boton.trim()) return toast.error("Falta el texto del botón");
    if (!promoForm.fecha_inicio || !promoForm.fecha_fin) return toast.error("Faltan las fechas de inicio y fin");
    if (promoForm.fecha_fin < promoForm.fecha_inicio) return toast.error("La fecha de fin no puede ser antes que la de inicio");
    setSavingPromo(true);
    try {
      const payload = { ...promoForm, precio: promoForm.precio !== "" ? parseFloat(promoForm.precio) : null, paquete: promoForm.paquete || null };
      if (editingPromoId) {
        await axios.put(`${API}/promotions/${editingPromoId}`, payload);
        toast.success("Promoción actualizada");
      } else {
        await axios.post(`${API}/promotions`, payload);
        toast.success("Promoción creada");
      }
      setPromoForm(emptyPromoForm);
      setEditingPromoId(null);
      fetchPromotions();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Error al guardar la promoción");
    } finally {
      setSavingPromo(false);
    }
  };
  const editPromotion = (p) => {
    setEditingPromoId(p.id);
    setPromoForm({ texto: p.texto, texto_boton: p.texto_boton, color_fondo: p.color_fondo, icono: p.icono || "", precio: p.precio ?? "", paquete: p.paquete || "", fecha_inicio: p.fecha_inicio, fecha_fin: p.fecha_fin, activa: p.activa });
  };
  const cancelEditPromotion = () => { setEditingPromoId(null); setPromoForm(emptyPromoForm); };
  const deletePromotion = async (id) => {
    try {
      await axios.delete(`${API}/promotions/${id}`);
      toast.success("Promoción eliminada");
      if (editingPromoId === id) cancelEditPromotion();
      fetchPromotions();
    } catch (e) {
      toast.error("Error al eliminar la promoción");
    }
  };
  const [newSession, setNewSession] = useState({ 
    event_name: "", 
    event_type: "boda",
    event_type_custom: "",
    event_date: "",
    client_phone: "",
    client_name: "",
    is_vip: false, 
    vip_pass: "",
    total_price: "",
    anticipo_amount: "",
    payment_status: "pendiente"
  });
  
  // Generar código único alfanumérico (difícil de adivinar/forzar, pero legible para compartir de viva voz)
  const generateUniqueCode = () => {
    const existingCodes = liveSessions.map(s => s.code);
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // sin 0/O ni 1/I para evitar confusión
    let code;
    let attempts = 0;
    do {
      code = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
      attempts++;
    } while (existingCodes.includes(code) && attempts < 100);
    return code;
  };
  
  // Tipos de evento disponibles con emojis temáticos
  const eventTypes = [
    { value: "boda", label: "💍 Boda", emoji: "💍" },
    { value: "quinceanios", label: "👑 Quinceaños", emoji: "👑" },
    { value: "cumpleanos", label: "🎂 Cumpleaños", emoji: "🎂" },
    { value: "empresarial", label: "🏢 Empresarial", emoji: "🏢" },
    { value: "publico", label: "✨ Evento Público", emoji: "✨" },
    { value: "fiesta", label: "💃 Fiesta", emoji: "💃" },
    { value: "otro", label: "📸 Otro", emoji: "📸" }
  ];
  
  // Estado de contratos: ahora gestionado por el hook useContracts() (ver abajo)

  // Estado para edición de sesiones Live
  const [editingSession, setEditingSession] = useState(null);
  const [editForm, setEditForm] = useState({
    event_name: "",
    event_type: "",
    event_type_custom: "",
    event_date: "",
    code: ""
  });
  
  // Estado para Ticket de Venta
  const [ticketVentaModal, setTicketVentaModal] = useState({ open: false, session: null });
  const [ticketVentaForm, setTicketVentaForm] = useState({
    precioTipo: "normal",  // normal, promo, combo
    anticipo: 0
  });
  
  // Estado para edición de eventos de Galería Pro
  const [editingEventId, setEditingEventId] = useState(null);
  const [editEventForm, setEditEventForm] = useState({
    name: "",
    date: "",
    time: "",
    fotoshare_url: "",
    video360_url: "",
    location: "",
    category: "otro",
    services: [],
    is_published: true
  });

  const [checkingSession, setCheckingSession] = useState(true);

  // Verifica la sesión real contra el servidor (cookie httpOnly) al cargar el panel
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then((res) => {
        setIsAuthenticated(true);
        setUserRole(res.data.role);
        setUserName(res.data.name || res.data.username);
      })
      .catch(() => { setIsAuthenticated(false); })
      .finally(() => setCheckingSession(false));
  }, []);

  // Si el servidor responde 401 en cualquier momento (sesión expirada), regresar al login
  useEffect(() => {
    const handleExpired = () => {
      setIsAuthenticated((prevAuth) => {
        if (prevAuth) toast.error("Sesión expirada, inicia sesión de nuevo");
        return false;
      });
      setUserRole(null);
      setUserName("");
    };
    window.addEventListener("picparty:session-expired", handleExpired);
    return () => window.removeEventListener("picparty:session-expired", handleExpired);
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated]);

  // Mantiene viva la sesión de ADMIN/VENTAS mientras hay actividad
  // (cierre automático a los 30 min de inactividad, aviso 1 min antes)
  useEffect(() => {
    if (!isAuthenticated || userRole === "staff") return;
    let lastRefresh = Date.now();
    let warned = false;
    const activity = () => {
      warned = false;
      const now = Date.now();
      if (now - lastRefresh > 5 * 60 * 1000) {
        lastRefresh = now;
        axios.post(`${API}/auth/refresh`).catch(() => {});
      }
    };
    const evts = ["mousemove", "keydown", "click", "scroll"];
    evts.forEach((ev) => window.addEventListener(ev, activity));
    const warnCheck = setInterval(() => {
      if (Date.now() - lastRefresh > 29 * 60 * 1000 && !warned) {
        warned = true;
        toast.warning("⏰ Tu sesión se cerrará en 1 minuto por inactividad");
      }
    }, 15000);
    return () => {
      evts.forEach((ev) => window.removeEventListener(ev, activity));
      clearInterval(warnCheck);
    };
  }, [isAuthenticated, userRole]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, {
        username: loginUser.toUpperCase(),
        password: loginPass,
      });
      setIsAuthenticated(true);
      setUserRole(res.data.role);
      setUserName(res.data.name || res.data.username);
      setLoginPass("");
      const roleEmoji = res.data.role === "admin" ? "🔐" : res.data.role === "ventas" ? "💼" : "👤";
      toast.success(`${roleEmoji} Acceso ${res.data.role.toUpperCase()} autorizado - Bienvenido ${res.data.name || res.data.username}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales incorrectas");
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* no-op */ }
    setIsAuthenticated(false);
    setUserRole(null);
    setUserName("");
  };

  // ============ EXPORTAR EXCEL ============
  const exportToExcel = () => {
    toast.info("Generando Excel...");
    
    // Hoja 1: Contratos
    const contractsData = contracts.map(c => {
      const servicios = [];
      if (c.cabina_hours) servicios.push(`Cabina ${c.cabina_hours}h`);
      if (c.video360_hours) servicios.push(`Video360 ${c.video360_hours}h`);
      if (c.key_moments) servicios.push(`Key Moments ${c.key_moments}`);
      if (c.include_live) servicios.push("PicPartyLive");
      
      const total = c.net_price || 0;
      const anticipo = c.anticipo_amount || 0;
      const saldo = total - anticipo;
      
      return {
        "Folio": c.folio || "",
        "Cliente": c.client_name || "",
        "Teléfono": c.client_phone || "",
        "Salón": c.salon || "",
        "Fecha Evento": c.event_date || "",
        "Servicios": servicios.join(", ") || "Sin servicios",
        "Total": total,
        "Anticipo": anticipo,
        "Saldo": saldo,
        "Estado": Math.abs(saldo) < 0.01 ? "LIQUIDADO" : anticipo > 0 ? "APARTADO" : "PENDIENTE"
      };
    });
    
    // Hoja 2: PicPartyLive
    const liveData = liveSessions.map(s => {
      const diasRestantes = s.vigencia_fin ? getDiasRestantes(s.vigencia_fin) : null;
      let estadoVigencia = "Sin activar";
      if (s.vigencia_activada) {
        estadoVigencia = s.vigencia_expirada ? "EXPIRADA" : `${diasRestantes} días`;
      }
      
      return {
        "Código": s.code || "",
        "Nombre Evento": s.event_name || "",
        "Fecha": s.event_date || "",
        "Tipo": s.event_type || "",
        "Fotos": photosCounts[s.code] || 0,
        "Vigencia": estadoVigencia,
        "Estado": s.is_active ? "ACTIVO" : "INACTIVO"
      };
    });
    
    // Hoja 3: Ingresos por mes
    const ingresosPorMes = {};
    
    // Procesar contratos
    contracts.forEach(c => {
      const fecha = c.event_date || c.created_at?.substring(0, 10) || "";
      const mes = fecha.substring(0, 7); // YYYY-MM
      if (!mes) return;
      
      if (!ingresosPorMes[mes]) {
        ingresosPorMes[mes] = {
          mes: mes,
          totalContratos: 0,
          totalPicPartyLive: 0,
          anticipoRecibido: 0,
          saldoPendiente: 0
        };
      }
      
      const total = c.net_price || 0;
      const anticipo = c.anticipo_amount || 0;
      
      ingresosPorMes[mes].totalContratos += total;
      ingresosPorMes[mes].anticipoRecibido += anticipo;
      ingresosPorMes[mes].saldoPendiente += (total - anticipo);
    });
    
    // Procesar PicPartyLive
    liveSessions.forEach(s => {
      const fecha = s.event_date || "";
      const mes = fecha.substring(0, 7);
      if (!mes) return;
      
      if (!ingresosPorMes[mes]) {
        ingresosPorMes[mes] = {
          mes: mes,
          totalContratos: 0,
          totalPicPartyLive: 0,
          anticipoRecibido: 0,
          saldoPendiente: 0
        };
      }
      
      const precio = s.total_price || 0;
      const anticipo = s.anticipo_amount || 0;
      
      ingresosPorMes[mes].totalPicPartyLive += precio;
      ingresosPorMes[mes].anticipoRecibido += anticipo;
      ingresosPorMes[mes].saldoPendiente += (precio - anticipo);
    });
    
    const ingresosData = Object.values(ingresosPorMes)
      .sort((a, b) => a.mes.localeCompare(b.mes))
      .map(m => ({
        "Mes": m.mes,
        "Total Contratos": m.totalContratos,
        "Total PicPartyLive": m.totalPicPartyLive,
        "Anticipo Recibido": m.anticipoRecibido,
        "Saldo Pendiente": m.saldoPendiente,
        "Total General": m.totalContratos + m.totalPicPartyLive
      }));
    
    // Agregar fila de totales
    if (ingresosData.length > 0) {
      ingresosData.push({
        "Mes": "TOTAL",
        "Total Contratos": ingresosData.reduce((sum, m) => sum + m["Total Contratos"], 0),
        "Total PicPartyLive": ingresosData.reduce((sum, m) => sum + m["Total PicPartyLive"], 0),
        "Anticipo Recibido": ingresosData.reduce((sum, m) => sum + m["Anticipo Recibido"], 0),
        "Saldo Pendiente": ingresosData.reduce((sum, m) => sum + m["Saldo Pendiente"], 0),
        "Total General": ingresosData.reduce((sum, m) => sum + m["Total General"], 0)
      });
    }
    
    // Crear workbook
    const wb = XLSX.utils.book_new();
    
    // Agregar hojas
    const ws1 = XLSX.utils.json_to_sheet(contractsData);
    const ws2 = XLSX.utils.json_to_sheet(liveData);
    const ws3 = XLSX.utils.json_to_sheet(ingresosData);
    
    // Ajustar anchos de columna
    ws1['!cols'] = [
      {wch: 15}, {wch: 25}, {wch: 15}, {wch: 25}, {wch: 12}, {wch: 35}, {wch: 12}, {wch: 12}, {wch: 12}, {wch: 12}
    ];
    ws2['!cols'] = [
      {wch: 12}, {wch: 30}, {wch: 12}, {wch: 15}, {wch: 8}, {wch: 15}, {wch: 12}
    ];
    ws3['!cols'] = [
      {wch: 12}, {wch: 18}, {wch: 18}, {wch: 18}, {wch: 18}, {wch: 18}
    ];
    
    XLSX.utils.book_append_sheet(wb, ws1, "Contratos");
    XLSX.utils.book_append_sheet(wb, ws2, "PicPartyLive");
    XLSX.utils.book_append_sheet(wb, ws3, "Ingresos");
    
    // Descargar archivo
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `PicParty_Reporte_${fecha}.xlsx`);
    
    toast.success("Excel exportado correctamente");
  };

  const fetchData = async () => {
    try {
      const [eventsRes, sessionsRes, prefsRes, contractsRes, proveedoresRes] = await Promise.all([
        axios.get(`${API}/events/admin`),
        axios.get(`${API}/live/sessions/all`).catch(() => ({ data: [] })),
        axios.get(`${API}/preferences`),
        axios.get(`${API}/contracts`).catch(() => ({ data: [] })),
        axios.get(`${API}/proveedores`).catch(() => ({ data: [] }))
      ]);
      setEvents(eventsRes.data);
      setLiveSessions(sessionsRes.data);
      setPreferences(prefsRes.data);
      setContracts(contractsRes.data);
      setProveedores(proveedoresRes.data);
      
      // Obtener conteo de fotos para cada sesión
      fetchPhotosCounts(sessionsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Estado y lógica de Contratos (extraído a src/modules/Contracts/useContracts.js)
  const {
    contracts, setContracts,
    contractsSearch, setContractsSearch,
    showContractForm, setShowContractForm,
    contractForm, setContractForm,
    editingContractId, contractPreview,
    createContract, updateContract, deleteContract,
    startEditContract, cancelEdit,
    printContractPDF, printProveedorPDF, generateRecibo,
    syncFolios,
    addAbono, editAbono, removeAbono, toggleContractCancelled, generateAbonoRecibo,
  } = useContracts(fetchData);

  // Estado y lógica de Cotizaciones + Textos del PDF (src/modules/Quotes/useQuotes.js)
  // Solo admin/ventas pueden ver Cotizaciones (el backend devuelve 403 a STAFF).
  const quotesHook = useQuotes(preciosForm, isAuthenticated && userRole !== "staff", fetchData);
  // Sección "Servicios" de la portada (solo Admin la edita; backend requiere Admin).
  const servicesSectionHook = useServicesSection(isAuthenticated && userRole === "admin");
  
  // Obtener conteo de fotos por evento (para actualización en tiempo real)
  const fetchPhotosCounts = async (sessions) => {
    if (!sessions || sessions.length === 0) return;
    
    const counts = {};
    try {
      await Promise.all(
        sessions.map(async (session) => {
          try {
            const res = await axios.get(`${API}/live/photos/${session.code}`);
            // El API devuelve {event_code, photos: [], total}
            const photosArray = res.data?.photos || res.data || [];
            const count = Array.isArray(photosArray) ? photosArray.length : (res.data?.total || 0);
            counts[session.code] = count;
          } catch (err) {
            counts[session.code] = 0;
          }
        })
      );
      setPhotosCounts(counts);
    } catch (e) {
      console.error("Error fetching photos counts:", e);
    }
  };
  
  // Actualizar conteo de fotos cada 10 segundos (real-time)
  useEffect(() => {
    if (isAuthenticated && liveSessions.length > 0) {
      // Cargar inmediatamente
      fetchPhotosCounts(liveSessions);
      
      // Luego actualizar cada 10 segundos
      const interval = setInterval(() => {
        fetchPhotosCounts(liveSessions);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, liveSessions.length]); // Depender de length para re-ejecutar cuando cambian las sesiones

  const createEvent = async () => {
    if (!newEvent.name || !newEvent.date) { toast.error("Nombre y fecha requeridos"); return; }
    try {
      await axios.post(`${API}/events`, newEvent);
      toast.success("Evento de galería creado (solo visualización)");
      setNewEvent({ name: "", date: "", time: "", description: "", fotoshare_url: "", video360_url: "", location: "", has_photos: true, has_video360: false, color: "", category: "otro", services: [], is_published: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteEvent = async (id) => {
    if (!confirm("¿Eliminar?")) return;
    await axios.delete(`${API}/events/${id}`);
    toast.success("Eliminado");
    fetchData();
  };

  // Activa/desactiva privacidad (PIN) de un evento sin tocar el resto de sus datos.
  const toggleEventPrivacy = async (event, makePrivate) => {
    const pin = eventPinDrafts[event.id];
    if (makePrivate && (!pin || pin.length !== 4)) {
      toast.error("Escribe un PIN de 4 dígitos para activar o actualizar la privacidad");
      return;
    }
    try {
      await axios.put(`${API}/events/${event.id}/privacy`, { is_private: makePrivate, pin: makePrivate ? pin : null });
      toast.success(makePrivate ? (event.is_private ? "PIN actualizado" : "Evento marcado como privado") : "Evento vuelto a público");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al actualizar privacidad"); }
  };

  const copyPrivateLink = (event) => {
    const pin = eventPinDrafts[event.id];
    const link = `${window.location.origin}/galeria/${event.id}`;
    if (!pin) {
      navigator.clipboard.writeText(link);
      toast.warning("PIN no disponible en esta sesión (solo se ve una vez al definirlo); se copió solo el link. Escribe un PIN nuevo y guárdalo si necesitas compartirlo.");
      return;
    }
    navigator.clipboard.writeText(`${link}\nPIN: ${pin}`);
    toast.success("Link copiado al portapapeles");
  };

  // Migración única: marca servicios (cabina/video360) de eventos antiguos según su link
  // ya guardado, y fija "Publicado" explícito en los que no lo tenían. Segura de repetir.
  const migrateEventsSchema = async () => {
    try {
      const res = await axios.post(`${API}/migrate-events-schema`);
      toast.success(`${res.data.message} (${res.data.updated} de ${res.data.total_events} eventos actualizados)`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al migrar eventos");
    }
  };

  // Guardar edición de evento de Galería Pro
  const saveEventEdit = async (eventId) => {
    if (!editEventForm.name || !editEventForm.date) {
      toast.error("Nombre y fecha son requeridos");
      return;
    }
    try {
      await axios.put(`${API}/events/${eventId}`, {
        name: editEventForm.name,
        date: editEventForm.date,
        time: editEventForm.time || "",
        fotoshare_url: editEventForm.fotoshare_url || "",
        video360_url: editEventForm.video360_url || "",
        location: editEventForm.location || "",
        category: editEventForm.category || "otro",
        services: editEventForm.services || [],
        is_published: editEventForm.is_published !== false
      });
      toast.success("Evento actualizado correctamente");
      setEditingEventId(null);
      setEditEventForm({ name: "", date: "", time: "", fotoshare_url: "", video360_url: "", location: "", category: "otro", services: [], is_published: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar evento");
    }
  };

  const createLiveSession = async () => {
    if (!newSession.event_name || !newSession.event_date) { 
      toast.error("Nombre y fecha son requeridos"); 
      return; 
    }
    
    // Validar teléfono (obligatorio)
    if (!newSession.client_phone || newSession.client_phone.length < 4) {
      toast.error("El teléfono del cliente es obligatorio (mínimo 4 dígitos para clave)");
      return;
    }
    
    // Generar código automático de 4 dígitos
    const autoCode = generateUniqueCode();
    
    try {
      const params = new URLSearchParams({
        code: autoCode,
        event_name: newSession.event_name,
        event_type: newSession.event_type,
        event_date: newSession.event_date,
        client_phone: newSession.client_phone,
        is_vip: newSession.is_vip
      });
      if (newSession.event_type === "otro" && newSession.event_type_custom) {
        params.append("event_type_custom", newSession.event_type_custom);
      }
      if (newSession.client_name) {
        params.append("client_name", newSession.client_name);
      }
      if (newSession.total_price) {
        params.append("total_price", newSession.total_price);
      }
      if (newSession.anticipo_amount) {
        params.append("anticipo_amount", newSession.anticipo_amount);
      }
      
      await axios.post(`${API}/live/sessions/create?${params.toString()}`);
      toast.success(`✅ Sesión creada - Código: ${autoCode}`);
      setNewSession({ 
        event_name: "", event_type: "boda", 
        event_type_custom: "", event_date: "", client_phone: "", client_name: "",
        is_vip: false, vip_pass: "", total_price: "", anticipo_amount: "", payment_status: "pendiente"
      });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear sesión"); }
  };

  // ============ EDICIÓN DE SESIONES LIVE ============
  const openEditModal = (session) => {
    setEditForm({
      event_name: session.event_name || "",
      event_type: session.event_type || "boda",
      event_type_custom: session.event_type_custom || "",
      event_date: session.event_date || "",
      code: session.code || "",
      client_name: session.client_name || "",
      client_phone: session.client_phone || "",
      total_price: session.total_price || "",
      anticipo_amount: session.anticipo_amount || "",
      payment_status: session.payment_status || "pendiente",
      cloudinary_folder: session.cloudinary_folder || ""
    });
    setEditingSession(session);
  };

  const closeEditModal = () => {
    setEditingSession(null);
    setEditForm({ event_name: "", event_type: "", event_type_custom: "", event_date: "", code: "", 
      client_name: "", client_phone: "", total_price: "", anticipo_amount: "", payment_status: "pendiente",
      cloudinary_folder: "" });
  };

  const saveSessionEdit = async () => {
    if (!editingSession) return;
    
    try {
      const params = new URLSearchParams();
      if (editForm.event_name) params.append("event_name", editForm.event_name);
      if (editForm.event_type) params.append("event_type", editForm.event_type);
      if (editForm.event_type === "otro" && editForm.event_type_custom) {
        params.append("event_type_custom", editForm.event_type_custom);
      }
      if (editForm.event_date) params.append("event_date", editForm.event_date);
      if (editForm.code && editForm.code !== editingSession.code) {
        params.append("code", editForm.code);
      }
      if (editForm.client_name) params.append("client_name", editForm.client_name);
      if (editForm.client_phone) params.append("client_phone", editForm.client_phone);
      if (editForm.total_price !== "" && editForm.total_price !== null) {
        params.append("total_price", editForm.total_price);
      }
      if (editForm.anticipo_amount !== "" && editForm.anticipo_amount !== null) {
        params.append("anticipo_amount", editForm.anticipo_amount);
      }
      if (editForm.payment_status) params.append("payment_status", editForm.payment_status);
      if (editForm.cloudinary_folder) params.append("cloudinary_folder", editForm.cloudinary_folder);
      
      await axios.put(`${API}/live/sessions/${editingSession.id}?${params.toString()}`);
      toast.success("✅ Evento actualizado correctamente");
      closeEditModal();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    }
  };

  // Función para obtener el emoji del tipo de evento
  const getEventTypeInfo = (type, custom) => {
    const found = eventTypes.find(t => t.value === type);
    if (type === "otro" && custom) {
      return { emoji: "✨", label: custom };
    }
    return found || { emoji: "📸", label: "Evento" };
  };

  // Estado para subida de fotos
  const [uploadingSession, setUploadingSession] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = React.useRef(null);

  // Función para subir fotos a Cloudinary (todas las seleccionadas)
  const handleUploadPhotos = async (session, files) => {
    if (!files || files.length === 0) return;
    
    if (!session.cloudinary_folder) {
      toast.error("Esta sesión no tiene carpeta de Cloudinary configurada. Edita la sesión primero.");
      return;
    }

    setUploadingSession(session.code);
    setUploadProgress({ current: 0, total: files.length });
    
    const BATCH_SIZE = 10; // Subir de 10 en 10
    let uploadedCount = 0;
    let failedCount = 0;
    const filesArray = Array.from(files);
    
    toast.info(`Iniciando subida de ${filesArray.length} fotos...`, { duration: 3000 });

    // Procesar TODOS los batches
    for (let i = 0; i < filesArray.length; i += BATCH_SIZE) {
      const batchStart = i;
      const batchEnd = Math.min(i + BATCH_SIZE, filesArray.length);
      const batch = filesArray.slice(batchStart, batchEnd);
      
      const formData = new FormData();
      batch.forEach(file => {
        formData.append('files', file);
      });

      try {
        const response = await axios.post(
          `${API}/live/upload-photos/${session.code}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000 // 3 minutos por batch
          }
        );
        
        uploadedCount += response.data.uploaded || 0;
        failedCount += response.data.failed || 0;
        
      } catch (e) {
        console.error(`Error en batch ${batchStart}-${batchEnd}:`, e);
        failedCount += batch.length;
      }
      
      // Actualizar progreso
      setUploadProgress({ current: batchEnd, total: filesArray.length });
    }

    setUploadingSession(null);
    setUploadProgress({ current: 0, total: 0 });
    
    if (uploadedCount > 0) {
      toast.success(`✅ ${uploadedCount} fotos subidas${failedCount > 0 ? `, ${failedCount} fallidas` : ''}`, { duration: 5000 });
      // Actualizar contador de fotos inmediatamente
      fetchPhotosCounts([session]);
      fetchData();
    } else {
      toast.error(`Error: No se pudo subir ninguna foto. ${failedCount} fallidas.`);
    }
  };

  // Trigger file input para sesión específica
  const triggerUploadPhotos = (session) => {
    if (fileInputRef.current) {
      fileInputRef.current.dataset.sessionCode = session.code;
      fileInputRef.current.dataset.sessionFolder = session.cloudinary_folder || '';
      fileInputRef.current.click();
    }
  };

  // Handler cuando se seleccionan archivos
  const handleFileSelect = (e) => {
    const files = e.target.files;
    const sessionCode = e.target.dataset.sessionCode;
    const session = liveSessions.find(s => s.code === sessionCode);
    
    if (session && files.length > 0) {
      handleUploadPhotos(session, files);
    }
    
    // Limpiar input para permitir subir los mismos archivos de nuevo
    e.target.value = '';
  };

  // ============ VIGENCIA DE SESIONES (6 MESES) ============
  const activarVigencia = async (session) => {
    try {
      const res = await axios.post(`${API}/live/sessions/${session.id}/activate-vigencia`);
      toast.success(`✅ Vigencia activada: ${res.data.message}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error activando vigencia");
    }
  };

  // Calcular días restantes de vigencia
  const getDiasRestantes = (vigenciaFin) => {
    if (!vigenciaFin) return null;
    const fin = new Date(vigenciaFin);
    const hoy = new Date();
    const diff = Math.ceil((fin - hoy) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Formatear fecha para mostrar
  const formatFechaVigencia = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ============ CAPÍTULOS/CARPETAS ============
  const openChapterModal = async (session) => {
    setChapterModal({ open: true, sessionId: session.id, sessionCode: session.code });
    setNewChapterName("");
    setSelectedPhotosForChapter([]);
    
    // Cargar capítulos y fotos
    try {
      const [chaptersRes, photosRes] = await Promise.all([
        axios.get(`${API}/live/sessions/${session.id}/chapters`),
        axios.get(`${API}/live/photos/${session.code}`)
      ]);
      setSessionChapters(chaptersRes.data.chapters || []);
      setSessionPhotosForChapters(photosRes.data.photos || []);
    } catch (e) {
      console.error("Error cargando capítulos:", e);
      setSessionChapters([]);
      setSessionPhotosForChapters([]);
    }
  };

  const closeChapterModal = () => {
    setChapterModal({ open: false, sessionId: null, sessionCode: null });
    setNewChapterName("");
    setSessionChapters([]);
    setSessionPhotosForChapters([]);
    setSelectedPhotosForChapter([]);
  };

  const createChapter = async () => {
    if (!newChapterName.trim()) {
      toast.error("El nombre del capítulo es obligatorio");
      return;
    }
    try {
      const order = sessionChapters.length;
      const res = await axios.post(
        `${API}/live/sessions/${chapterModal.sessionId}/chapters?name=${encodeURIComponent(newChapterName)}&order=${order}`
      );
      toast.success("Capítulo creado");
      setSessionChapters([...sessionChapters, res.data.chapter]);
      setNewChapterName("");
    } catch (e) {
      toast.error("Error creando capítulo");
    }
  };

  const deleteChapter = async (chapterId) => {
    if (!confirm("¿Eliminar este capítulo? Las fotos quedarán sin capítulo asignado.")) return;
    try {
      await axios.delete(`${API}/live/sessions/${chapterModal.sessionId}/chapters/${chapterId}`);
      setSessionChapters(sessionChapters.filter(ch => ch.id !== chapterId));
      // Actualizar fotos para quitar el chapter_id
      setSessionPhotosForChapters(sessionPhotosForChapters.map(p => 
        p.chapter_id === chapterId ? {...p, chapter_id: null} : p
      ));
      toast.success("Capítulo eliminado");
    } catch (e) {
      toast.error("Error eliminando capítulo");
    }
  };

  const assignPhotosToChapter = async (chapterId) => {
    if (selectedPhotosForChapter.length === 0) {
      toast.error("Selecciona al menos una foto");
      return;
    }
    try {
      await axios.post(`${API}/live/photos/bulk-assign-chapter`, {
        photo_ids: selectedPhotosForChapter,
        chapter_id: chapterId
      });
      // Actualizar estado local
      setSessionPhotosForChapters(sessionPhotosForChapters.map(p => 
        selectedPhotosForChapter.includes(p.id) ? {...p, chapter_id: chapterId} : p
      ));
      setSelectedPhotosForChapter([]);
      toast.success(`${selectedPhotosForChapter.length} fotos asignadas al capítulo`);
    } catch (e) {
      toast.error("Error asignando fotos");
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotosForChapter(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  // Función para descargar el QR como JPG (para WhatsApp)
  const downloadQRasJPG = async (session) => {
    toast.info("Generando imagen JPG para WhatsApp...");
    
    try {
      const qrUrl = `https://${SITE_DOMAIN}/live?code=${session.code}`;
      
      // Crear canvas para la imagen
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 1080;  // Tamaño óptimo para WhatsApp
      canvas.height = 1350;
      
      // Fondo blanco
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Logo PicParty (texto como fallback)
      ctx.fillStyle = '#d4af37';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('PIC PARTY', canvas.width / 2, 80);
      
      // Subtítulo
      ctx.fillStyle = '#666666';
      ctx.font = '24px Arial';
      ctx.fillText('Cabina Fotográfica', canvas.width / 2, 115);
      
      // Nombre del evento
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 42px Arial';
      ctx.fillText(session.event_name.toUpperCase(), canvas.width / 2, 200);
      
      // Fecha del evento
      if (session.event_date) {
        const formattedDate = new Date(session.event_date + 'T12:00:00').toLocaleDateString('es-MX', { 
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
        });
        ctx.fillStyle = '#555555';
        ctx.font = '28px Arial';
        ctx.fillText(formattedDate, canvas.width / 2, 250);
      }
      
      // Generar QR
      const QRCode = (await import('qrcode')).default;
      const qrCanvas = document.createElement('canvas');
      await QRCode.toCanvas(qrCanvas, qrUrl, { 
        width: 500,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
        errorCorrectionLevel: 'H'
      });
      
      // Dibujar QR centrado
      ctx.drawImage(qrCanvas, (canvas.width - 500) / 2, 300, 500, 500);
      
      // Código
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 32px Arial';
      ctx.fillText(`Código: ${session.code}`, canvas.width / 2, 860);
      
      // Instrucciones
      ctx.fillStyle = '#666666';
      ctx.font = '24px Arial';
      ctx.fillText('Escanea el código QR con tu celular', canvas.width / 2, 930);
      ctx.fillText('para ver y compartir fotos del evento', canvas.width / 2, 960);
      
      // Notas legales
      ctx.fillStyle = '#888888';
      ctx.font = '18px Arial';
      ctx.fillText('Las fotos se almacenan por 6 meses', canvas.width / 2, 1030);
      ctx.fillText('La proyección en pantallas no es responsabilidad de PicParty', canvas.width / 2, 1055);
      
      // Contacto (sin RFC)
      ctx.fillStyle = '#555555';
      ctx.font = '20px Arial';
      ctx.fillText('Tel: 614 272 5008 | octavio@picparty.net', canvas.width / 2, 1120);
      
      // Footer
      ctx.fillStyle = '#999999';
      ctx.font = '18px Arial';
      ctx.fillText('picparty.net | PicParty - Cabina Fotográfica', canvas.width / 2, 1180);
      
      // Convertir a JPG y descargar
      const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const fileName = `QR_PicParty_${session.event_name.replace(/\s+/g, '_')}_WhatsApp.jpg`;
      
      const downloadLink = document.createElement('a');
      downloadLink.href = jpgDataUrl;
      downloadLink.download = fileName;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
      }, 1000);
      
      toast.success("¡Imagen JPG generada para WhatsApp!");
      
    } catch (error) {
      console.error("Error generando JPG:", error);
      toast.error("Error al generar imagen. Intenta de nuevo.");
    }
  };

  const printQRPDF = async (session) => {
    toast.info("Generando PDF optimizado para impresión...");
    
    try {
      const qrUrl = `https://${SITE_DOMAIN}/live?code=${session.code}`;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // === FONDO BLANCO PARA IMPRESIÓN B&W ===
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // === LOGO PICPARTY - Esquina superior izquierda fija ===
      try {
        pdf.addImage(PICPARTY_LOGO_BASE64, 'PNG', 15, 10, 40, 40);
      } catch(e) {
        // Fallback: texto si falla el logo
        pdf.setFontSize(20);
        pdf.setTextColor(212, 175, 55);
        pdf.setFont(undefined, 'bold');
        pdf.text("PIC PARTY", 15, 35);
      }
      
      // === DATOS DE CONTACTO - Esquina superior derecha (sin RFC) ===
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont(undefined, 'normal');
      pdf.text("Adán Octavio Irigoyen Arias", pageWidth - 15, 15, { align: 'right' });
      pdf.text("Tel: 614 272 5008", pageWidth - 15, 20, { align: 'right' });
      pdf.text("octavio@picparty.net", pageWidth - 15, 25, { align: 'right' });
      
      // === ENCABEZADO - Línea decorativa gris claro ===
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(15, 55, pageWidth - 15, 55);
      
      // Tipo de evento (sin emojis, texto gris oscuro)
      const typeInfo = getEventTypeInfo(session.event_type, session.event_type_custom);
      const cleanLabel = typeInfo.label.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]/gu, '').trim();
      pdf.setFontSize(14);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont(undefined, 'normal');
      pdf.text(cleanLabel.toUpperCase(), pageWidth / 2, 65, { align: 'center' });
      
      // === NOMBRE DEL EVENTO - Negro, prominente ===
      pdf.setFontSize(26);
      pdf.setTextColor(20, 20, 20);
      pdf.setFont(undefined, 'bold');
      pdf.text(session.event_name.toUpperCase(), pageWidth / 2, 80, { align: 'center' });
      
      // === QR EN ALTA RESOLUCIÓN - B&W NÍTIDO ===
      const QRCode = (await import('qrcode')).default;
      const qrCanvas = document.createElement('canvas');
      qrCanvas.width = 1200;
      qrCanvas.height = 1200;
      
      await QRCode.toCanvas(qrCanvas, qrUrl, { 
        width: 1200,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
        errorCorrectionLevel: 'H'
      });
      
      const qrSize = 90;
      const qrDataUrl = qrCanvas.toDataURL('image/png');
      pdf.addImage(qrDataUrl, 'PNG', (pageWidth - qrSize) / 2, 88, qrSize, qrSize);
      
      // === CÓDIGO DEL EVENTO ===
      pdf.setFontSize(16);
      pdf.setTextColor(50, 50, 50);
      pdf.setFont(undefined, 'bold');
      pdf.text(`Código: ${session.code}`, pageWidth / 2, 188, { align: 'center' });
      
      // Fecha del evento
      if (session.event_date) {
        pdf.setFontSize(12);
        pdf.setTextColor(80, 80, 80);
        pdf.setFont(undefined, 'normal');
        const formattedDate = new Date(session.event_date + 'T12:00:00').toLocaleDateString('es-MX', { 
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
        });
        pdf.text(formattedDate, pageWidth / 2, 198, { align: 'center' });
      }
      
      // === INSTRUCCIONES - Recuadro gris claro ===
      pdf.setFillColor(245, 245, 245);
      pdf.roundedRect(20, 208, pageWidth - 40, 22, 3, 3, 'F');
      pdf.setFontSize(11);
      pdf.setTextColor(60, 60, 60);
      pdf.text("Escanea el código QR con tu celular", pageWidth / 2, 217, { align: 'center' });
      pdf.text("para ver y compartir fotos del evento", pageWidth / 2, 224, { align: 'center' });
      
      // === NOTAS LEGALES ===
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.setFont(undefined, 'italic');
      pdf.text("* Las fotos y videos se almacenan por un período de 6 meses.", pageWidth / 2, 242, { align: 'center' });
      pdf.text("* La proyección en pantallas del evento no es responsabilidad de PicParty.", pageWidth / 2, 249, { align: 'center' });
      
      // Footer
      pdf.setFontSize(9);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont(undefined, 'normal');
      pdf.text("picparty.net | PicParty - Cabina Fotográfica | Chihuahua, Chih.", pageWidth / 2, 265, { align: 'center' });
      
      // === DESCARGA FORZADA CON BLOB URL ===
      const fileName = `QR_PicParty_${session.event_name.replace(/\s+/g, '_')}_${session.event_date || 'evento'}.pdf`;
      
      // Generar Blob del PDF
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      
      // Crear enlace de descarga forzada
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = fileName;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      
      // Forzar click para iniciar descarga
      downloadLink.click();
      
      // Limpiar después de un momento
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
      
      // Mostrar toast con enlace de respaldo
      toast.success(
        <div>
          <p>¡PDF generado!</p>
          <a 
            href={blobUrl} 
            download={fileName}
            style={{ color: '#60a5fa', textDecoration: 'underline', display: 'block', marginTop: '8px' }}
            onClick={(e) => e.stopPropagation()}
          >
            Haz clic aquí si no inició la descarga
          </a>
        </div>,
        { duration: 10000 }
      );
      
    } catch (error) {
      console.error("Error generando PDF:", error);
      toast.error("Error al generar PDF. Intenta de nuevo.");
    }
  };
  
  // Función para generar Ticket de Pago
  const generatePaymentTicket = async (session) => {
    toast.info("Generando Ticket de Pago...");
    
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Fondo blanco
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Logo PicParty
      try {
        pdf.addImage(PICPARTY_LOGO_BASE64, 'PNG', 15, 10, 40, 40);
      } catch(e) {
        pdf.setFontSize(20);
        pdf.setTextColor(212, 175, 55);
        pdf.setFont(undefined, 'bold');
        pdf.text("PIC PARTY", 15, 35);
      }
      
      // Título del documento
      pdf.setFontSize(22);
      pdf.setTextColor(26, 11, 46);
      pdf.setFont(undefined, 'bold');
      pdf.text("TICKET DE PAGO", pageWidth / 2, 25, { align: 'center' });
      
      // Subtítulo
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont(undefined, 'normal');
      pdf.text("PicPartyLive - Muro de Fotos en Vivo", pageWidth / 2, 33, { align: 'center' });
      
      // Línea separadora
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(15, 55, pageWidth - 15, 55);
      
      let y = 70;
      
      // === DATOS DEL PROVEEDOR ===
      pdf.setFillColor(245, 245, 245);
      pdf.rect(15, y - 5, pageWidth - 30, 28, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, 'bold');
      pdf.text("DATOS DEL PROVEEDOR:", 20, y);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      y += 6;
      pdf.text("Adán Octavio Irigoyen Arias", 20, y);
      y += 5;
      pdf.text("RFC: IIAA8004021A9", 20, y);
      y += 5;
      pdf.text("Tel: 614 272 5008 | octavio@picparty.net", 20, y);
      
      y += 18;
      
      // === DATOS DEL CLIENTE ===
      pdf.setFillColor(250, 250, 250);
      pdf.rect(15, y - 5, pageWidth - 30, 22, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, 'bold');
      pdf.text("DATOS DEL CLIENTE:", 20, y);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      y += 6;
      pdf.text(`Nombre: ${session.client_name || session.event_name || 'No especificado'}`, 20, y);
      y += 5;
      pdf.text(`Teléfono: ${session.client_phone || 'No especificado'}`, 20, y);
      
      y += 18;
      
      // === DATOS DEL EVENTO ===
      pdf.setFillColor(245, 245, 245);
      pdf.rect(15, y - 5, pageWidth - 30, 28, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, 'bold');
      pdf.text("SERVICIO CONTRATADO:", 20, y);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      y += 6;
      pdf.text(`Evento: ${session.event_name}`, 20, y);
      y += 5;
      
      // Formatear fecha
      let formattedDate = session.event_date || 'No especificada';
      try {
        formattedDate = new Date(session.event_date + 'T12:00:00').toLocaleDateString('es-MX', { 
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
        });
      } catch(e) {}
      pdf.text(`Fecha: ${formattedDate}`, 20, y);
      y += 5;
      pdf.text(`Código de acceso: ${session.code}`, 20, y);
      
      y += 20;
      
      // === DESGLOSE DE PAGO ===
      pdf.setFontSize(10);
      pdf.setTextColor(26, 11, 46);
      pdf.setFont(undefined, 'bold');
      pdf.text("DESGLOSE DE PAGO", 20, y);
      
      y += 8;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(20, y, pageWidth - 20, y);
      y += 8;
      
      const totalPrice = session.total_price || 0;
      const anticipoAmount = session.anticipo_amount || 0;
      const saldoPendiente = totalPrice - anticipoAmount;
      
      // Monto Total
      pdf.setFontSize(11);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont(undefined, 'normal');
      pdf.text("Servicio PicPartyLive:", 25, y);
      pdf.setFont(undefined, 'bold');
      pdf.text(`$${totalPrice.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, pageWidth - 25, y, { align: 'right' });
      
      y += 8;
      pdf.setFont(undefined, 'normal');
      pdf.text("Anticipo recibido:", 25, y);
      pdf.setTextColor(34, 139, 34);
      pdf.setFont(undefined, 'bold');
      pdf.text(`-$${anticipoAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, pageWidth - 25, y, { align: 'right' });
      
      y += 3;
      pdf.setDrawColor(150, 150, 150);
      pdf.line(100, y, pageWidth - 25, y);
      y += 8;
      
      // Saldo pendiente
      pdf.setFontSize(12);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont(undefined, 'bold');
      pdf.text("SALDO PENDIENTE:", 25, y);
      if (saldoPendiente > 0) {
        pdf.setTextColor(200, 50, 50);  // Rojo para saldo pendiente
      } else {
        pdf.setTextColor(34, 139, 34);  // Verde para liquidado
      }
      pdf.text(`$${saldoPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, pageWidth - 25, y, { align: 'right' });
      
      y += 25;
      
      // === SELLO DE ESTADO ===
      const isLiquidado = saldoPendiente <= 0 || session.payment_status === 'liquidado';
      const selloText = isLiquidado ? "LIQUIDADO" : "APARTADO";
      
      // Recuadro del sello
      if (isLiquidado) {
        pdf.setDrawColor(34, 139, 34);  // Verde
        pdf.setTextColor(34, 139, 34);
      } else {
        pdf.setDrawColor(212, 175, 55);  // Dorado
        pdf.setTextColor(212, 175, 55);
      }
      pdf.setLineWidth(3);
      pdf.roundedRect(pageWidth / 2 - 45, y, 90, 30, 5, 5, 'S');
      
      // Texto del sello (el color ya está establecido arriba)
      pdf.setFontSize(28);
      pdf.setFont(undefined, 'bold');
      pdf.text(selloText, pageWidth / 2, y + 21, { align: 'center' });
      
      y += 45;
      
      // Fecha de generación
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont(undefined, 'normal');
      const fechaGeneracion = new Date().toLocaleDateString('es-MX', { 
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
      pdf.text(`Documento generado el ${fechaGeneracion}`, pageWidth / 2, y, { align: 'center' });
      
      // Footer
      pdf.setFontSize(9);
      pdf.setTextColor(150, 150, 150);
      pdf.text("picparty.net | PicParty - Cabina Fotográfica | Chihuahua, Chih.", pageWidth / 2, pageHeight - 15, { align: 'center' });
      
      // === DESCARGAR PDF ===
      const fileName = `Ticket_PicPartyLive_${session.event_name.replace(/\s+/g, '_')}_${session.code}.pdf`;
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = fileName;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
      
      toast.success("¡Ticket de Pago generado!");
      
    } catch (error) {
      console.error("Error generando Ticket:", error);
      toast.error("Error al generar Ticket. Intenta de nuevo.");
    }
  };
  
  // Precios de PicPartyLive
  const PRECIOS_PICPARTYLIVE = {
    normal: { precio: 1500, label: "Normal" },
    promo: { precio: 1000, label: "Promo Expo Boda" },
    combo: { precio: 700, label: "Combo con otro servicio" }
  };
  
  // Abrir modal de Ticket de Venta - Pre-cargar datos de la sesión
  const openTicketVentaModal = (session) => {
    // Determinar tipo de precio basado en el total_price guardado
    let precioTipo = "normal";
    if (session.total_price === 1000) precioTipo = "promo";
    else if (session.total_price === 700) precioTipo = "combo";
    
    setTicketVentaForm({ 
      precioTipo: precioTipo, 
      anticipo: session.anticipo_amount || 0 
    });
    setTicketVentaModal({ open: true, session });
  };
  
  // Generar Ticket de Venta PDF
  const generateTicketVenta = async () => {
    const session = ticketVentaModal.session;
    if (!session) return;
    
    toast.info("Generando Ticket de Venta...");
    
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Fondo blanco
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Logo PicParty
      try {
        pdf.addImage(PICPARTY_LOGO_BASE64, 'PNG', 15, 10, 40, 40);
      } catch(e) {
        pdf.setFontSize(20);
        pdf.setTextColor(212, 175, 55);
        pdf.setFont(undefined, 'bold');
        pdf.text("PIC PARTY", 15, 35);
      }
      
      // Título del documento
      pdf.setFontSize(22);
      pdf.setTextColor(26, 11, 46);
      pdf.setFont(undefined, 'bold');
      pdf.text("TICKET DE VENTA", pageWidth / 2, 25, { align: 'center' });
      
      // Subtítulo
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont(undefined, 'normal');
      pdf.text("PicPartyLive - Muro de Fotos en Vivo", pageWidth / 2, 33, { align: 'center' });
      
      // Línea separadora
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(15, 55, pageWidth - 15, 55);
      
      let y = 70;
      
      // === DATOS DEL PROVEEDOR ===
      pdf.setFillColor(245, 245, 245);
      pdf.rect(15, y - 5, pageWidth - 30, 28, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, 'bold');
      pdf.text("DATOS DEL PROVEEDOR:", 20, y);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      y += 6;
      pdf.text("Adán Octavio Irigoyen Arias", 20, y);
      y += 5;
      pdf.text("RFC: IIAA8004021A9", 20, y);
      y += 5;
      pdf.text("Tel: 614 272 5008 | octavio@picparty.net", 20, y);
      
      y += 18;
      
      // === DATOS DEL CLIENTE ===
      pdf.setFillColor(250, 250, 250);
      pdf.rect(15, y - 5, pageWidth - 30, 28, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, 'bold');
      pdf.text("DATOS DEL CLIENTE:", 20, y);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      y += 6;
      pdf.text(`Nombre: ${session.client_name || session.event_name || 'No especificado'}`, 20, y);
      y += 5;
      pdf.text(`Celular: ${session.client_phone || 'No especificado'}`, 20, y);
      y += 5;
      
      // Formatear fecha
      let formattedDate = session.event_date || 'No especificada';
      try {
        formattedDate = new Date(session.event_date + 'T12:00:00').toLocaleDateString('es-MX', { 
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
        });
      } catch(e) {}
      pdf.text(`Fecha del Evento: ${formattedDate}`, 20, y);
      
      y += 18;
      
      // === SERVICIO CONTRATADO ===
      pdf.setFillColor(245, 245, 245);
      pdf.rect(15, y - 5, pageWidth - 30, 16, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, 'bold');
      pdf.text("SERVICIO:", 20, y);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      y += 6;
      pdf.text(`PicPartyLive - Muro de Fotos en Vivo | Código: ${session.code}`, 20, y);
      
      y += 18;
      
      // === DESGLOSE DE PAGO ===
      pdf.setFontSize(10);
      pdf.setTextColor(26, 11, 46);
      pdf.setFont(undefined, 'bold');
      pdf.text("DESGLOSE DE PAGO", 20, y);
      
      y += 8;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(20, y, pageWidth - 20, y);
      y += 8;
      
      const precioInfo = PRECIOS_PICPARTYLIVE[ticketVentaForm.precioTipo];
      const totalPrice = precioInfo.precio;
      const anticipoAmount = parseFloat(ticketVentaForm.anticipo) || 0;
      const saldoPendiente = totalPrice - anticipoAmount;
      
      // Precio seleccionado
      pdf.setFontSize(11);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont(undefined, 'normal');
      pdf.text(`PicPartyLive (${precioInfo.label}):`, 25, y);
      pdf.setFont(undefined, 'bold');
      pdf.text(`$${totalPrice.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, pageWidth - 25, y, { align: 'right' });
      
      y += 8;
      pdf.setFont(undefined, 'normal');
      pdf.text("Anticipo recibido:", 25, y);
      pdf.setTextColor(34, 139, 34);
      pdf.setFont(undefined, 'bold');
      pdf.text(`-$${anticipoAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, pageWidth - 25, y, { align: 'right' });
      
      y += 3;
      pdf.setDrawColor(150, 150, 150);
      pdf.line(100, y, pageWidth - 25, y);
      y += 8;
      
      // Saldo pendiente
      pdf.setFontSize(12);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont(undefined, 'bold');
      pdf.text("SALDO PENDIENTE:", 25, y);
      if (saldoPendiente > 0) {
        pdf.setTextColor(200, 50, 50);
      } else {
        pdf.setTextColor(34, 139, 34);
      }
      pdf.text(`$${saldoPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, pageWidth - 25, y, { align: 'right' });
      
      y += 25;
      
      // === SELLO DE ESTADO ===
      const isLiquidado = saldoPendiente <= 0;
      const selloText = isLiquidado ? "LIQUIDADO" : "APARTADO";
      
      // Recuadro del sello
      if (isLiquidado) {
        pdf.setDrawColor(34, 139, 34);
        pdf.setTextColor(34, 139, 34);
      } else {
        pdf.setDrawColor(212, 175, 55);
        pdf.setTextColor(212, 175, 55);
      }
      pdf.setLineWidth(3);
      pdf.roundedRect(pageWidth / 2 - 45, y, 90, 30, 5, 5, 'S');
      
      // Texto del sello
      pdf.setFontSize(28);
      pdf.setFont(undefined, 'bold');
      pdf.text(selloText, pageWidth / 2, y + 21, { align: 'center' });
      
      y += 45;
      
      // Fecha de generación
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont(undefined, 'normal');
      const fechaGeneracion = new Date().toLocaleDateString('es-MX', { 
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
      pdf.text(`Documento generado el ${fechaGeneracion}`, pageWidth / 2, y, { align: 'center' });
      
      // Footer
      pdf.setFontSize(9);
      pdf.setTextColor(150, 150, 150);
      pdf.text("picparty.net | PicParty - Cabina Fotográfica | Chihuahua, Chih.", pageWidth / 2, pageHeight - 15, { align: 'center' });
      
      // === DESCARGAR PDF ===
      const fileName = `TicketVenta_PicPartyLive_${session.event_name.replace(/\s+/g, '_')}_${session.code}.pdf`;
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = fileName;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
      
      toast.success("¡Ticket de Venta generado!");
      setTicketVentaModal({ open: false, session: null });
      
    } catch (error) {
      console.error("Error generando Ticket de Venta:", error);
      toast.error("Error al generar Ticket. Intenta de nuevo.");
    }
  };
  
  // Estado para mostrar enlace de descarga manual
  const [manualDownloadUrl, setManualDownloadUrl] = useState(null);
  const [manualDownloadName, setManualDownloadName] = useState("");

  // ============ FUNCIONES DE CONTRATOS ============
  // Movidas a src/modules/Contracts/useContracts.js (hook `useContracts`), ver arriba

  // LOGIN SCREEN
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-premium-radial flex items-center justify-center">
        <span className="text-gold" data-testid="session-check-loading">Cargando...</span>
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-premium-radial flex items-center justify-center">
        <Card className="w-full max-w-md card-premium">
          <CardHeader className="text-center">
            <img src={PICPARTY_LOGO} alt="PicParty" className="w-32 h-32 mx-auto mb-4" />
            <CardTitle className="text-gold text-2xl">Panel Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input 
                  placeholder="Usuario" 
                  value={loginUser} 
                  onChange={(e) => setLoginUser(e.target.value.toUpperCase())} 
                  className="input-premium"
                  autoComplete="username"
                  data-testid="login-user-input"
                />
              </div>
              <div>
                <Input 
                  type="password" 
                  placeholder="Contraseña" 
                  value={loginPass} 
                  onChange={(e) => setLoginPass(e.target.value)} 
                  className="input-premium"
                  autoComplete="current-password"
                  data-testid="login-pass-input"
                />
              </div>
              <Button type="submit" className="w-full btn-gold" data-testid="login-submit-btn">Ingresar</Button>
            </form>
            <Link to="/" className="block text-center mt-4 text-pearl-muted hover:text-gold">← Volver</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-night flex items-center justify-center text-pearl">Cargando...</div>;

  // ============ VISTA STAFF - RESTRINGIDA ============
  if (userRole === "staff") {
    return (
      <div className="min-h-screen bg-night">
        <header className="header-premium">
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src={PICPARTY_LOGO} alt="PicParty" className="h-10 w-10" />
              <span className="text-xl font-bold text-gold">PicParty STAFF</span>
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">Staff</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="destructive" onClick={handleLogout} data-testid="staff-logout-btn">Salir</Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6">
          <Card className="card-premium border-gold/30 mb-6">
            <CardHeader>
              <CardTitle className="text-gold">Galería de Eventos por Contrato</CardTitle>
              <CardDescription className="text-pearl-muted">
                Selecciona un evento para ver sus templates y agregar los links de Fotoshare
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Selector de Eventos (Contratos con fecha) */}
              <div className="mb-4">
                <Label className="text-pearl mb-2 block">Seleccionar Evento:</Label>
                <Select value={selectedEventCode || ""} onValueChange={(val) => {
                  setSelectedEventCode(val);
                  const contract = contracts.find(c => c.id === val);
                  if (contract) {
                    setFotoshareLinks({ fotos: contract.link_fotos || "", videos: contract.link_videos || "" });
                  }
                }}>
                  <SelectTrigger className="input-premium" data-testid="staff-event-selector">
                    <SelectValue placeholder="Selecciona un evento..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contracts
                      .sort((a, b) => new Date(b.event_date) - new Date(a.event_date))
                      .map(contract => (
                        <SelectItem key={contract.id} value={contract.id}>
                          {contract.client_name} - {new Date(contract.event_date).toLocaleDateString('es-MX')}
                          {contract.anticipo_status === "pagado" && " ✅"}
                          {contract.anticipo_status === "abonado" && " 💰"}
                          {contract.anticipo_status === "pendiente" && " ⏳"}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contenido del evento seleccionado */}
              {selectedEventCode && (() => {
                const selectedContract = contracts.find(c => c.id === selectedEventCode);
                if (!selectedContract) return null;
                
                return (
                  <div className="space-y-6">
                    {/* Info del evento */}
                    <div className="p-4 bg-night/50 rounded-lg border border-gold/20">
                      <h3 className="text-gold text-lg font-bold mb-2">{selectedContract.client_name}</h3>
                      <div className="grid grid-cols-2 gap-2 text-pearl-muted text-sm">
                        <span>Fecha: {new Date(selectedContract.event_date).toLocaleDateString('es-MX')}</span>
                        <span>Teléfono: {selectedContract.client_phone}</span>
                        <span className="flex items-center gap-2">Estado: <Badge className={selectedContract.anticipo_status === "pagado" ? "bg-green-500/20 text-green-300" : "bg-yellow-500/20 text-yellow-300"}>{selectedContract.anticipo_status}</Badge></span>
                      </div>
                    </div>

                    {/* Servicios Contratados */}
                    <div className="p-4 bg-gold/10 rounded-lg border border-gold/30">
                      <h4 className="text-gold font-semibold mb-3">Servicios Contratados:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedContract.include_cabina && <Badge className="badge-gold">Cabina de Fotos ({selectedContract.cabina_hours}h)</Badge>}
                        {selectedContract.include_video360 && <Badge className="badge-gold">Video 360 ({selectedContract.video360_hours}h)</Badge>}
                        {selectedContract.include_key_moments && <Badge className="badge-gold">Key Moments ({selectedContract.key_moments_pieces} piezas)</Badge>}
                        {selectedContract.include_live && <Badge className="badge-gold">PicPartyLive</Badge>}
                      </div>
                    </div>

                    {/* ============ LINKS DE FOTOSHARE ============ */}
                    <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                      <h4 className="text-purple-300 font-semibold mb-3 flex items-center gap-2">
                        <span>📎</span> Links de Fotoshare.co
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-pearl text-sm">Link de FOTOS:</Label>
                          <Input 
                            placeholder="https://fotoshare.co/e/..." 
                            value={fotoshareLinks.fotos}
                            onChange={(e) => setFotoshareLinks({...fotoshareLinks, fotos: e.target.value})}
                            className="input-premium mt-1"
                            data-testid="staff-link-fotos"
                          />
                        </div>
                        <div>
                          <Label className="text-pearl text-sm">Link de VIDEOS:</Label>
                          <Input 
                            placeholder="https://fotoshare.co/v/..." 
                            value={fotoshareLinks.videos}
                            onChange={(e) => setFotoshareLinks({...fotoshareLinks, videos: e.target.value})}
                            className="input-premium mt-1"
                            data-testid="staff-link-videos"
                          />
                        </div>
                        <Button 
                          onClick={async () => {
                            try {
                              const params = new URLSearchParams();
                              if (fotoshareLinks.fotos) params.append('link_fotos', fotoshareLinks.fotos);
                              if (fotoshareLinks.videos) params.append('link_videos', fotoshareLinks.videos);
                              await axios.put(`${API}/contracts/${selectedEventCode}/links?${params.toString()}`);
                              toast.success("✅ Links guardados correctamente");
                              fetchData();
                            } catch (e) {
                              toast.error("Error guardando links");
                            }
                          }}
                          className="w-full btn-gold"
                          data-testid="staff-save-links-btn"
                        >
                          Guardar Links de Fotoshare
                        </Button>
                      </div>
                    </div>

                    {/* ============ GALERÍA DE TEMPLATES CLOUDINARY ============ */}
                    <div className="p-4 bg-night/50 rounded-lg border border-gold/20">
                      <h4 className="text-gold font-semibold mb-3 flex items-center gap-2">
                        <span>🖼️</span> Templates del Evento
                      </h4>
                      <p className="text-pearl-muted text-sm mb-3">
                        Descarga los templates (PNG/JPG) para el evento desde Cloudinary
                      </p>
                      <Button 
                        onClick={() => {
                          // Abrir Cloudinary con la carpeta del evento
                          const folderPath = `ADOCA/TEMPLATES/${selectedContract.client_name.replace(/\s+/g, '_').toUpperCase()}`;
                          window.open(`https://cloudinary.com/console/media_library/folders/${encodeURIComponent(folderPath)}`, '_blank');
                          toast.info("Abriendo Cloudinary... Busca la carpeta de templates del evento.");
                        }}
                        className="btn-gold-outline"
                        data-testid="staff-download-templates-btn"
                      >
                        Abrir Templates en Cloudinary
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {!selectedEventCode && (
                <div className="text-center py-12">
                  <span className="text-6xl mb-4 block">📋</span>
                  <h3 className="text-pearl text-xl font-bold mb-2">Selecciona un Evento</h3>
                  <p className="text-pearl-muted">Elige un evento del selector para ver los templates y agregar links</p>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // ============ VISTA ADMIN - COMPLETA ============
  return (
    <div className="min-h-screen bg-night">
      <header className="header-premium">
        <div className="container mx-auto px-4 py-3 flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <img src={PICPARTY_LOGO} alt="PicParty" className="h-10 w-10" />
            <span className="text-xl font-bold text-gold">{userName || "Admin"}</span>
            <Badge className={userRole === "admin" ? "bg-red-500/20 text-red-300 border-red-500/30" : userRole === "ventas" ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "bg-purple-500/20 text-purple-300 border-purple-500/30"}>
              {userRole?.toUpperCase()}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {userRole === "admin" && (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={exportToExcel}
                data-testid="export-excel-btn"
              >
                📊 Exportar Excel
              </Button>
            )}
            <Link to="/" className="pp2-home-btn" data-testid="admin-back-home"><Home size={24} /> Inicio</Link>
            <Button variant="destructive" onClick={handleLogout} data-testid="admin-logout-btn">Salir</Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <Tabs defaultValue="contracts">
          <TabsList className="w-full flex flex-wrap h-auto gap-1 mb-2 bg-night border border-gold/20" data-testid="admin-tabs-row1">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="data-[state=active]:bg-gold data-[state=active]:text-night">Cotizaciones ({quotesHook.quotes.length})</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="tab-contracts" className="data-[state=active]:bg-gold data-[state=active]:text-night">Contratos ({contracts.length})</TabsTrigger>
            <TabsTrigger value="reporte" data-testid="tab-reporte-pagos" className="data-[state=active]:bg-gold data-[state=active]:text-night">Reporte Pagos</TabsTrigger>
            {userRole === "admin" && <TabsTrigger value="proveedores" data-testid="tab-proveedores" className="data-[state=active]:bg-gold data-[state=active]:text-night">Proveedores ({proveedores.length})</TabsTrigger>}
          </TabsList>
          <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-night border border-gold/20" data-testid="admin-tabs-row2">
            {userRole === "admin" && <TabsTrigger value="precios" data-testid="tab-precios" className="data-[state=active]:bg-gold data-[state=active]:text-night">Precios</TabsTrigger>}
            {userRole === "admin" && <TabsTrigger value="promociones" data-testid="tab-promociones" className="data-[state=active]:bg-gold data-[state=active]:text-night">Promociones</TabsTrigger>}
            {userRole === "admin" && <TabsTrigger value="servicios" data-testid="tab-servicios" className="data-[state=active]:bg-gold data-[state=active]:text-night">Servicios (portada)</TabsTrigger>}
            <TabsTrigger value="events" data-testid="tab-galeria" className="data-[state=active]:bg-gold data-[state=active]:text-night">Galería Pro ({events.length})</TabsTrigger>
            <TabsTrigger value="live" data-testid="tab-live" className="data-[state=active]:bg-gold data-[state=active]:text-night">PICPARTYLIVE ({liveSessions.length})</TabsTrigger>
            {userRole === "admin" && <TabsTrigger value="cloudinary" data-testid="tab-cloudinary" className="data-[state=active]:bg-gold data-[state=active]:text-night">Cloudinary</TabsTrigger>}
            {userRole === "admin" && <TabsTrigger value="seguridad" data-testid="tab-seguridad" className="data-[state=active]:bg-gold data-[state=active]:text-night">Seguridad</TabsTrigger>}
          </TabsList>

          {/* ============ PESTAÑA DE COTIZACIONES ============ */}
          <TabsContent value="quotes" className="space-y-4 mt-4" data-testid="quotes-tab-content">
            <QuotesTab quotesHook={quotesHook} DatePicker={DatePicker} userRole={userRole} />
          </TabsContent>

          {/* ============ PESTAÑA DE CONTRATOS ============ */}
          <TabsContent value="contracts" className="space-y-4 mt-4">
            <ContractsTab
              userRole={userRole}
              contracts={contracts}
              contractsSearch={contractsSearch}
              setContractsSearch={setContractsSearch}
              showContractForm={showContractForm}
              setShowContractForm={setShowContractForm}
              contractForm={contractForm}
              setContractForm={setContractForm}
              editingContractId={editingContractId}
              contractPreview={contractPreview}
              cancelEdit={cancelEdit}
              createContract={createContract}
              updateContract={updateContract}
              startEditContract={startEditContract}
              printContractPDF={printContractPDF}
              printProveedorPDF={printProveedorPDF}
              deleteContract={deleteContract}
              syncFolios={syncFolios}
              DatePicker={DatePicker}
            />
          </TabsContent>

          {/* ============ PESTAÑA REPORTE DE PAGOS ============ */}
          <TabsContent value="reporte" className="space-y-4 mt-4">
            <ReportePagos
              contracts={contracts}
              generateRecibo={generateRecibo}
              addAbono={addAbono}
              editAbono={editAbono}
              removeAbono={removeAbono}
              toggleContractCancelled={toggleContractCancelled}
              generateAbonoRecibo={generateAbonoRecibo}
              DatePicker={DatePicker}
            />
          </TabsContent>

          {/* ============ PESTAÑA PROVEEDORES (Solo Admin) ============ */}
          {userRole === "admin" && (
            <TabsContent value="proveedores" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-pearl text-xl font-bold">Gestión de Proveedores</h2>
                  <p className="text-pearl-muted text-sm">Alta, edición y asignación de proveedores para servicios</p>
                </div>
                <Button onClick={() => { setShowProveedorForm(true); setEditingProveedor(null); setProveedorForm({ nombre_empresa: "", contacto: "", telefono: "", servicios: [], costo_cabina: "", costo_video360: "", costo_key_moments: "", notas: "" }); }} className="btn-gold">
                  + Nuevo Proveedor
                </Button>
              </div>

              {/* Formulario de Proveedor */}
              {showProveedorForm && (
                <Card className="card-premium border-gold/50">
                  <CardHeader>
                    <CardTitle className="text-gold">{editingProveedor ? "Editar Proveedor" : "Nuevo Proveedor"}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-pearl">Nombre Empresa *</Label>
                        <Input value={proveedorForm.nombre_empresa} onChange={(e) => setProveedorForm({...proveedorForm, nombre_empresa: e.target.value})} className="input-premium" placeholder="Ej: Foto Eventos MX" />
                      </div>
                      <div>
                        <Label className="text-pearl">Contacto *</Label>
                        <Input value={proveedorForm.contacto} onChange={(e) => setProveedorForm({...proveedorForm, contacto: e.target.value})} className="input-premium" placeholder="Nombre del contacto" />
                      </div>
                      <div>
                        <Label className="text-pearl">Teléfono *</Label>
                        <Input value={proveedorForm.telefono} onChange={(e) => setProveedorForm({...proveedorForm, telefono: e.target.value})} className="input-premium" placeholder="614 123 4567" />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-pearl mb-2 block">Servicios que ofrece:</Label>
                      <div className="flex flex-wrap gap-3">
                        {["cabina", "video360", "key_moments"].map(servicio => (
                          <label key={servicio} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox 
                              checked={proveedorForm.servicios.includes(servicio)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setProveedorForm({...proveedorForm, servicios: [...proveedorForm.servicios, servicio]});
                                } else {
                                  setProveedorForm({...proveedorForm, servicios: proveedorForm.servicios.filter(s => s !== servicio)});
                                }
                              }}
                            />
                            <span className="text-pearl">{servicio === "cabina" ? "Cabina de Fotos" : servicio === "video360" ? "Video 360" : "Key Moments"}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-pearl">Costo Cabina ($)</Label>
                        <Input type="number" value={proveedorForm.costo_cabina} onChange={(e) => setProveedorForm({...proveedorForm, costo_cabina: e.target.value})} className="input-premium" placeholder="0" />
                      </div>
                      <div>
                        <Label className="text-pearl">Costo Video 360 ($)</Label>
                        <Input type="number" value={proveedorForm.costo_video360} onChange={(e) => setProveedorForm({...proveedorForm, costo_video360: e.target.value})} className="input-premium" placeholder="0" />
                      </div>
                      <div>
                        <Label className="text-pearl">Costo Key Moments ($)</Label>
                        <Input type="number" value={proveedorForm.costo_key_moments} onChange={(e) => setProveedorForm({...proveedorForm, costo_key_moments: e.target.value})} className="input-premium" placeholder="0" />
                      </div>
                    </div>

                    <div>
                      <Label className="text-pearl">Notas</Label>
                      <Textarea value={proveedorForm.notas} onChange={(e) => setProveedorForm({...proveedorForm, notas: e.target.value})} className="input-premium" placeholder="Observaciones, horarios, condiciones..." />
                    </div>

                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => { setShowProveedorForm(false); setEditingProveedor(null); }}>Cancelar</Button>
                      <Button className="btn-gold" onClick={async () => {
                        try {
                          const data = {
                            nombre_empresa: proveedorForm.nombre_empresa,
                            contacto: proveedorForm.contacto,
                            telefono: proveedorForm.telefono,
                            servicios: proveedorForm.servicios,
                            costo_cabina: proveedorForm.costo_cabina ? parseFloat(proveedorForm.costo_cabina) : null,
                            costo_video360: proveedorForm.costo_video360 ? parseFloat(proveedorForm.costo_video360) : null,
                            costo_key_moments: proveedorForm.costo_key_moments ? parseFloat(proveedorForm.costo_key_moments) : null,
                            notas: proveedorForm.notas
                          };
                          if (editingProveedor) {
                            await axios.put(`${API}/proveedores/${editingProveedor}`, data);
                            toast.success("Proveedor actualizado");
                          } else {
                            await axios.post(`${API}/proveedores`, data);
                            toast.success("Proveedor creado");
                          }
                          setShowProveedorForm(false);
                          setEditingProveedor(null);
                          fetchData();
                        } catch (e) {
                          toast.error("Error guardando proveedor");
                        }
                      }}>
                        {editingProveedor ? "Guardar Cambios" : "Crear Proveedor"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Lista de Proveedores */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {proveedores.map(prov => (
                  <Card key={prov.id} className="card-premium hover:border-gold/50 transition-all">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-gold font-bold text-lg">{prov.nombre_empresa}</h3>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => {
                            setEditingProveedor(prov.id);
                            setProveedorForm({
                              nombre_empresa: prov.nombre_empresa,
                              contacto: prov.contacto,
                              telefono: prov.telefono,
                              servicios: prov.servicios || [],
                              costo_cabina: prov.costo_cabina || "",
                              costo_video360: prov.costo_video360 || "",
                              costo_key_moments: prov.costo_key_moments || "",
                              notas: prov.notas || ""
                            });
                            setShowProveedorForm(true);
                          }}>Editar</Button>
                          <Button size="sm" variant="destructive" onClick={async () => {
                            if (confirm("¿Eliminar este proveedor?")) {
                              await axios.delete(`${API}/proveedores/${prov.id}`);
                              toast.success("Proveedor eliminado");
                              fetchData();
                            }
                          }}>X</Button>
                        </div>
                      </div>
                      <p className="text-pearl text-sm"><strong>Contacto:</strong> {prov.contacto}</p>
                      <p className="text-pearl text-sm"><strong>Tel:</strong> {prov.telefono}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {prov.servicios?.map(s => (
                          <Badge key={s} className="badge-gold text-xs">{s}</Badge>
                        ))}
                      </div>
                      {(prov.costo_cabina || prov.costo_video360 || prov.costo_key_moments) && (
                        <div className="mt-2 pt-2 border-t border-gold/20 text-xs text-pearl-muted">
                          {prov.costo_cabina && <span className="mr-2">Cabina: ${prov.costo_cabina}</span>}
                          {prov.costo_video360 && <span className="mr-2">360: ${prov.costo_video360}</span>}
                          {prov.costo_key_moments && <span>KM: ${prov.costo_key_moments}</span>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {proveedores.length === 0 && (
                  <p className="text-pearl-muted col-span-full text-center py-8">No hay proveedores registrados</p>
                )}
              </div>
            </TabsContent>
          )}

          {/* ============ PESTAÑA SEGURIDAD (solo ADMIN) ============ */}
          {userRole === "admin" && (
            <TabsContent value="seguridad" className="space-y-6 mt-4" data-testid="seguridad-tab-content">
              <Card className="card-premium border-gold/30">
                <CardHeader>
                  <CardTitle className="text-gold">Cambiar Contraseña (Admin / Ventas)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Select value={passwordForm.target_username} onValueChange={(v) => setPasswordForm({ ...passwordForm, target_username: v })}>
                    <SelectTrigger className="input-premium" data-testid="password-target-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OCTAVIO">OCTAVIO (Admin)</SelectItem>
                      <SelectItem value="ANABEL">ANABEL (Ventas)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="password"
                    placeholder="Nueva contraseña (mín. 6 caracteres)"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="input-premium"
                    data-testid="password-new-input"
                  />
                  <Button
                    className="btn-gold"
                    data-testid="password-change-btn"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/auth/change-password`, passwordForm);
                        toast.success("Contraseña actualizada");
                        setPasswordForm({ ...passwordForm, new_password: "" });
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Error al cambiar la contraseña");
                      }
                    }}
                  >
                    Guardar Contraseña
                  </Button>
                </CardContent>
              </Card>

              <Card className="card-premium border-gold/30">
                <CardHeader>
                  <CardTitle className="text-gold">Personal (PIN propio y eventos asignados)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input placeholder="Código (ej. MARIA)" value={newStaffForm.staff_code}
                      onChange={(e) => setNewStaffForm({ ...newStaffForm, staff_code: e.target.value.toUpperCase() })}
                      className="input-premium" data-testid="new-staff-code-input" />
                    <Input placeholder="Nombre" value={newStaffForm.name}
                      onChange={(e) => setNewStaffForm({ ...newStaffForm, name: e.target.value })}
                      className="input-premium" data-testid="new-staff-name-input" />
                    <Input placeholder="PIN (mín. 4 dígitos)" value={newStaffForm.pin}
                      onChange={(e) => setNewStaffForm({ ...newStaffForm, pin: e.target.value })}
                      className="input-premium" data-testid="new-staff-pin-input" />
                  </div>
                  <Button
                    className="btn-gold"
                    data-testid="create-staff-btn"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/staff/members`, newStaffForm);
                        toast.success(`Personal ${newStaffForm.staff_code} creado`);
                        setNewStaffForm({ staff_code: "", name: "", pin: "", assigned_contract_ids: [] });
                        fetchStaffMembers();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Error al crear personal");
                      }
                    }}
                  >
                    Crear Personal
                  </Button>

                  <div className="space-y-3 mt-4">
                    {staffMembers.map((staff) => (
                      <Card key={staff.staff_code} className="bg-night/50 border-gold/20" data-testid={`staff-member-${staff.staff_code}`}>
                        <CardContent className="p-4 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-pearl font-semibold">{staff.name} ({staff.staff_code})</span>
                            <Badge className={staff.active ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}>
                              {staff.active ? "Activo" : "Desactivado"}
                            </Badge>
                          </div>
                          <Label className="text-pearl-muted text-sm">Eventos asignados:</Label>
                          <div className="flex flex-wrap gap-2">
                            {contracts.map((c) => {
                              const isAssigned = (staff.assigned_contract_ids || []).includes(c.id);
                              return (
                                <Badge
                                  key={c.id}
                                  data-testid={`assign-contract-${staff.staff_code}-${c.id}`}
                                  className={`cursor-pointer ${isAssigned ? "bg-gold text-night" : "bg-night border border-gold/30 text-pearl-muted"}`}
                                  onClick={async () => {
                                    const current = staff.assigned_contract_ids || [];
                                    const updated = isAssigned ? current.filter((id) => id !== c.id) : [...current, c.id];
                                    try {
                                      await axios.put(`${API}/staff/members/${staff.staff_code}`, { assigned_contract_ids: updated });
                                      fetchStaffMembers();
                                    } catch (e) {
                                      toast.error("Error al asignar evento");
                                    }
                                  }}
                                >
                                  {c.client_name}
                                </Badge>
                              );
                            })}
                          </div>
                          <div className="flex gap-2 items-center mt-2">
                            <Input
                              placeholder="Nuevo PIN"
                              value={pinResetValues[staff.staff_code] || ""}
                              onChange={(e) => setPinResetValues({ ...pinResetValues, [staff.staff_code]: e.target.value })}
                              className="input-premium w-40"
                              data-testid={`reset-pin-input-${staff.staff_code}`}
                            />
                            <Button
                              className="btn-gold-outline"
                              data-testid={`reset-pin-btn-${staff.staff_code}`}
                              onClick={async () => {
                                const newPin = pinResetValues[staff.staff_code];
                                if (!newPin) return;
                                try {
                                  await axios.put(`${API}/staff/members/${staff.staff_code}/pin`, { new_pin: newPin });
                                  toast.success("PIN actualizado");
                                  setPinResetValues({ ...pinResetValues, [staff.staff_code]: "" });
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Error al cambiar PIN");
                                }
                              }}
                            >
                              Cambiar PIN
                            </Button>
                            <Button
                              variant="destructive"
                              data-testid={`deactivate-staff-btn-${staff.staff_code}`}
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/staff/members/${staff.staff_code}`, { active: !staff.active });
                                  fetchStaffMembers();
                                } catch (e) {
                                  toast.error("Error al actualizar personal");
                                }
                              }}
                            >
                              {staff.active ? "Desactivar" : "Activar"}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {staffMembers.length === 0 && (
                      <p className="text-pearl-muted text-center py-6">No hay personal registrado</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="card-premium border-gold/30">
                <CardHeader>
                  <CardTitle className="text-gold">Respaldos de Base de Datos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-pearl-muted text-sm">
                    Se genera un respaldo automático todos los días a las 6:00 AM (UTC), conservando los últimos 30 días.
                    También puedes generar y descargar uno manualmente cuando quieras.
                  </p>
                  <Button
                    className="btn-gold"
                    data-testid="generate-backup-btn"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/admin/backups/generate`);
                        toast.success("Respaldo generado, descargando...");
                        window.open(`${API}/admin/backups/download/${res.data.filename}`, "_blank");
                        fetchBackups();
                      } catch (e) {
                        toast.error("Error al generar el respaldo");
                      }
                    }}
                  >
                    Generar y Descargar Respaldo Ahora
                  </Button>
                  <div className="space-y-2 mt-3">
                    {backups.map((b) => (
                      <div key={b.filename} className="flex justify-between items-center text-sm text-pearl-muted border-b border-gold/10 pb-1" data-testid={`backup-row-${b.filename}`}>
                        <span>{b.filename} ({b.size_kb} KB) — {new Date(b.created_at).toLocaleString()}</span>
                        <a href={`${API}/admin/backups/download/${b.filename}`} target="_blank" rel="noreferrer" className="text-gold underline" data-testid={`backup-download-${b.filename}`}>
                          Descargar
                        </a>
                      </div>
                    ))}
                    {backups.length === 0 && <p className="text-pearl-muted text-center py-4">Aún no hay respaldos generados</p>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}


          {/* ============ PESTAÑA PRECIOS DEL COTIZADOR (solo ADMIN) ============ */}
          {userRole === "admin" && (
            <TabsContent value="precios" className="space-y-6 mt-4" data-testid="precios-tab-content">
              {!preciosForm ? (
                <p className="text-pearl-muted text-center py-8">Cargando precios...</p>
              ) : (
                <>
                  <Card className="card-premium border-gold/30">
                    <CardHeader>
                      <CardTitle className="text-gold">Cabina de Fotos - Paquetes por hora</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {preciosForm.cabina.map((tier, idx) => (
                        <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-3 border-b border-gold/10 pb-4" data-testid={`precio-cabina-row-${idx}`}>
                          <div className="flex flex-col gap-1">
                            <span className="text-pearl-muted text-xs">Nombre</span>
                            <Input value={tier.label || ""} placeholder={`${tier.horas} horas`} className="input-premium" data-testid={`precio-cabina-nombre-${idx}`}
                              onChange={(e) => {
                                const next = [...preciosForm.cabina];
                                next[idx] = { ...next[idx], label: e.target.value };
                                setPreciosForm({ ...preciosForm, cabina: next });
                              }} />
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-pearl-muted text-xs">Horas</span>
                            <Input type="number" min="1" value={tier.horas} className="input-premium" data-testid={`precio-cabina-horas-${idx}`}
                              onChange={(e) => {
                                const next = [...preciosForm.cabina];
                                next[idx] = { ...next[idx], horas: parseInt(e.target.value) || 0 };
                                setPreciosForm({ ...preciosForm, cabina: next });
                              }} />
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-pearl-muted text-xs">Precio ($)</span>
                            <Input type="number" min="1" value={tier.precio} className="input-premium" data-testid={`precio-cabina-precio-${idx}`}
                              onChange={(e) => {
                                const next = [...preciosForm.cabina];
                                next[idx] = { ...next[idx], precio: parseFloat(e.target.value) || 0 };
                                setPreciosForm({ ...preciosForm, cabina: next });
                              }} />
                          </div>
                          <label className="flex items-center gap-2 text-sm text-pearl-muted self-end pb-2">
                            <input type="checkbox" checked={tier.activo !== false} data-testid={`precio-cabina-activo-${idx}`}
                              onChange={(e) => {
                                const next = [...preciosForm.cabina];
                                next[idx] = { ...next[idx], activo: e.target.checked };
                                setPreciosForm({ ...preciosForm, cabina: next });
                              }} />
                            Mostrar en el cotizador
                          </label>
                          <div className="flex flex-col gap-1 md:col-span-2">
                            <span className="text-pearl-muted text-xs">Descripción (opcional)</span>
                            <Input value={tier.descripcion || ""} placeholder="Se muestra al cliente cuando selecciona este paquete" className="input-premium" data-testid={`precio-cabina-descripcion-${idx}`}
                              onChange={(e) => {
                                const next = [...preciosForm.cabina];
                                next[idx] = { ...next[idx], descripcion: e.target.value };
                                setPreciosForm({ ...preciosForm, cabina: next });
                              }} />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card className="card-premium border-gold/30">
                    <CardHeader>
                      <CardTitle className="text-gold">Cabina 2h + 50 Llaveros (paquete)</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-pearl-muted text-xs">Nombre</span>
                        <Input value={preciosForm.cabina_llaveros.label} className="input-premium" data-testid="precio-llaveros-nombre"
                          onChange={(e) => setPreciosForm({ ...preciosForm, cabina_llaveros: { ...preciosForm.cabina_llaveros, label: e.target.value } })} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-pearl-muted text-xs">Precio ($)</span>
                        <Input type="number" min="1" value={preciosForm.cabina_llaveros.precio} className="input-premium" data-testid="precio-llaveros-precio"
                          onChange={(e) => setPreciosForm({ ...preciosForm, cabina_llaveros: { ...preciosForm.cabina_llaveros, precio: parseFloat(e.target.value) || 0 } })} />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-pearl-muted">
                        <input type="checkbox" checked={preciosForm.cabina_llaveros.activo !== false} data-testid="precio-llaveros-activo"
                          onChange={(e) => setPreciosForm({ ...preciosForm, cabina_llaveros: { ...preciosForm.cabina_llaveros, activo: e.target.checked } })} />
                        Mostrar en el cotizador
                      </label>
                      <div className="flex flex-col gap-1 md:col-span-2">
                        <span className="text-pearl-muted text-xs">Descripción (opcional)</span>
                        <Input value={preciosForm.cabina_llaveros.descripcion || ""} className="input-premium" data-testid="precio-llaveros-descripcion"
                          onChange={(e) => setPreciosForm({ ...preciosForm, cabina_llaveros: { ...preciosForm.cabina_llaveros, descripcion: e.target.value } })} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-premium border-gold/30">
                    <CardHeader>
                      <CardTitle className="text-gold">Video 360°</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-pearl-muted text-xs">Nombre</span>
                        <Input value={preciosForm.video360.label} className="input-premium" data-testid="precio-video360-nombre"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360: { ...preciosForm.video360, label: e.target.value } })} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-pearl-muted text-xs">Precio ($)</span>
                        <Input type="number" min="1" value={preciosForm.video360.precio} className="input-premium" data-testid="precio-video360-precio"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360: { ...preciosForm.video360, precio: parseFloat(e.target.value) || 0 } })} />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-pearl-muted">
                        <input type="checkbox" checked={preciosForm.video360.activo !== false} data-testid="precio-video360-activo"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360: { ...preciosForm.video360, activo: e.target.checked } })} />
                        Mostrar en el cotizador
                      </label>
                      <div className="flex flex-col gap-1 md:col-span-2">
                        <span className="text-pearl-muted text-xs">Descripción (opcional)</span>
                        <Input value={preciosForm.video360.descripcion || ""} className="input-premium" data-testid="precio-video360-descripcion"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360: { ...preciosForm.video360, descripcion: e.target.value } })} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-premium border-gold/30">
                    <CardHeader>
                      <CardTitle className="text-gold">Video 360° con Nieve (promoción navideña)</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-pearl-muted text-xs">Nombre</span>
                        <Input value={preciosForm.video360_nieve.label} className="input-premium" data-testid="precio-video360nieve-nombre"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360_nieve: { ...preciosForm.video360_nieve, label: e.target.value } })} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-pearl-muted text-xs">Precio ($)</span>
                        <Input type="number" min="1" value={preciosForm.video360_nieve.precio} className="input-premium" data-testid="precio-video360nieve-precio"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360_nieve: { ...preciosForm.video360_nieve, precio: parseFloat(e.target.value) || 0 } })} />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-pearl-muted">
                        <input type="checkbox" checked={preciosForm.video360_nieve.activo === true} data-testid="precio-video360nieve-activo"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360_nieve: { ...preciosForm.video360_nieve, activo: e.target.checked } })} />
                        Mostrar en el cotizador
                      </label>
                      <div className="flex flex-col gap-1 md:col-span-2">
                        <span className="text-pearl-muted text-xs">Descripción (opcional)</span>
                        <Input value={preciosForm.video360_nieve.descripcion || ""} className="input-premium" data-testid="precio-video360nieve-descripcion"
                          onChange={(e) => setPreciosForm({ ...preciosForm, video360_nieve: { ...preciosForm.video360_nieve, descripcion: e.target.value } })} />
                      </div>
                    </CardContent>
                  </Card>

                  <Button className="btn-gold" disabled={savingPrecios} onClick={savePreciosCotizador} data-testid="guardar-precios-btn">
                    {savingPrecios ? "Guardando..." : "Guardar Precios"}
                  </Button>
                </>
              )}
            </TabsContent>
          )}

          {/* ============ PESTAÑA PROMOCIONES (solo ADMIN) ============ */}
          {userRole === "admin" && (
            <TabsContent value="promociones" className="space-y-6 mt-4" data-testid="promociones-tab-content">
              <Card className="card-premium border-gold/30">
                <CardHeader>
                  <CardTitle className="text-gold">{editingPromoId ? "Editar Promoción" : "Nueva Promoción"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input placeholder="Texto de la promoción" value={promoForm.texto} className="input-premium" data-testid="promo-texto-input"
                    onChange={(e) => setPromoForm({ ...promoForm, texto: e.target.value })} />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input placeholder="Texto del botón (ej. Cotizar)" value={promoForm.texto_boton} className="input-premium" data-testid="promo-boton-input"
                      onChange={(e) => setPromoForm({ ...promoForm, texto_boton: e.target.value })} />
                    <Input placeholder="Ícono o emoji (opcional, ej. ❄)" value={promoForm.icono} className="input-premium" data-testid="promo-icono-input"
                      onChange={(e) => setPromoForm({ ...promoForm, icono: e.target.value })} />
                    <div className="flex items-center gap-2">
                      <input type="color" value={promoForm.color_fondo} data-testid="promo-color-input"
                        onChange={(e) => setPromoForm({ ...promoForm, color_fondo: e.target.value })}
                        className="w-10 h-10 rounded border border-gold/30 bg-transparent cursor-pointer" />
                      <Input value={promoForm.color_fondo} className="input-premium" data-testid="promo-color-hex-input"
                        onChange={(e) => setPromoForm({ ...promoForm, color_fondo: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-pearl-muted text-xs">Precio a mostrar (opcional, solo referencia)</span>
                      <Input type="number" min="0" placeholder="Ej. 3599" value={promoForm.precio} className="input-premium" data-testid="promo-precio-input"
                        onChange={(e) => setPromoForm({ ...promoForm, precio: e.target.value })} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-pearl-muted text-xs">Paquete que preselecciona el botón "Cotizar"</span>
                      <Select value={promoForm.paquete || "ninguno"} onValueChange={(v) => setPromoForm({ ...promoForm, paquete: v === "ninguno" ? "" : v })}>
                        <SelectTrigger className="input-premium" data-testid="promo-paquete-select"><SelectValue placeholder="Ninguno (abre el cotizador vacío)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ninguno">Ninguno (abre el cotizador vacío)</SelectItem>
                          <SelectItem value="cabina_2">Cabina 2 horas</SelectItem>
                          <SelectItem value="cabina_3">Cabina 3 horas</SelectItem>
                          <SelectItem value="cabina_4">Cabina 4 horas</SelectItem>
                          <SelectItem value="cabina_5">Cabina 5 horas</SelectItem>
                          <SelectItem value="cabina_llaveros">Cabina + 50 llaveros</SelectItem>
                          <SelectItem value="video360">Video 360°</SelectItem>
                          <SelectItem value="video360_nieve">Video 360° con Nieve</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-pearl-muted text-xs">Fecha de inicio</span>
                      <DatePicker value={promoForm.fecha_inicio} onChange={(d) => setPromoForm({ ...promoForm, fecha_inicio: d })} placeholder="Fecha de inicio" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-pearl-muted text-xs">Fecha de fin</span>
                      <DatePicker value={promoForm.fecha_fin} onChange={(d) => setPromoForm({ ...promoForm, fecha_fin: d })} placeholder="Fecha de fin" />
                    </div>
                  </div>
                  <p className="text-pearl-muted text-xs">Se compara con la hora de Chihuahua; la promoción se ve durante todo el día de la fecha de fin.</p>
                  <div className="flex items-center gap-2 bg-night/50 border border-gold/20 rounded-md px-3 py-2 w-fit">
                    <Switch checked={promoForm.activa} onCheckedChange={(v) => setPromoForm({ ...promoForm, activa: v })} data-testid="promo-activa-switch" />
                    <Label className="text-pearl text-sm cursor-pointer">Activa</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button className="btn-gold" disabled={savingPromo} onClick={savePromotion} data-testid="guardar-promo-btn">
                      {savingPromo ? "Guardando..." : editingPromoId ? "Guardar Cambios" : "Crear Promoción"}
                    </Button>
                    {editingPromoId && (
                      <Button variant="outline" className="btn-gold-outline" onClick={cancelEditPromotion} data-testid="cancelar-promo-btn">Cancelar</Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                {promotionsList.map((p) => (
                  <Card key={p.id} className="card-premium border-gold/30" data-testid={`promo-row-${p.id}`}>
                    <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full border border-gold/20 inline-block" style={{ background: p.color_fondo }} />
                        <div>
                          <p className="text-pearl font-semibold">{p.icono ? `${p.icono} ` : ""}{p.texto}</p>
                          <p className="text-pearl-muted text-sm">{p.fecha_inicio} — {p.fecha_fin} · Botón: "{p.texto_boton}" · {p.activa ? "ACTIVA" : "Inactiva"}{p.precio ? ` · $${p.precio}` : ""}{p.paquete ? ` · Preselecciona: ${p.paquete}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="btn-gold-outline" onClick={() => editPromotion(p)} data-testid={`promo-editar-${p.id}`}>Editar</Button>
                        <Button size="sm" variant="destructive" onClick={() => deletePromotion(p.id)} data-testid={`promo-eliminar-${p.id}`}>Eliminar</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {promotionsList.length === 0 && (
                  <p className="text-pearl-muted text-center py-8">Aún no hay promociones creadas</p>
                )}
              </div>
            </TabsContent>
          )}

          {/* ============ PESTAÑA SERVICIOS (PORTADA) ============ */}
          {userRole === "admin" && (
            <TabsContent value="servicios" className="space-y-4 mt-4" data-testid="servicios-tab-content">
              <ServicesSectionTab servicesSectionHook={servicesSectionHook} />
            </TabsContent>
          )}


          {/* ============ PESTAÑA GALERÍA PRO ============ */}
          <TabsContent value="events" className="space-y-4 mt-4">
            <Card className="card-premium border-gold/30">
              <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-gold font-semibold">Galería Pro - Solo Visualización</p>
                  <p className="text-pearl-muted text-sm">Estos eventos son para mostrar en IFRAME. NO generan carpetas en Cloudinary.</p>
                </div>
                <Button size="sm" variant="outline" className="btn-gold-outline" onClick={migrateEventsSchema} data-testid="migrate-events-schema-btn">
                  Migrar eventos existentes (una sola vez)
                </Button>
              </CardContent>
            </Card>
            <Card className="card-premium">
              <CardHeader><CardTitle className="text-pearl">Crear Evento de Galería</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Input placeholder="NOMBRE *" value={newEvent.name} onChange={(e) => setNewEvent({...newEvent, name: e.target.value.toUpperCase()})} className="input-premium" />
                  <div className="relative">
                    <DatePicker value={newEvent.date} onChange={(date) => setNewEvent({...newEvent, date})} placeholder="Fecha *" />
                  </div>
                  <Input placeholder="Salón o lugar" value={newEvent.location} onChange={(e) => setNewEvent({...newEvent, location: e.target.value})} className="input-premium" />
                  <Input type="time" placeholder="Hora del servicio" value={newEvent.time} onChange={(e) => setNewEvent({...newEvent, time: e.target.value})} className="input-premium" data-testid="new-event-time-input" />
                  <Select value={newEvent.category} onValueChange={(v) => setNewEvent({...newEvent, category: v})}>
                    <SelectTrigger className="input-premium"><SelectValue placeholder="Tipo de evento" /></SelectTrigger>
                    <SelectContent>
                      {EVENT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="col-span-2 md:col-span-3 flex flex-wrap gap-3 bg-night/50 border border-gold/20 rounded-md px-3 py-2">
                    <Label className="text-pearl-muted text-sm w-full md:w-auto">Servicio:</Label>
                    {EVENT_SERVICES.map(s => (
                      <label key={s.value} className="flex items-center gap-2 text-pearl text-sm cursor-pointer">
                        <input type="checkbox" checked={newEvent.services.includes(s.value)} onChange={() => toggleNewEventService(s.value)} className="w-4 h-4 accent-gold cursor-pointer" data-testid={`new-event-service-${s.value}`} />
                        {s.label}
                      </label>
                    ))}
                  </div>
                  {newEvent.services.includes("cabina") && (
                    <Input placeholder="Link de fotos (cabina)" value={newEvent.fotoshare_url} onChange={(e) => setNewEvent({...newEvent, fotoshare_url: e.target.value})} className="input-premium" data-testid="new-event-fotoshare-url" />
                  )}
                  {newEvent.services.includes("video360") && (
                    <Input placeholder="Link de video 360°" value={newEvent.video360_url} onChange={(e) => setNewEvent({...newEvent, video360_url: e.target.value})} className="input-premium" data-testid="new-event-video360-url" />
                  )}
                  <div className="flex items-center gap-2 bg-night/50 border border-gold/20 rounded-md px-3">
                    <Switch checked={newEvent.is_published} onCheckedChange={(v) => setNewEvent({...newEvent, is_published: v})} data-testid="new-event-published-switch" />
                    <Label className="text-pearl text-sm cursor-pointer">Publicado (visible en galería pública)</Label>
                  </div>
                  <Button onClick={createEvent} className="btn-gold col-span-2 md:col-span-1">Crear Evento</Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Lista de Eventos con Edición */}
            {events.map(event => (
              <Card key={event.id} className="card-premium">
                <CardContent className="p-4">
                  {editingEventId === event.id ? (
                    /* === MODO EDICIÓN === */
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Input 
                          placeholder="NOMBRE" 
                          value={editEventForm.name || ''} 
                          onChange={(e) => setEditEventForm({...editEventForm, name: e.target.value.toUpperCase()})} 
                          className="input-premium" 
                        />
                        <DatePicker 
                          value={editEventForm.date || ''} 
                          onChange={(date) => setEditEventForm({...editEventForm, date})} 
                          placeholder="Fecha"
                        />
                        <Input 
                          placeholder="Salón o lugar" 
                          value={editEventForm.location || ''} 
                          onChange={(e) => setEditEventForm({...editEventForm, location: e.target.value})} 
                          className="input-premium" 
                        />
                        <Input 
                          type="time"
                          placeholder="Hora del servicio" 
                          value={editEventForm.time || ''} 
                          onChange={(e) => setEditEventForm({...editEventForm, time: e.target.value})} 
                          className="input-premium" 
                          data-testid="edit-event-time-input"
                        />
                        <Select value={editEventForm.category || 'otro'} onValueChange={(v) => setEditEventForm({...editEventForm, category: v})}>
                          <SelectTrigger className="input-premium"><SelectValue placeholder="Tipo de evento" /></SelectTrigger>
                          <SelectContent>
                            {EVENT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="col-span-2 md:col-span-3 flex flex-wrap gap-3 bg-night/50 border border-gold/20 rounded-md px-3 py-2">
                          <Label className="text-pearl-muted text-sm w-full md:w-auto">Servicio:</Label>
                          {EVENT_SERVICES.map(s => (
                            <label key={s.value} className="flex items-center gap-2 text-pearl text-sm cursor-pointer">
                              <input type="checkbox" checked={(editEventForm.services || []).includes(s.value)} onChange={() => toggleEditEventService(s.value)} className="w-4 h-4 accent-gold cursor-pointer" data-testid={`edit-event-service-${s.value}`} />
                              {s.label}
                            </label>
                          ))}
                        </div>
                        {(editEventForm.services || []).includes("cabina") && (
                          <Input 
                            placeholder="Link de fotos (cabina)" 
                            value={editEventForm.fotoshare_url || ''} 
                            onChange={(e) => setEditEventForm({...editEventForm, fotoshare_url: e.target.value})} 
                            className="input-premium" 
                            data-testid="edit-event-fotoshare-url"
                          />
                        )}
                        {(editEventForm.services || []).includes("video360") && (
                          <Input 
                            placeholder="Link de video 360°" 
                            value={editEventForm.video360_url || ''} 
                            onChange={(e) => setEditEventForm({...editEventForm, video360_url: e.target.value})} 
                            className="input-premium" 
                            data-testid="edit-event-video360-url"
                          />
                        )}
                        <div className="flex items-center gap-2 bg-night/50 border border-gold/20 rounded-md px-3">
                          <Switch checked={editEventForm.is_published !== false} onCheckedChange={(v) => setEditEventForm({...editEventForm, is_published: v})} data-testid="edit-event-published-switch" />
                          <Label className="text-pearl text-sm cursor-pointer">Publicado</Label>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="btn-gold" onClick={() => saveEventEdit(event.id)}>Guardar</Button>
                        <Button size="sm" variant="outline" className="btn-gold-outline" onClick={() => setEditingEventId(null)}>Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    /* === MODO VISUALIZACIÓN === */
                    <div className="flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded flex items-center justify-center text-2xl bg-gold/20">📸</div>
                        <div>
                          <h3 className="text-pearl font-bold">{event.name}</h3>
                          <p className="text-pearl-muted text-sm">{event.date}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge className="badge-gold text-xs">Solo visualización IFRAME</Badge>
                            {event.is_published === false ? (
                              <Badge className="bg-gray-600 text-white text-xs" data-testid={`event-unpublished-badge-${event.id}`}>Oculto</Badge>
                            ) : (
                              <Badge className="bg-green-600 text-white text-xs">Publicado</Badge>
                            )}
                            {event.is_private && (
                              <Badge className="bg-purple-700 text-white text-xs" data-testid={`event-private-badge-${event.id}`}>Privado (PIN)</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="btn-gold-outline" onClick={() => { setEditingEventId(event.id); setEditEventForm({name: event.name, date: event.date, time: event.time || "", fotoshare_url: event.fotoshare_url, video360_url: event.video360_url, location: event.location || "", category: event.category || "otro", services: event.services || [], is_published: event.is_published !== false}); }}>Editar</Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteEvent(event.id)}>Eliminar</Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 bg-night/50 border border-gold/20 rounded-md px-3 py-2">
                      <Switch
                        checked={!!event.is_private}
                        onCheckedChange={(v) => toggleEventPrivacy(event, v)}
                        data-testid={`event-private-switch-${event.id}`}
                      />
                      <Label className="text-pearl text-sm cursor-pointer">Privado (pide PIN)</Label>
                      <Input
                        placeholder="PIN 4 dígitos"
                        value={eventPinDrafts[event.id] || ""}
                        onChange={(e) => setEventPinDrafts({ ...eventPinDrafts, [event.id]: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                        className="input-premium w-28"
                        maxLength={4}
                        data-testid={`event-pin-input-${event.id}`}
                      />
                      {event.is_private && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="btn-gold-outline"
                            onClick={() => toggleEventPrivacy(event, true)}
                            data-testid={`event-save-pin-btn-${event.id}`}
                          >
                            Guardar PIN
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="btn-gold-outline"
                            onClick={() => copyPrivateLink(event)}
                            data-testid={`event-copy-link-btn-${event.id}`}
                          >
                            <Copy size={14} className="mr-1" /> Copiar link + PIN
                          </Button>
                        </>
                      )}
                    </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* ============ PESTAÑA PICPARTYLIVE ============ */}
          <TabsContent value="live" className="space-y-4 mt-4">
            {/* Input file oculto para subida de fotos */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              data-session-code=""
              data-session-folder=""
            />
            
            <Card className="card-premium border-gold/30">
              <CardContent className="p-4">
                <p className="text-gold font-semibold">PICPARTYLIVE - Muro en Vivo</p>
                <p className="text-pearl-muted text-sm">Estos eventos generan carpeta automática en Cloudinary y código QR para invitados. Incluye software de proyección en tiempo real para pantallas o TV. No incluye equipo físico (pantallas/cableado).</p>
              </CardContent>
            </Card>
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-pearl">Crear Sesión Live</CardTitle>
                <CardDescription className="text-pearl-muted">El código de 4 dígitos se genera automáticamente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Fila 1: Nombre del Evento */}
                <div>
                  <Label className="text-pearl text-sm">Nombre del Evento *</Label>
                  <Input 
                    placeholder="ej: Boda de Pedro y María" 
                    value={newSession.event_name} 
                    onChange={(e) => setNewSession({...newSession, event_name: e.target.value})} 
                    className="input-premium mt-1" 
                  />
                </div>
                
                {/* Fila 2: Tipo de Evento y Fecha */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-pearl text-sm">Tipo de Evento *</Label>
                    <Select 
                      value={newSession.event_type} 
                      onValueChange={(v) => setNewSession({...newSession, event_type: v, event_type_custom: v === "otro" ? newSession.event_type_custom : ""})}
                    >
                      <SelectTrigger className="bg-night border-gold/30 text-pearl mt-1">
                        <SelectValue placeholder="Selecciona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {eventTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.emoji} {type.label.replace(type.emoji, '').trim()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-pearl text-sm">Fecha del Evento *</Label>
                    <div className="mt-1">
                      <DatePicker 
                        value={newSession.event_date} 
                        onChange={(event_date) => setNewSession({...newSession, event_date})} 
                        placeholder="Seleccionar fecha"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Campo de Teléfono del Cliente (OBLIGATORIO) */}
                <div>
                  <Label className="text-pearl text-sm">Teléfono del Cliente * <span className="text-gold text-xs">(Clave de descarga)</span></Label>
                  <Input 
                    type="text"
                    placeholder="Ej: 6146010070" 
                    value={newSession.client_phone} 
                    onChange={(e) => setNewSession({...newSession, client_phone: e.target.value})} 
                    className="input-premium mt-1" 
                  />
                  <p className="text-pearl-muted/70 text-xs mt-1">Los últimos 4 dígitos serán la clave para descargar las fotos</p>
                </div>
                
                {/* Campo personalizado si es "Otro" */}
                {newSession.event_type === "otro" && (
                  <div>
                    <Label className="text-pearl text-sm">Especifica el tipo de evento</Label>
                    <Input 
                      placeholder="ej: Graduación, Bautizo, etc." 
                      value={newSession.event_type_custom} 
                      onChange={(e) => setNewSession({...newSession, event_type_custom: e.target.value})} 
                      className="input-premium mt-1" 
                    />
                  </div>
                )}
                
                {/* Campos de Pago */}
                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gold/20">
                  <div>
                    <Label className="text-pearl text-sm">Nombre Cliente</Label>
                    <Input 
                      placeholder="Nombre del cliente" 
                      value={newSession.client_name} 
                      onChange={(e) => setNewSession({...newSession, client_name: e.target.value})} 
                      className="input-premium mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-pearl text-sm">Monto Total $</Label>
                    <Input 
                      type="number"
                      placeholder="0.00" 
                      value={newSession.total_price} 
                      onChange={(e) => setNewSession({...newSession, total_price: e.target.value})} 
                      className="input-premium mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-pearl text-sm">Anticipo $</Label>
                    <Input 
                      type="number"
                      placeholder="0.00" 
                      value={newSession.anticipo_amount} 
                      onChange={(e) => setNewSession({...newSession, anticipo_amount: e.target.value})} 
                      className="input-premium mt-1" 
                    />
                  </div>
                </div>
                
                {/* Fila 3: VIP y Botón */}
                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={newSession.is_vip} 
                      onCheckedChange={(c) => setNewSession({...newSession, is_vip: c})} 
                    />
                    <Label className="text-gold">VIP</Label>
                  </div>
                  <Button onClick={createLiveSession} className="btn-gold px-6">
                    Crear Sesión Live
                  </Button>
                </div>
              </CardContent>
            </Card>
            {/* Lista de sesiones - Ordenadas por fecha descendente */}
            {liveSessions.length === 0 ? (
              <Card className="card-premium border-dashed">
                <CardContent className="p-8 text-center">
                  <span className="text-5xl block mb-4">📺</span>
                  <p className="text-pearl-muted">No hay sesiones Live creadas</p>
                  <p className="text-pearl-muted/70 text-sm">Crea tu primera sesión con el formulario de arriba</p>
                </CardContent>
              </Card>
            ) : (
              [...liveSessions]
                .sort((a, b) => new Date(b.created_at || '1970-01-01') - new Date(a.created_at || '1970-01-01'))
                .map(session => {
                  const typeInfo = getEventTypeInfo(session.event_type, session.event_type_custom);
                  return (
                    <Card key={session.id} className="card-premium hover:border-gold/50 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          {/* Lado izquierdo: QR y datos */}
                          <div className="flex items-start gap-4">
                            <div className="bg-white p-2 rounded-lg shadow-lg">
                              <QRCodeSVG value={`https://${SITE_DOMAIN}/live?code=${session.code}`} size={70} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-pearl font-bold text-lg">{session.code}</span>
                                <Badge className={session.is_active ? "bg-gold text-night" : "bg-pearl-muted/30 text-pearl-muted"}>
                                  {session.is_active ? "Activo" : "Inactivo"}
                                </Badge>
                                {session.is_vip && <Badge className="bg-gold text-night">VIP</Badge>}
                              </div>
                              <p className="text-pearl font-medium">{session.event_name}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <Badge className="badge-gold text-xs">
                                  {typeInfo.emoji} {typeInfo.label}
                                </Badge>
                                {session.event_date && (
                                  <span className="text-pearl-muted text-sm">
                                    {new Date(session.event_date + 'T12:00:00').toLocaleDateString('es-MX', { 
                                      day: 'numeric', month: 'short', year: 'numeric' 
                                    })}
                                  </span>
                                )}
                                {/* Contador de fotos en tiempo real */}
                                <Badge className="badge-gold text-xs animate-pulse">
                                  {photosCounts[session.code] || 0} fotos
                                </Badge>
                              </div>
                              <p className="text-gold/70 text-xs mt-2 font-mono">
                                https://{SITE_DOMAIN}/live?code={session.code}
                              </p>
                              {/* Clave de descarga (últimos 4 dígitos del teléfono) */}
                              {session.client_phone && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className="badge-gold text-xs">
                                    Clave descarga: <span className="font-mono font-bold ml-1">{session.client_phone.slice(-4)}</span>
                                  </Badge>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="text-gold hover:bg-gold/20 text-xs h-6 px-2"
                                    onClick={() => {
                                      navigator.clipboard.writeText(session.client_phone.slice(-4));
                                      toast.success("Clave copiada: " + session.client_phone.slice(-4));
                                    }}
                                  >
                                    📋
                                  </Button>
                                </div>
                              )}
                              {!session.client_phone && (
                                <Badge className="bg-red-500/30 text-red-300 text-xs mt-1">
                                  Sin teléfono (descarga bloqueada)
                                </Badge>
                              )}
                              
                              {/* Botón de sincronizar Cloudinary */}
                              <div className="flex items-center gap-2 mt-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="btn-gold-outline text-xs h-7"
                                  onClick={async () => {
                                    const folder = prompt(
                                      "Ingresa la ruta de Cloudinary:\n(Ej: ADOCA/FEBRERO/28-02-26/EXPO PÚBLICO BODA)", 
                                      session.cloudinary_folder || "ADOCA/"
                                    );
                                    if (folder) {
                                      try {
                                        toast.info("Sincronizando fotos...");
                                        const res = await axios.post(`${API}/live/sync-cloudinary/${session.code}?folder_path=${encodeURIComponent(folder)}`);
                                        toast.success(`${res.data.imported} fotos importadas, ${res.data.skipped} ya existían (Total: ${res.data.total_found})`);
                                        // Actualizar contador inmediatamente
                                        fetchPhotosCounts([session]);
                                        fetchData();
                                      } catch (e) {
                                        toast.error("Error: " + (e.response?.data?.detail || "No se pudo sincronizar"));
                                      }
                                    }
                                  }}
                                >
                                  Sincronizar Cloudinary
                                </Button>
                                {session.cloudinary_folder && (
                                  <span className="text-pearl-muted/60 text-[10px] truncate max-w-[150px]" title={session.cloudinary_folder}>
                                    {session.cloudinary_folder}
                                  </span>
                                )}
                              </div>
                              
                              {/* Botón Subir Fotos */}
                              <div className="mt-2">
                                <Button 
                                  size="sm" 
                                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                                  onClick={() => triggerUploadPhotos(session)}
                                  disabled={uploadingSession === session.code || !session.cloudinary_folder}
                                  data-testid={`upload-photos-${session.code}`}
                                >
                                  {uploadingSession === session.code ? (
                                    <>
                                      <span className="animate-spin mr-2">⏳</span>
                                      Subiendo {uploadProgress.current}/{uploadProgress.total}...
                                    </>
                                  ) : (
                                    <>📤 Subir Fotos (selección múltiple)</>
                                  )}
                                </Button>
                                {!session.cloudinary_folder && (
                                  <p className="text-red-400 text-[10px] mt-1">Configura la ruta Cloudinary primero</p>
                                )}
                              </div>
                              
                              {/* Botones de compartir */}
                              <div className="flex gap-2 mt-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="btn-gold-outline text-xs h-7"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`https://${SITE_DOMAIN}/live?code=${session.code}`);
                                    toast.success("Link copiado al portapapeles");
                                  }}
                                >
                                  Copiar Link
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700 text-xs h-7"
                                  onClick={() => {
                                    const msg = encodeURIComponent(`¡Hola! Tu evento *${session.event_name}* en PICPARTYLIVE está listo.\n\n📸 Acceso directo: https://${SITE_DOMAIN}/live?code=${session.code}\n\n¡Sube tus fotos y compártelas con todos!`);
                                    window.open(`https://wa.me/?text=${msg}`, '_blank');
                                  }}
                                >
                                  WhatsApp
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Lado derecho: Botones */}
                          <div className="flex flex-col gap-2">
                            <Button 
                              size="sm" 
                              className="btn-gold" 
                              onClick={() => printQRPDF(session)}
                              data-testid={`print-qr-${session.code}`}
                            >
                              Imprimir QR
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-white" 
                              onClick={() => downloadQRasJPG(session)}
                              data-testid={`download-jpg-${session.code}`}
                            >
                              JPG WhatsApp
                            </Button>
                            {/* Botón Capítulos */}
                            <Button 
                              size="sm" 
                              className="bg-indigo-600 hover:bg-indigo-700 text-white" 
                              onClick={() => openChapterModal(session)}
                              data-testid={`chapters-${session.code}`}
                            >
                              Capítulos
                            </Button>
                            {/* Botón Vigencia */}
                            {!session.vigencia_activada ? (
                              <Button 
                                size="sm" 
                                className="bg-teal-600 hover:bg-teal-700 text-white" 
                                onClick={() => activarVigencia(session)}
                                data-testid={`vigencia-${session.code}`}
                              >
                                Activar Vigencia
                              </Button>
                            ) : (
                              <div className="text-xs text-center">
                                {session.vigencia_expirada ? (
                                  <Badge className="bg-red-600 text-white">EXPIRADA</Badge>
                                ) : (
                                  <div>
                                    <Badge className="bg-teal-600 text-white mb-1">
                                      {getDiasRestantes(session.vigencia_fin)} días
                                    </Badge>
                                    <p className="text-pearl-muted/70 text-[10px]">
                                      Vence: {formatFechaVigencia(session.vigencia_fin)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            <Button 
                              size="sm" 
                              className="bg-blue-600 hover:bg-blue-700 text-white" 
                              onClick={() => generatePaymentTicket(session)}
                              data-testid={`payment-ticket-${session.code}`}
                            >
                              Ticket Pago
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-purple-600 hover:bg-purple-700 text-white" 
                              onClick={() => openTicketVentaModal(session)}
                              data-testid={`ticket-venta-${session.code}`}
                            >
                              Ticket Venta
                            </Button>
                            <Button 
                              size="sm" 
                              className="btn-gold-outline"
                              onClick={() => openEditModal(session)}
                              data-testid={`edit-${session.code}`}
                            >
                              Editar
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="btn-gold-outline"
                              onClick={async () => { 
                                await axios.put(`${API}/live/sessions/${session.id}/toggle`); 
                                fetchData(); 
                              }}
                            >
                              {session.is_active ? "Pausar" : "Activar"}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              onClick={async () => { 
                                if (confirm('¿Eliminar esta sesión?')) {
                                  await axios.delete(`${API}/live/sessions/${session.id}`); 
                                  fetchData(); 
                                }
                              }}
                            >
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
            )}

            {/* ============ MODAL DE TICKET DE VENTA ============ */}
            {ticketVentaModal.open && ticketVentaModal.session && (
              <div className="fixed inset-0 bg-night/90 flex items-center justify-center z-50">
                <Card className="card-premium border-gold/50 w-full max-w-md mx-4">
                  <CardHeader>
                    <CardTitle className="text-gold flex items-center gap-2">
                      Ticket de Venta - PicPartyLive
                    </CardTitle>
                    <CardDescription className="text-pearl-muted">
                      Selecciona el precio y anticipo para generar el ticket
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Datos del evento */}
                    <div className="p-3 bg-night/50 rounded-lg border border-gold/20">
                      <p className="text-pearl font-semibold">{ticketVentaModal.session.event_name}</p>
                      <p className="text-pearl-muted text-sm">Código: {ticketVentaModal.session.code}</p>
                    </div>
                    
                    {/* Selector de Precio */}
                    <div className="space-y-2">
                      <Label className="text-gold font-bold">Precio NETO</Label>
                      <div className="grid grid-cols-1 gap-2">
                        <Button
                          variant={ticketVentaForm.precioTipo === "normal" ? "default" : "outline"}
                          className={`h-auto py-3 justify-between ${ticketVentaForm.precioTipo === "normal" ? "btn-gold" : "btn-gold-outline"}`}
                          onClick={() => setTicketVentaForm({...ticketVentaForm, precioTipo: "normal"})}
                        >
                          <span>Normal</span>
                          <span className="font-bold">$1,500 MXN</span>
                        </Button>
                        <Button
                          variant={ticketVentaForm.precioTipo === "promo" ? "default" : "outline"}
                          className={`h-auto py-3 justify-between ${ticketVentaForm.precioTipo === "promo" ? "btn-gold" : "btn-gold-outline"}`}
                          onClick={() => setTicketVentaForm({...ticketVentaForm, precioTipo: "promo"})}
                        >
                          <span>Promo Expo Boda</span>
                          <span className="font-bold">$1,000 MXN</span>
                        </Button>
                        <Button
                          variant={ticketVentaForm.precioTipo === "combo" ? "default" : "outline"}
                          className={`h-auto py-3 justify-between ${ticketVentaForm.precioTipo === "combo" ? "btn-gold" : "btn-gold-outline"}`}
                          onClick={() => setTicketVentaForm({...ticketVentaForm, precioTipo: "combo"})}
                        >
                          <span>Combo con otro servicio</span>
                          <span className="font-bold">$700 MXN</span>
                        </Button>
                      </div>
                    </div>
                    
                    {/* Anticipo */}
                    <div className="space-y-2">
                      <Label className="text-pearl">Anticipo Recibido ($)</Label>
                      <Input 
                        type="number"
                        placeholder="0"
                        value={ticketVentaForm.anticipo}
                        onChange={(e) => setTicketVentaForm({...ticketVentaForm, anticipo: e.target.value})}
                        className="input-premium"
                      />
                    </div>
                    
                    {/* Preview de saldo */}
                    <div className="p-3 bg-night/50 rounded-lg border border-gold/20">
                      <div className="flex justify-between items-center">
                        <span className="text-pearl-muted">Precio:</span>
                        <span className="text-pearl font-bold">
                          ${PRECIOS_PICPARTYLIVE[ticketVentaForm.precioTipo].precio.toLocaleString()} MXN
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-pearl-muted">Anticipo:</span>
                        <span className="text-green-500 font-bold">
                          -${(parseFloat(ticketVentaForm.anticipo) || 0).toLocaleString()} MXN
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-gold/20 mt-2">
                        <span className="text-gold font-bold">Saldo:</span>
                        <span className={`text-xl font-bold ${
                          (PRECIOS_PICPARTYLIVE[ticketVentaForm.precioTipo].precio - (parseFloat(ticketVentaForm.anticipo) || 0)) <= 0 
                            ? "text-green-500" 
                            : "text-gold"
                        }`}>
                          ${(PRECIOS_PICPARTYLIVE[ticketVentaForm.precioTipo].precio - (parseFloat(ticketVentaForm.anticipo) || 0)).toLocaleString()} MXN
                        </span>
                      </div>
                      <div className="text-center mt-2">
                        <Badge className={
                          (PRECIOS_PICPARTYLIVE[ticketVentaForm.precioTipo].precio - (parseFloat(ticketVentaForm.anticipo) || 0)) <= 0 
                            ? "bg-green-600" 
                            : "bg-gold text-night"
                        }>
                          {(PRECIOS_PICPARTYLIVE[ticketVentaForm.precioTipo].precio - (parseFloat(ticketVentaForm.anticipo) || 0)) <= 0 
                            ? "LIQUIDADO" 
                            : "APARTADO"
                          }
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Botones */}
                    <div className="flex gap-3 pt-2">
                      <Button 
                        variant="outline" 
                        className="flex-1 btn-gold-outline"
                        onClick={() => setTicketVentaModal({ open: false, session: null })}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        className="flex-1 btn-gold"
                        onClick={generateTicketVenta}
                      >
                        Generar PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ============ MODAL DE CAPÍTULOS ============ */}
            {chapterModal.open && (
              <div className="fixed inset-0 bg-night/90 flex items-center justify-center z-50 p-4">
                <Card className="card-premium border-gold/50 w-full max-w-4xl max-h-[90vh] overflow-hidden">
                  <CardHeader className="border-b border-gold/20">
                    <CardTitle className="text-gold flex items-center justify-between">
                      <span>Gestionar Capítulos - {chapterModal.sessionCode}</span>
                      <Button size="sm" variant="ghost" onClick={closeChapterModal} className="text-pearl-muted">✕</Button>
                    </CardTitle>
                    <CardDescription className="text-pearl-muted">
                      Organiza las fotos en capítulos (ej: Preparativos, Ceremonia, Fiesta, Cabina)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 overflow-y-auto max-h-[70vh]">
                    {/* Crear nuevo capítulo */}
                    <div className="flex gap-2 mb-4">
                      <Input
                        value={newChapterName}
                        onChange={(e) => setNewChapterName(e.target.value)}
                        placeholder="Nombre del capítulo (ej: Ceremonia)"
                        className="input-premium flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && createChapter()}
                      />
                      <Button onClick={createChapter} className="btn-gold">
                        + Crear
                      </Button>
                    </div>

                    {/* Lista de capítulos */}
                    <div className="space-y-4">
                      {sessionChapters.length === 0 ? (
                        <p className="text-pearl-muted text-center py-4">No hay capítulos. Crea el primero arriba.</p>
                      ) : (
                        sessionChapters.map(chapter => {
                          const photosInChapter = sessionPhotosForChapters.filter(p => p.chapter_id === chapter.id);
                          return (
                            <div key={chapter.id} className="border border-gold/20 rounded-lg p-3">
                              <div className="flex justify-between items-center mb-2">
                                <h4 className="text-gold font-semibold">{chapter.name}</h4>
                                <div className="flex gap-2 items-center">
                                  <Badge className="bg-gold/20 text-gold">{photosInChapter.length} fotos</Badge>
                                  {selectedPhotosForChapter.length > 0 && (
                                    <Button 
                                      size="sm" 
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                      onClick={() => assignPhotosToChapter(chapter.id)}
                                    >
                                      + Asignar {selectedPhotosForChapter.length} seleccionadas
                                    </Button>
                                  )}
                                  <Button 
                                    size="sm" 
                                    variant="destructive" 
                                    className="text-xs"
                                    onClick={() => deleteChapter(chapter.id)}
                                  >
                                    Eliminar
                                  </Button>
                                </div>
                              </div>
                              {/* Miniaturas de fotos en este capítulo */}
                              {photosInChapter.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {photosInChapter.slice(0, 8).map(photo => (
                                    <img 
                                      key={photo.id} 
                                      src={photo.thumbnail_url || photo.cloudinary_url} 
                                      alt="" 
                                      className="w-12 h-12 object-cover rounded"
                                    />
                                  ))}
                                  {photosInChapter.length > 8 && (
                                    <div className="w-12 h-12 bg-night/50 rounded flex items-center justify-center text-pearl-muted text-xs">
                                      +{photosInChapter.length - 8}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}

                      {/* Fotos sin capítulo */}
                      <div className="border border-pearl-muted/20 rounded-lg p-3 mt-4">
                        <h4 className="text-pearl-muted font-semibold mb-2">
                          Fotos sin capítulo ({sessionPhotosForChapters.filter(p => !p.chapter_id).length})
                        </h4>
                        <p className="text-pearl-muted/60 text-xs mb-2">Selecciona fotos y luego haz clic en "Asignar" en el capítulo deseado</p>
                        <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
                          {sessionPhotosForChapters.filter(p => !p.chapter_id).map(photo => (
                            <div 
                              key={photo.id} 
                              className={`relative cursor-pointer ${selectedPhotosForChapter.includes(photo.id) ? 'ring-2 ring-gold' : ''}`}
                              onClick={() => togglePhotoSelection(photo.id)}
                            >
                              <img 
                                src={photo.thumbnail_url || photo.cloudinary_url} 
                                alt="" 
                                className="w-16 h-16 object-cover rounded"
                              />
                              {selectedPhotosForChapter.includes(photo.id) && (
                                <div className="absolute inset-0 bg-gold/30 rounded flex items-center justify-center">
                                  <span className="text-white text-lg">✓</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ============ MODAL DE EDICIÓN ============ */}
            {editingSession && (
              <div className="fixed inset-0 bg-night/90 flex items-center justify-center z-50">
                <Card className="card-premium border-gold/50 w-full max-w-md mx-4">
                  <CardHeader>
                    <CardTitle className="text-pearl flex items-center gap-2">
                      Editar Evento: {editingSession.code}
                    </CardTitle>
                    <CardDescription className="text-pearl-muted">
                      Modifica los datos del evento y guarda los cambios
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Código */}
                    <div>
                      <Label className="text-pearl text-sm">Código del Evento</Label>
                      <Input 
                        value={editForm.code}
                        onChange={(e) => setEditForm({...editForm, code: e.target.value.toUpperCase()})}
                        className="input-premium mt-1"
                        placeholder="Código único"
                      />
                    </div>
                    
                    {/* Nombre */}
                    <div>
                      <Label className="text-pearl text-sm">Nombre del Evento *</Label>
                      <Input 
                        value={editForm.event_name}
                        onChange={(e) => setEditForm({...editForm, event_name: e.target.value})}
                        className="input-premium mt-1"
                        placeholder="Nombre del evento"
                      />
                    </div>
                    
                    {/* Tipo de Evento */}
                    <div>
                      <Label className="text-pearl text-sm">Tipo de Evento</Label>
                      <Select 
                        value={editForm.event_type} 
                        onValueChange={(v) => setEditForm({...editForm, event_type: v})}
                      >
                        <SelectTrigger className="bg-night border-gold/30 text-pearl mt-1">
                          <SelectValue placeholder="Selecciona tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {eventTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.emoji} {type.label.replace(type.emoji, '').trim()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Campo personalizado si es "Otro" */}
                    {editForm.event_type === "otro" && (
                      <div>
                        <Label className="text-pearl text-sm">Especifica el tipo</Label>
                        <Input 
                          value={editForm.event_type_custom}
                          onChange={(e) => setEditForm({...editForm, event_type_custom: e.target.value})}
                          className="input-premium mt-1"
                          placeholder="ej: Graduación, Bautizo"
                        />
                      </div>
                    )}
                    
                    {/* Fecha */}
                    <div>
                      <Label className="text-pearl text-sm">Fecha del Evento *</Label>
                      <div className="mt-1">
                        <DatePicker 
                          value={editForm.event_date}
                          onChange={(event_date) => setEditForm({...editForm, event_date})}
                          placeholder="Seleccionar fecha"
                        />
                      </div>
                      <p className="text-pearl-muted/70 text-xs mt-1">
                        Cambia a fecha de hoy para activar acceso inmediato
                      </p>
                    </div>
                    
                    {/* Campos de Pago */}
                    <div className="border-t border-gold/20 pt-4 space-y-3">
                      <Label className="text-gold text-sm font-bold">Datos de Pago (para Ticket)</Label>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-pearl text-xs">Nombre Cliente</Label>
                          <Input 
                            value={editForm.client_name}
                            onChange={(e) => setEditForm({...editForm, client_name: e.target.value})}
                            className="input-premium mt-1"
                            placeholder="Nombre"
                          />
                        </div>
                        <div>
                          <Label className="text-pearl text-xs">Teléfono</Label>
                          <Input 
                            value={editForm.client_phone}
                            onChange={(e) => setEditForm({...editForm, client_phone: e.target.value})}
                            className="input-premium mt-1"
                            placeholder="Teléfono"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-pearl text-xs">Monto Total $</Label>
                          <Input 
                            type="number"
                            value={editForm.total_price}
                            onChange={(e) => setEditForm({...editForm, total_price: e.target.value})}
                            className="input-premium mt-1"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label className="text-pearl text-xs">Anticipo Recibido $</Label>
                          <Input 
                            type="number"
                            value={editForm.anticipo_amount}
                            onChange={(e) => setEditForm({...editForm, anticipo_amount: e.target.value})}
                            className="input-premium mt-1"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-pearl text-xs">Estado de Pago</Label>
                        <Select 
                          value={editForm.payment_status} 
                          onValueChange={(v) => setEditForm({...editForm, payment_status: v})}
                        >
                          <SelectTrigger className="bg-night border-gold/30 text-pearl mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendiente">Pendiente</SelectItem>
                            <SelectItem value="apartado">Apartado (con anticipo)</SelectItem>
                            <SelectItem value="liquidado">Liquidado (pagado total)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Carpeta Cloudinary */}
                    <div className="border-t border-gold/20 pt-4">
                      <Label className="text-gold text-sm font-bold">Ruta Cloudinary</Label>
                      <Input 
                        value={editForm.cloudinary_folder}
                        onChange={(e) => setEditForm({...editForm, cloudinary_folder: e.target.value})}
                        className="input-premium mt-2"
                        placeholder="ej: ADOCA/FEBRERO/28-02-26/BODA_NOMBRE"
                      />
                      <p className="text-pearl-muted/70 text-xs mt-1">
                        Ruta completa de la carpeta en Cloudinary para sincronizar fotos
                      </p>
                    </div>
                    
                    {/* Botones */}
                    <div className="flex gap-3 pt-4">
                      <Button 
                        className="flex-1 btn-gold-outline"
                        onClick={closeEditModal}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        className="flex-1 btn-gold"
                        onClick={saveSessionEdit}
                      >
                        Guardar Cambios
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="cloudinary" className="mt-4">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-pearl">Galería de Imágenes por Evento</CardTitle>
                <CardDescription className="text-pearl-muted">Solo imágenes PNG/JPG - Descarga directa para Staff</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selector de Evento */}
                <div className="space-y-2">
                  <Label className="text-pearl">Seleccionar Evento</Label>
                  <Select onValueChange={(code) => setSelectedEventCode(code)}>
                    <SelectTrigger className="bg-night border-gold/30 text-pearl">
                      <SelectValue placeholder="Selecciona un evento para ver sus imágenes" />
                    </SelectTrigger>
                    <SelectContent>
                      {liveSessions.map(session => (
                        <SelectItem key={session.code} value={session.code}>
                          {session.name} - {session.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Galería de Fotos del Evento Seleccionado */}
                {selectedEventCode && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-gold font-bold">Imágenes del Evento: {selectedEventCode}</h3>
                      <Button 
                        className="btn-gold-outline" 
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/live/photos/${selectedEventCode}`);
                            setEventPhotos(res.data.photos || []);
                            toast.success(`${res.data.total} imágenes cargadas`);
                          } catch(e) {
                            toast.error("Error cargando imágenes");
                          }
                        }}
                      >
                        Cargar Imágenes
                      </Button>
                    </div>
                    
                    {eventPhotos.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {eventPhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative group">
                            <img 
                              src={photo.photo_url} 
                              alt={`Foto ${idx + 1}`}
                              className="w-full h-32 object-cover rounded border border-gold/30"
                            />
                            <a 
                              href={photo.photo_url} 
                              download={`evento_${selectedEventCode}_foto_${idx + 1}.jpg`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute inset-0 bg-night/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            >
                              <Button size="sm" className="btn-gold">
                                Descargar
                              </Button>
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-pearl-muted text-center py-8">Haz clic en "Cargar Imágenes" para ver las fotos del evento</p>
                    )}
                    
                    {eventPhotos.length > 0 && (
                      <div className="p-3 bg-gold/10 border border-gold/30 rounded">
                        <p className="text-gold text-sm font-semibold">Descarga Masiva</p>
                        <p className="text-pearl-muted text-xs">Haz clic en cada imagen para descargarla. Solo formatos PNG/JPG soportados.</p>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded">
                  <p className="text-green-400 font-semibold">Sistema Optimizado</p>
                  <p className="text-pearl-muted text-sm mt-1">Solo imágenes PNG/JPG. Sin videos ni audios.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ============ APP PRINCIPAL ============
function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<EventGallery />} />
          <Route path="/galeria/:eventId" element={<EventGalleryViewer />} />
          <Route path="/cotizador" element={<Cotizador />} />
          <Route path="/picpartylive" element={<PicPartyLiveLanding />} />
          <Route path="/live" element={<PicPartyLive />} />
          <Route path="/admin" element={<AdminPanel />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
